import visitAcceptanceExperiment from "../../config/visitAcceptanceExperiment.json";
import { buildExperiment as buildStampCardExperiment } from "../../config/memberStampCardProgram";
import funnelExperimentGoals from "./funnelExperimentGoals.json";
import { getMemberPostSignupMembershipOffer } from "../memberPostSignupMembershipOffer";
import {
  MEMBER_DASHBOARD_FOUNDER_WELCOME_POSTER_STORAGE_PATH,
  MEMBER_DASHBOARD_FOUNDER_WELCOME_POSTER_URL,
  MEMBER_DASHBOARD_FOUNDER_WELCOME_STORAGE_PATH,
  MEMBER_DASHBOARD_FOUNDER_WELCOME_VIDEO_ID,
  MEMBER_DASHBOARD_FOUNDER_WELCOME_VIDEO_URL,
} from "../founderWelcomeMedia";

const SEARCH_PREFIX = "exp_track__";
const CANONICAL_RANDOMIZED_ASSIGNMENT_POLICY = Object.freeze({
  eligibilityPolicyKey: "universal",
  assignmentHashVersion: "uint53_avalanche_v1",
  assignmentBucketPolicyVersion: "cadence_weighted_v1",
});
const LANDING_EXPERIMENT_RESTORATION_AT = "2026-08-25T04:00:00.000Z";
const LANDING_EXPERIMENT_PAUSE_AT = "2026-08-22T07:00:00.000Z";
const VISIT_BOOKING_REALLOCATION_AT = "2026-09-01T07:00:00.000Z";
const EXPERIMENT_PORTFOLIO_REALLOCATION_AT = "2026-09-16T07:00:00.000Z";
const LANDING_HERO_CTA_COPY_EXPANSION_AT = "2026-09-20T07:00:00.000Z";
const LANDING_HERO_CTA_COPY_FOUR_ARM_AT = "2026-10-18T07:00:00.000Z";
const LANDING_HERO_CTA_RETIRED_VARIANTS = Object.freeze([
  { key: "start_to_do_list", label: "Start Your To-Do List", iconKey: "task_list" },
  { key: "meet_your_helper", label: "Meet Your Helper", iconKey: "waving_hand" },
  { key: "book_help_for_my_home", label: "Book Help for My Home", iconKey: "waving_hand" },
]);
const LANDING_SECTION_SLOT_4_START_AT = "2026-09-13T07:00:00.000Z";
const LANDING_SECTION_SLOT_5_START_AT = "2026-10-11T20:00:00.000Z";
const LANDING_SLOT_3_PROMOTION_AT = "2026-10-27T22:00:00.000Z";
const LANDING_AUDIENCE_LANGUAGE_START_AT = "2026-09-05T07:00:00.000Z";
const LANDING_AUDIENCE_LANGUAGE_DECISION_GRADE_START_AT =
  "2026-09-06T07:00:00.000Z";
const SITEWIDE_BBB_BRANDING_START_AT = "2026-09-20T07:00:00.000Z";
const SITEWIDE_BBB_BRANDING_DECISION_GRADE_START_AT =
  "2026-09-21T07:00:00.000Z";
const EXPERIMENT_DENOMINATOR_RESTART_AT = "2026-09-25T07:00:00.000Z";
const MEMBER_FIRST_BOOKING_EXPERIMENTS_START_AT = "2026-09-06T07:00:00.000Z";
const MEMBER_FIRST_BOOKING_EXPERIMENTS_DECISION_GRADE_START_AT =
  "2026-09-07T07:00:00.000Z";
const PRE_ONBOARDING_V2_EXPERIMENTS_START_AT = "2026-09-09T07:00:00.000Z";
const PRE_ONBOARDING_V2_EXPERIMENTS_DECISION_GRADE_START_AT =
  "2026-09-10T07:00:00.000Z";
const PRE_ONBOARDING_FLOW_V3_SPLIT_START_AT = "2026-10-12T07:00:00.000Z";
const PRE_ONBOARDING_V2_RESERVATION_ENTRY_START_AT = "2026-09-24T07:00:00.000Z";
const PRE_ONBOARDING_V2_RESERVATION_ENTRY_DECISION_GRADE_START_AT = "2026-09-25T07:00:00.000Z";
const MEMBER_FIRST_BOOKING_ENTRY_EXPERIMENTS_START_AT =
  "2026-09-11T07:00:00.000Z";
const MEMBER_FIRST_BOOKING_ENTRY_EXPERIMENTS_DECISION_GRADE_START_AT =
  "2026-09-12T07:00:00.000Z";
const LANDING_EXPERIMENT_INCIDENT_GAP = Object.freeze({
  startAt: LANDING_EXPERIMENT_PAUSE_AT,
  endAt: LANDING_EXPERIMENT_RESTORATION_AT,
  reason: "value_metrics_lifecycle_coupling_pause_2026_08_21",
});
const historicalExperimentRevision = ({
  revisionId,
  kind = "ab_test",
  assignmentUnit = "session",
  seedSalt,
  eligibilityPolicyKey = "universal",
  arms,
  surfaces,
  exposureMetricKey,
  primaryMetricKey,
  effectiveStartAt,
  decisionGradeStartAt = effectiveStartAt,
  effectiveEndAt = VISIT_BOOKING_REALLOCATION_AT,
  comparisonVariantKey,
  contractFingerprint,
  sourceContractFingerprint = null,
  ...overrides
}) =>
  Object.freeze({
    revisionId,
    kind,
    assignmentUnit,
    sourceAssignmentUnit: assignmentUnit,
    assignmentCadence: "static",
    assignmentSeedSalt: seedSalt,
    eligibilityPolicyKey,
    assignmentHashVersion: "uint53_avalanche_v1",
    assignmentBucketPolicyVersion: "cadence_weighted_v1",
    arms: Object.freeze(arms.map((arm) => Object.freeze({ ...arm }))),
    surfaces: Object.freeze([...surfaces]),
    assignmentMetricKey: "assignment",
    exposureMetricKey,
    primaryMetricKey,
    activatedAt: effectiveStartAt,
    effectiveStartAt,
    decisionGradeStartAt,
    effectiveEndAt,
    enabled: true,
    disabledReason: null,
    comparisonVariantKey,
    comparisonStatus: "valid_concurrent_control",
    noValidComparisonReason: null,
    financialPolicy: "assignment_cohort_d30",
    horizonDays: 30,
    contractFingerprint,
    ...(sourceContractFingerprint
      ? {
          sourceContractFingerprint,
          acceptedLegacyAssignmentFingerprints: Object.freeze([
            sourceContractFingerprint,
          ]),
        }
      : {}),
    ...overrides,
  });

const CLEAN_SLATE_REVISION_HISTORY = Object.freeze({
  member_engagement_intensity_v1: Object.freeze([
    historicalExperimentRevision({
      revisionId: "member_engagement_intensity_v1_r2",
      sourceContractFingerprint:
        "c48f41360facd55330bb3c9fb8b03e181c4e40b77ad42af2e982baf3b713ec35",
      assignmentUnit: "member",
      seedSalt: "member_engagement_intensity_v1_r2",
      eligibilityPolicyKey: "authenticated_member",
      arms: [
        { key: "low", weight: 1 },
        { key: "medium", weight: 1 },
        { key: "high", weight: 1 },
      ],
      surfaces: ["member_engagement"],
      exposureMetricKey: "member_engagement_sent",
      primaryMetricKey: "visit_billed_revenue_d30_per_assigned_member",
      effectiveStartAt: "2026-08-14T07:00:00.000Z",
      decisionGradeStartAt: "2026-08-16T07:00:00.000Z",
      comparisonVariantKey: "medium",
      contractFingerprint:
        "2723b3f065b2cbf541e1d9a300811051a3079485863b9425a89977169be5e690",
    }),
  ]),
  funnel_landing_hero_headline_v1: Object.freeze([
    historicalExperimentRevision({
      revisionId: "funnel_landing_hero_headline_v1_r2",
      sourceContractFingerprint:
        "eda0d8bb7b5871215f2599443e1ae484eb5402de2979ee62575fae09e5a030fe",
      seedSalt: "funnel_landing_hero_headline_v1_r2",
      arms: [
        { key: "control", weight: 20 },
        { key: "students_helping_seniors", weight: 20 },
        { key: "affordable_help_for_seniors", weight: 20 },
        { key: "extra_hand", weight: 20 },
        { key: "delightful_help_around_the_house", weight: 20 },
      ],
      surfaces: ["join_landing"],
      exposureMetricKey: "landing_hero_viewed",
      primaryMetricKey: "landing_qualified_active_engagement",
      effectiveStartAt: LANDING_EXPERIMENT_RESTORATION_AT,
      comparisonVariantKey: "control",
      contractFingerprint:
        "feb83add967b003a9f8b6b17dcb52c5c654f56864cafe6a6e57a8e9426301165",
    }),
    historicalExperimentRevision({
      revisionId: "funnel_landing_hero_headline_v1_r3",
      sourceContractFingerprint:
        "1f4975a92997d6756efa79c31caa9bff0b044c57bc0d23047c2da7a21841f056",
      seedSalt: "funnel_landing_hero_headline_v1_r3",
      arms: [
        { key: "control", weight: 1 },
        { key: "affordable_help_for_seniors", weight: 1 },
        { key: "extra_hand", weight: 1 },
        { key: "delightful_help_around_the_house", weight: 1 },
      ],
      surfaces: ["join_landing"],
      exposureMetricKey: "landing_hero_viewed",
      primaryMetricKey: "landing_qualified_active_engagement",
      effectiveStartAt: VISIT_BOOKING_REALLOCATION_AT,
      effectiveEndAt: EXPERIMENT_PORTFOLIO_REALLOCATION_AT,
      comparisonVariantKey: "control",
      contractFingerprint:
        "f826bf54fb89520d1aaf981879762375639735e3125d5aaaaa86dd05e84618e3",
    }),
  ]),
  funnel_landing_hero_cta_copy_v1: Object.freeze([
    historicalExperimentRevision({
      revisionId: "funnel_landing_hero_cta_copy_v1_r2",
      sourceContractFingerprint:
        "7a39d51cff0f84e228b52b25d41bbfcd53302a4880a2701688714e1aa36ac839",
      seedSalt: "funnel_landing_hero_cta_copy_v1_r2",
      arms: [
        { key: "check_availability", weight: 25 },
        { key: "book_household_help", weight: 25 },
        { key: "start_to_do_list", weight: 25 },
        { key: "meet_your_helper", weight: 25 },
      ],
      surfaces: ["join_landing"],
      exposureMetricKey: "landing_hero_viewed",
      primaryMetricKey: "landing_cta_clicked",
      effectiveStartAt: LANDING_EXPERIMENT_RESTORATION_AT,
      comparisonVariantKey: "check_availability",
      contractFingerprint:
        "75e854c046d9dca448f637d7aeb4af0eba1c55babeb70f373262816d988c7d88",
    }),
    historicalExperimentRevision({
      revisionId: "funnel_landing_hero_cta_copy_v1_r3",
      sourceContractFingerprint:
        "17c91e14fd38e6660a2be66310e1f6d6faec92f450ba88010226dfa25b544993",
      seedSalt: "funnel_landing_hero_cta_copy_v1_r3",
      arms: [
        { key: "check_availability", weight: 1 },
        { key: "book_household_help", weight: 1 },
        { key: "start_to_do_list", weight: 1 },
        { key: "meet_your_helper", weight: 1 },
      ],
      surfaces: ["join_landing"],
      exposureMetricKey: "landing_hero_viewed",
      primaryMetricKey: "landing_cta_clicked",
      effectiveStartAt: VISIT_BOOKING_REALLOCATION_AT,
      effectiveEndAt: EXPERIMENT_PORTFOLIO_REALLOCATION_AT,
      comparisonVariantKey: "check_availability",
      contractFingerprint:
        "23aeb7bb2d198586f561735f96828a2adaa8a4a8ac981a3b4d8d1132cc5e81aa",
    }),
    historicalExperimentRevision({
      revisionId: "funnel_landing_hero_cta_copy_v1_r4",
      sourceContractFingerprint:
        "deef86ad483f62c234e365a6a091ccf6f1ddb8056fcb0ea1232436e3521c4677",
      seedSalt: "funnel_landing_hero_cta_copy_v1_r4",
      arms: [
        { key: "check_availability", weight: 1 },
        { key: "start_to_do_list", weight: 1 },
        { key: "meet_your_helper", weight: 1 },
      ],
      surfaces: ["join_landing"],
      exposureMetricKey: "landing_hero_viewed",
      primaryMetricKey: "landing_cta_clicked",
      effectiveStartAt: EXPERIMENT_PORTFOLIO_REALLOCATION_AT,
      effectiveEndAt: LANDING_HERO_CTA_COPY_EXPANSION_AT,
      comparisonVariantKey: "check_availability",
      contractFingerprint:
        "adc64fb2f38269263578118f70b13ded622cb286cfdc52811093d5ddae6af7cf",
    }),
    historicalExperimentRevision({
      revisionId: "funnel_landing_hero_cta_copy_v1_r5",
      sourceContractFingerprint:
        "3b1be2853b20d66393e875f835892c687b7865d4ab04ca976a73198b194cf61d",
      seedSalt: "funnel_landing_hero_cta_copy_v1_r5",
      arms: [
        { key: "check_availability", weight: 1 },
        { key: "start_to_do_list", weight: 1 },
        { key: "meet_your_helper", weight: 1 },
        { key: "meet_my_helper", weight: 1 },
        { key: "start_my_to_do_list", weight: 1 },
        { key: "plan_my_first_visit", weight: 1 },
        { key: "book_help_for_my_home", weight: 1 },
      ],
      surfaces: ["join_landing"],
      exposureMetricKey: "landing_hero_viewed",
      primaryMetricKey: "landing_cta_clicked",
      effectiveStartAt: LANDING_HERO_CTA_COPY_EXPANSION_AT,
      effectiveEndAt: LANDING_HERO_CTA_COPY_FOUR_ARM_AT,
      comparisonVariantKey: "check_availability",
      contractFingerprint:
        "07281d92740d1b1c093f8f573d91ba6c72f5fc698e403bbca00440fa409b1f2a",
    }),
  ]),
  funnel_landing_section_slot_3_v1: Object.freeze([
    historicalExperimentRevision({
      revisionId: "funnel_landing_section_slot_3_v1_r1",
      sourceContractFingerprint:
        "2454620c2ab44b69c57c1f801f4fcf0a5bf8a4f23d579798df4c18099dce2cee",
      seedSalt: "funnel_landing_section_slot_3_v1_r1",
      arms: [
        { key: "helpers", weight: 1 },
        { key: "helper-selectivity", weight: 1 },
        { key: "guarantee", weight: 1 },
        { key: "story", weight: 1 },
        { key: "all-are-welcome", weight: 1 },
      ],
      surfaces: ["join_landing"],
      exposureMetricKey: "landing_section_slot_assigned",
      primaryMetricKey: "landing_section_slot_pre_onboarding_opened",
      effectiveStartAt: VISIT_BOOKING_REALLOCATION_AT,
      effectiveEndAt: EXPERIMENT_PORTFOLIO_REALLOCATION_AT,
      contractFingerprint:
        "9e52f9ed2bab91c725affa82f81d2d978bc3a7b355ade31a912e1e6b916b7d0f",
      comparisonStatus: "no_valid_concurrent_control",
      noValidComparisonReason: "multi_arm_slot_has_no_control_variant",
      candidateExclusionPolicyKey: "landing_prior_slot_selections_v1",
      excludesExperimentKeys: [
        "funnel_landing_section_slot_1_v1",
        "funnel_landing_section_slot_2_v1",
      ],
    }),
    historicalExperimentRevision({
      revisionId: "funnel_landing_section_slot_3_v1_r2",
      sourceContractFingerprint: "b42509dcb5a348924ee96a7223287d04c05ab68b17ce8993e16a8f8517737d9a",
      seedSalt: "funnel_landing_section_slot_3_v1_r2",
      arms: [
        { key: "helper-selectivity", weight: 1 },
        { key: "guarantee", weight: 1 },
        { key: "story", weight: 1 },
        { key: "all-are-welcome", weight: 1 },
        { key: "credibility", weight: 1 },
      ],
      surfaces: ["join_landing"],
      exposureMetricKey: "landing_section_slot_assigned",
      primaryMetricKey: "landing_section_slot_pre_onboarding_opened",
      effectiveStartAt: EXPERIMENT_PORTFOLIO_REALLOCATION_AT,
      effectiveEndAt: LANDING_HERO_CTA_COPY_FOUR_ARM_AT,
      contractFingerprint: "4d502dba4c6568da1db1219b7fd67d40575b73466ef338168cbf62004fd390e7",
      comparisonStatus: "no_valid_concurrent_control",
      noValidComparisonReason: "multi_arm_slot_has_no_control_variant",
      candidateExclusionPolicyKey: "landing_prior_slot_selections_v1",
      excludesExperimentKeys: [],
    }),
  ]),
  funnel_landing_section_slot_4_v1: Object.freeze([
    historicalExperimentRevision({
      revisionId: "funnel_landing_section_slot_4_v1_r1",
      sourceContractFingerprint:
        "a3c9c619bf705ccb3e9a97edb0f23c652f7e228704acbae163ee2b6e5078253f",
      seedSalt: "funnel_landing_section_slot_4_v1_r1",
      arms: [
        { key: "reviews", weight: 1 },
        { key: "member-photos", weight: 1 },
        { key: "helpers", weight: 1 },
        { key: "helper-selectivity", weight: 1 },
        { key: "story", weight: 1 },
        { key: "credibility", weight: 1 },
      ],
      surfaces: ["join_landing"],
      exposureMetricKey: "landing_section_slot_assigned",
      primaryMetricKey: "landing_section_slot_pre_onboarding_opened",
      effectiveStartAt: LANDING_SECTION_SLOT_4_START_AT,
      effectiveEndAt: EXPERIMENT_PORTFOLIO_REALLOCATION_AT,
      contractFingerprint:
        "1f60bfdb2f50e3570cbda1f76364bc0c0cfe1729f34124937f62482b62344a61",
      comparisonStatus: "no_valid_concurrent_control",
      noValidComparisonReason: "multi_arm_slot_has_no_control_variant",
      candidateExclusionPolicyKey: "landing_prior_slot_selections_v1",
      excludesExperimentKeys: [
        "funnel_landing_section_slot_1_v1",
        "funnel_landing_section_slot_2_v1",
        "funnel_landing_section_slot_3_v1",
      ],
    }),
  ]),
  funnel_landing_section_slot_5_v1: Object.freeze([
    historicalExperimentRevision({
      revisionId: "funnel_landing_section_slot_5_v1_r1",
      contractFingerprint: "bf8a63a69e8f6fe0148b92837a2b243277260028626577bf049233e44d333144",
      sourceContractFingerprint: "c82377da0a3c9023219eb3b74cdbab7d749130c4dbb479400d88d97e51fb65bd",
      seedSalt: "funnel_landing_section_slot_5_v1_r1",
      arms: [
        { key: "video", weight: 1 },
        { key: "family-values", weight: 1 },
        { key: "helper-selectivity", weight: 1 },
        { key: "credibility", weight: 1 },
      ],
      surfaces: ["join_landing"],
      exposureMetricKey: "landing_section_slot_assigned",
      primaryMetricKey: "landing_section_slot_pre_onboarding_opened",
      effectiveStartAt: LANDING_SECTION_SLOT_5_START_AT,
      effectiveEndAt: LANDING_SLOT_3_PROMOTION_AT,
      comparisonStatus: "no_valid_concurrent_control",
      noValidComparisonReason: "multi_arm_slot_has_no_control_variant",
      candidateExclusionPolicyKey: "landing_prior_slot_selections_v1",
      excludesExperimentKeys: [
        "funnel_landing_section_slot_3_v1",
        "funnel_landing_section_slot_4_v1",
      ],
    }),
  ]),
  funnel_landing_hero_trust_signal_v1: Object.freeze([
    historicalExperimentRevision({
      revisionId: "funnel_landing_hero_trust_signal_v1_r2",
      sourceContractFingerprint:
        "70dd5bc4c84281c79b748b2eabaf3cda684718ec534332753f9e0df494a83cf5",
      seedSalt: "funnel_landing_hero_trust_signal_v1_r2",
      arms: [
        { key: "control", weight: 50 },
        { key: "hard_working_personable", weight: 50 },
      ],
      surfaces: ["join_landing"],
      exposureMetricKey: "landing_hero_viewed",
      primaryMetricKey: "landing_qualified_active_engagement",
      effectiveStartAt: LANDING_EXPERIMENT_RESTORATION_AT,
      comparisonVariantKey: "control",
      contractFingerprint:
        "af28f83657722810afd3513512790f8e0b9d77ef2864fb5442bb0c1fdb686ce4",
    }),
  ]),
  funnel_landing_mobile_hero_media_layout_v1: Object.freeze([
    historicalExperimentRevision({
      revisionId: "funnel_landing_mobile_hero_media_layout_v1_r2",
      sourceContractFingerprint:
        "25991e3491b4f0bb31401f2be4d5eb41ac529f3e0fa3b3517cfb319300a60005",
      kind: "factorial_test",
      seedSalt: "funnel_landing_mobile_hero_media_layout_v1_r2",
      arms: [
        { key: "founder_copy_first", weight: 25 },
        { key: "founder_media_first", weight: 25 },
        { key: "stack_copy_first", weight: 25 },
        { key: "stack_media_first", weight: 25 },
      ],
      surfaces: ["join_landing"],
      exposureMetricKey: "landing_hero_viewed",
      primaryMetricKey: "landing_pre_onboarding_opened",
      effectiveStartAt: LANDING_EXPERIMENT_RESTORATION_AT,
      comparisonVariantKey: "founder_copy_first",
      contractFingerprint:
        "a32dc8631a33b1017e116ef0d3f5c85d621d6c4b35990a524b16bf9b9777b443",
    }),
  ]),
  funnel_helper_discount_presentation_v1: Object.freeze([
    historicalExperimentRevision({
      revisionId: "funnel_helper_discount_presentation_v1_r2",
      sourceContractFingerprint:
        "da73a2016ebc4d5d5d439f2ffe277f38e811369047c782f26a47e2880f0c1307",
      seedSalt: "funnel_helper_discount_presentation_v1_r2",
      arms: [
        { key: "control", weight: 50 },
        { key: "sticky_bar", weight: 50 },
      ],
      surfaces: ["join_landing", "pre_onboarding_flow"],
      exposureMetricKey: "helper_discount_presentation_eligible_viewed",
      primaryMetricKey: "landing_pre_onboarding_opened",
      effectiveStartAt: LANDING_EXPERIMENT_RESTORATION_AT,
      comparisonVariantKey: "control",
      contractFingerprint:
        "0e56f36f8b7973d014af82477390f46c215900bb8d1569d1137770489950f9d0",
    }),
  ]),
  funnel_pre_onboarding_start_card_v2: Object.freeze([
    historicalExperimentRevision({
      revisionId: "funnel_pre_onboarding_start_card_v2_r1",
      sourceContractFingerprint:
        "e310d506f8de2ddb543573f4ea93ac33b44f1366f7ace4e5d0a7d5927f1cdc03",
      seedSalt: "funnel_pre_onboarding_start_card_v2",
      arms: [
        { key: "help_type_first", weight: 34 },
        { key: "location_first", weight: 33 },
        { key: "how_it_works_first", weight: 33 },
      ],
      surfaces: ["pre_onboarding_flow"],
      exposureMetricKey: "surface_viewed",
      primaryMetricKey: "outcome_member_account_created",
      effectiveStartAt: "2026-08-02T07:00:00.000Z",
      comparisonVariantKey: "help_type_first",
      contractFingerprint:
        "e27e819299b314961353c345d5da1c0bfc5254869b250e78bfc07b9f243e8307",
    }),
  ]),
  member_post_signup_dashboard_v3: Object.freeze([
    historicalExperimentRevision({
      revisionId: "member_post_signup_dashboard_v3_r1",
      sourceContractFingerprint:
        "6e4da7f1b5d0ebd89b786aff64235fedc3ea8072c17a1fadfc731f28818bf067",
      assignmentUnit: "member",
      seedSalt: "member_post_signup_dashboard_v3",
      arms: [
        { key: "legacy_onboarding", weight: 30 },
        { key: "dashboard_checklist_v2", weight: 70 },
      ],
      surfaces: [
        "member_post_signup",
        "member_dashboard_home",
        "member_dashboard_billing",
        "schedule_visit_flow",
      ],
      exposureMetricKey: "post_signup_experience_viewed",
      primaryMetricKey: "outcome_member_membership_paid",
      effectiveStartAt: "2026-08-06T07:00:00.000Z",
      comparisonVariantKey: "legacy_onboarding",
      contractFingerprint:
        "f8bcdc313562d15cc679c1750a04849743faae0958a5efc1d7d55616342dc387",
    }),
  ]),
  member_dashboard_checklist_order_v1: Object.freeze([
    historicalExperimentRevision({
      revisionId: "member_dashboard_checklist_order_v1_r1",
      sourceContractFingerprint:
        "3e36b717763eb011522b7646eda1ca88f030e72a34ae0f0650228dce58d5ae86",
      assignmentUnit: "member",
      seedSalt: "member_dashboard_checklist_order_v1",
      arms: [
        { key: "current_order", weight: 50 },
        { key: "book_visit_first", weight: 50 },
      ],
      surfaces: ["member_dashboard_home", "schedule_visit_flow"],
      exposureMetricKey: "new_member_checklist_viewed",
      primaryMetricKey: "outcome_member_visit_booked",
      effectiveStartAt: "2026-08-14T07:00:00.000Z",
      decisionGradeStartAt: "2026-08-16T07:00:00.000Z",
      comparisonVariantKey: "current_order",
      contractFingerprint:
        "4593de6500fd5c4b57461f2ba68e124b2b42a3984362cea2ce13ebf0f6c98aed",
    }),
  ]),
  member_post_signup_membership_discount_v1: Object.freeze([
    historicalExperimentRevision({
      revisionId: "member_post_signup_membership_discount_v1_r1",
      assignmentUnit: "member",
      seedSalt: "member_post_signup_membership_discount_v1",
      arms: [
        { key: "save_10_percent", weight: 25 },
        { key: "save_20_percent", weight: 25 },
        { key: "save_30_percent", weight: 25 },
        { key: "save_40_percent", weight: 25 },
      ],
      surfaces: [
        "member_dashboard_billing",
        "member_dashboard_home",
        "membership_checkout",
      ],
      exposureMetricKey: "membership_discount_offer_viewed",
      primaryMetricKey: "outcome_member_membership_paid",
      effectiveStartAt: "2026-08-14T07:00:00.000Z",
      decisionGradeStartAt: "2026-08-16T07:00:00.000Z",
      effectiveEndAt: "2026-08-21T07:00:00.000Z",
      comparisonVariantKey: "save_10_percent",
      contractFingerprint:
        "a66095afaf251f9a27005d1c622baec7fdbde9212336e8738dcf9438c4d44fb5",
      sourceContractFingerprint:
        "869b66cff7b365ceb73b6565a42ef7eb3fcf6dea834f50ac1439f5ccef0d342b",
      acceptedLegacyAssignmentFingerprints: Object.freeze([
        "869b66cff7b365ceb73b6565a42ef7eb3fcf6dea834f50ac1439f5ccef0d342b",
      ]),
    }),
    historicalExperimentRevision({
      revisionId: "member_post_signup_membership_discount_v1_r2",
      sourceContractFingerprint:
        "33d992d1dcf8dd2a1c72e728c7b64bbeaf69486278defbeef91bb19d5e340d0f",
      assignmentUnit: "member",
      seedSalt: "member_post_signup_membership_discount_v1",
      arms: [
        { key: "save_10_percent", weight: 50 },
        { key: "save_20_percent", weight: 50 },
      ],
      surfaces: [
        "member_dashboard_billing",
        "member_dashboard_home",
        "membership_checkout",
      ],
      exposureMetricKey: "membership_discount_offer_viewed",
      primaryMetricKey: "outcome_member_membership_paid",
      effectiveStartAt: "2026-08-21T07:00:00.000Z",
      comparisonVariantKey: "save_10_percent",
      contractFingerprint:
        "9aab500d93210637feea16561db83e212fc004aa1a6d2854dc2504a4759cd369",
    }),
  ]),
});

const normalizeEventMetricKey = (value = "") =>
  String(value || "")
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "_")
    .replace(/^_+|_+$/g, "")
    .replace(/_+/g, "_");

export const toOutcomeMetricKey = (eventName = "") =>
  `outcome_${normalizeEventMetricKey(eventName)}`;

export const EXPERIMENT_KEYS = {
  visitMarginV1: "visit_margin_v1",
  membershipInitialChoiceOptionsV1: "membership_initial_choice_options_v1",
  landingCredibilityCountersV1: "funnel_landing_credibility_counters_v1",
  landingHelpersTitleV1: "funnel_landing_helpers_title_v1",
  landingReviewsTitleV1: "funnel_landing_reviews_title_v1",
  landingHelperSelectivityTitleV1: "funnel_landing_helper_selectivity_title_v1",
  landingFounderStoryTitleV1: "funnel_landing_founder_story_title_v1",

  funnelLandingPuzzlesV1: "funnel_landing_puzzles_v1",
  memberDashboardPuzzlesV1: "member_dashboard_puzzles_v1",
  funnelLandingHeroHeadlineV1: "funnel_landing_hero_headline_v1",
  funnelLandingHeroReviewQuoteV1: "funnel_landing_hero_review_quote_v1",
  funnelLandingHeroSelectivityV1: "funnel_landing_hero_selectivity_v1",
  funnelLandingTopSectionOrderV1: "funnel_landing_top_section_order_v1",
  funnelLandingReviewLayoutV1: "funnel_landing_review_layout_v1",
  funnelLandingHeroVideoV1: "funnel_landing_hero_video_v1",
  funnelLandingHeroTaglineV1: "funnel_landing_hero_tagline_v1",
  funnelLandingPwycChartCenterV1: "funnel_landing_pwyc_chart_center_v1",
  funnelLandingAudienceLanguageV1: "funnel_landing_audience_language_v1",
  funnelSitewideBbbBrandingV1: "funnel_sitewide_bbb_branding_v1",
  funnelLandingHeroCtaCopyV1: "funnel_landing_hero_cta_copy_v1",
  funnelLandingHeroTrustSignalV1: "funnel_landing_hero_trust_signal_v1",
  funnelLandingDesktopHeroMediaV1: "funnel_landing_desktop_hero_media_v1",
  funnelLandingMobileHeroMediaLayoutV1:
    "funnel_landing_mobile_hero_media_layout_v1",
  funnelLandingSectionOrderV1: "funnel_landing_section_order_v1",
  funnelLandingSectionSlot1V1: "funnel_landing_section_slot_1_v1",
  funnelLandingSectionSlot2V1: "funnel_landing_section_slot_2_v1",
  funnelLandingSectionSlot3V1: "funnel_landing_section_slot_3_v1",
  funnelLandingSectionSlot4V1: "funnel_landing_section_slot_4_v1",
  funnelLandingSectionSlot5V1: "funnel_landing_section_slot_5_v1",
  funnelLandingHelperSelectivityPresenceV1:
    "funnel_landing_helper_selectivity_presence_v1",
  funnelHelperDiscountPresentationV1: "funnel_helper_discount_presentation_v1",
  funnelLandingCredibilityMiddleBandsV1: "funnel_landing_credibility_middle_bands_v1",
  funnelBookingCheckoutLocalCredibilityV1:
    "funnel_booking_checkout_local_credibility_v1",
  funnelSameDayFirstVisitFreeHourV1: "funnel_same_day_first_visit_free_hour_v1",
  funnelPreOnboardingContactConsolidationV1:
    "funnel_pre_onboarding_contact_consolidation_v1",
  funnelPreOnboardingLocationFirstShortV1:
    "funnel_pre_onboarding_location_first_short_v1",
  funnelPreOnboardingStartCardV2: "funnel_pre_onboarding_start_card_v2",
  funnelPreOnboardingFlowLengthV2: "funnel_pre_onboarding_flow_length_v2",
  funnelPreOnboardingContactEndingV2: "funnel_pre_onboarding_contact_ending_v2",
  funnelPreOnboardingFlowVersionV1: "funnel_pre_onboarding_flow_version_v1",
  funnelPreOnboardingLandingEducationV1: "funnel_pre_onboarding_landing_education_v1",
  funnelPreOnboardingV2HelperLobbyV1:
    "funnel_pre_onboarding_v2_helper_lobby_v1",
  funnelPreOnboardingV2ReservationEntryV1:
    "funnel_pre_onboarding_v2_reservation_entry_v1",
  funnelLocalHelperMomentumCardV1: "funnel_local_helper_momentum_card_v1",
  memberPostSignupDashboardV3: "member_post_signup_dashboard_v3",
  memberDashboardChecklistOrderV1: "member_dashboard_checklist_order_v1",
  memberPostSignupMembershipDiscountV1:
    "member_post_signup_membership_discount_v1",
  memberScheduleVisitPickerV2: "member_schedule_visit_picker_v2",
  memberDashboardFounderWelcomeCardV1:
    "member_dashboard_founder_welcome_card_v1",
  memberDashboardFounderNoteV2: "member_dashboard_founder_note_v2",
  memberStampCardV1: "member_stamp_card_v1",
  memberAddressCaptureModeV1: "member_address_capture_mode_v1",
  memberFirstVisitReservationGateV2: "member_first_visit_reservation_gate_v2",
  memberHelperGalleryVisibilityV1: "member_helper_gallery_visibility_v1",
  memberFirstBookingHelperLobbyV1: "member_first_booking_helper_lobby_v1",
  memberFirstBookingEntryModeV1: "member_first_booking_entry_mode_v1",
  memberFirstVisitHelperCountDefaultV1:
    "member_first_visit_helper_count_default_v1",
};

const FUNNEL_OUTCOME_METRICS = [
  {
    key: toOutcomeMetricKey("member_account_created"),
    label: "Account created",
    kind: "outcome",
    sourceEvent: "member_account_created",
  },
  {
    key: toOutcomeMetricKey("member_onboarding_completed"),
    label: "Onboarding completed",
    kind: "outcome",
    sourceEvent: "member_onboarding_completed",
  },
  {
    key: toOutcomeMetricKey("member_visit_booked"),
    label: "Visit booked",
    kind: "outcome",
    sourceEvent: "member_visit_booked",
  },
  {
    key: toOutcomeMetricKey("member_membership_paid"),
    label: "Membership paid",
    kind: "outcome",
    sourceEvent: "member_membership_paid",
  },
  {
    key: toOutcomeMetricKey("member_membership_refunded"),
    label: "Membership refunded",
    kind: "outcome",
    sourceEvent: "member_membership_refunded",
  },
  {
    key: toOutcomeMetricKey("member_visit_refunded"),
    label: "Visit refunded",
    kind: "outcome",
    sourceEvent: "member_visit_refunded",
  },
];

const LANDING_HERO_ENGAGEMENT_PRIMARY_METRIC_KEY =
  "landing_qualified_active_engagement";
const LANDING_HERO_CTA_COPY_PRIMARY_METRIC_KEY = "landing_cta_clicked";
const LANDING_SECTION_ORDER_PRIMARY_METRIC_KEY =
  "landing_section_order_binary_cta_click";

const LANDING_HERO_GUARDRAIL_METRIC_KEYS = [
  "landing_cta_viewed",
  "landing_cta_clicked",
  "landing_pre_onboarding_opened",
  toOutcomeMetricKey("member_account_created"),
  toOutcomeMetricKey("member_membership_paid"),
  toOutcomeMetricKey("member_membership_refunded"),
];

const LANDING_SECTION_ORDER_RANKER_VERSION = "landing_section_order_ranker_v1";
const LANDING_SECTION_ORDER_SLOT_CANDIDATE_POOLS = {
  slot1: ["reviews", "member-photos", "helpers", "howItWorks", "credibility"],
  slot2: [
    "reviews",
    "member-photos",
    "helpers",
    "howItWorks",
    "credibility",
    "benefits",
    "all-are-welcome",
  ],
  slot3: [
    "tasks",
    "stories",
    "member-photos",
    "helpers",
    "helper-selectivity",
    "guarantee",
    "story",
    "all-are-welcome",
  ],
};
const LANDING_SECTION_ORDER_EXCLUDED_TOP_SLOT_KEYS = ["highlighted-helper"];
const LANDING_SECTION_ORDER_AFTER_TOP_SLOT_KEYS = ["highlighted-helper"];
const LANDING_SECTION_ORDER_SLOT_BACKGROUNDS = [
  "blue_green_gradient",
  "white",
  "light_blue",
  "white",
];
const LANDING_SECTION_ORDER_ADAPTIVE_POLICY = {
  mode: "balanced_exploration",
  minimumSlotOneVisibleViewsPerCandidate: 300,
  minimumSlotOneMeaningfulViewsPerCandidate: 300,
  minimumRunWeeksBeforeAdaptiveReview: 4,
  preferredRunWeeksBeforeAdaptiveReview: 8,
  minimumExplorationShare: 0.1,
  promotionRule:
    "Keep weights balanced until every active slot-one candidate has several hundred visible-slot exposures plus a meaningful-view quality read, or a four-to-eight-week read; do not promote tiny-sample winners.",
};

const LANDING_SECTION_SLOT_LABELS = Object.freeze({
  "all-are-welcome": "All Are Welcome",
  credibility: "Credibility",
  guarantee: "Guarantee",
  helpers: "Helpers",
  "helper-selectivity": "Helper Selectivity",
  "member-photos": "Member Photos",
  reviews: "Reviews",
  story: "Founder Story",
  video: "Senior Testimonials",
  "family-values": "Family Values",
});

export const LANDING_SECTION_SLOT_EXPERIMENTS = Object.freeze([
  Object.freeze({
    experimentKey: EXPERIMENT_KEYS.funnelLandingSectionSlot1V1,
    revisionId: "funnel_landing_section_slot_1_v1_r1",
    slotKey: "slot1",
    status: "retired",
    effectiveEndAt: EXPERIMENT_PORTFOLIO_REALLOCATION_AT,
    promotedVariantKey: "helpers",
    sourceContractFingerprint:
      "1501ffbdcf67ce090f6ca3b20156de12e40f3582939b62ca286491517d661199",
    candidates: Object.freeze(["member-photos", "helpers", "credibility"]),
    excludesExperimentKeys: Object.freeze([]),
  }),
  Object.freeze({
    experimentKey: EXPERIMENT_KEYS.funnelLandingSectionSlot2V1,
    revisionId: "funnel_landing_section_slot_2_v1_r1",
    slotKey: "slot2",
    status: "retired",
    effectiveEndAt: EXPERIMENT_PORTFOLIO_REALLOCATION_AT,
    promotedVariantKey: "reviews",
    sourceContractFingerprint:
      "ea3ad05a399b13b611470967a3b9267f3f73f3ed9bda69d71b024951da7148f2",
    candidates: Object.freeze([
      "reviews",
      "member-photos",
      "helpers",
      "credibility",
      "all-are-welcome",
    ]),
    excludesExperimentKeys: Object.freeze([
      EXPERIMENT_KEYS.funnelLandingSectionSlot1V1,
    ]),
  }),
  Object.freeze({
    experimentKey: EXPERIMENT_KEYS.funnelLandingSectionSlot3V1,
    revisionId: "funnel_landing_section_slot_3_v1_r3",
    slotKey: "slot3",
    status: "retired",
    effectiveStartAt: LANDING_HERO_CTA_COPY_FOUR_ARM_AT,
    decisionGradeStartAt: LANDING_HERO_CTA_COPY_FOUR_ARM_AT,
    effectiveEndAt: LANDING_SLOT_3_PROMOTION_AT,
    promotedVariantKey: "helper-selectivity",
    continuePreviousRevisionUntilStart: true,
    candidates: Object.freeze([
      "helper-selectivity",
      "guarantee",
      "all-are-welcome",
      "credibility",
    ]),
    excludesExperimentKeys: Object.freeze([]),
  }),
  Object.freeze({
    experimentKey: EXPERIMENT_KEYS.funnelLandingSectionSlot4V1,
    revisionId: "funnel_landing_section_slot_4_v1_r2",
    slotKey: "slot4",
    status: "retired",
    effectiveStartAt: EXPERIMENT_PORTFOLIO_REALLOCATION_AT,
    decisionGradeStartAt: EXPERIMENT_PORTFOLIO_REALLOCATION_AT,
    effectiveEndAt: LANDING_HERO_CTA_COPY_FOUR_ARM_AT,
    promotedVariantKey: "story",
    sourceContractFingerprint: "c9d6af37f68062af9c0be49b5ddf78d071ba981ae90b0d3fe656884ade47c670",
    candidates: Object.freeze([
      "member-photos",
      "helper-selectivity",
      "story",
      "credibility",
    ]),
    excludesExperimentKeys: Object.freeze([
      EXPERIMENT_KEYS.funnelLandingSectionSlot3V1,
    ]),
  }),
  Object.freeze({
    experimentKey: EXPERIMENT_KEYS.funnelLandingSectionSlot5V1,
    revisionId: "funnel_landing_section_slot_5_v1_r2",
    slotKey: "slot5",
    effectiveStartAt: LANDING_SLOT_3_PROMOTION_AT,
    decisionGradeStartAt: LANDING_SLOT_3_PROMOTION_AT,
    continuePreviousRevisionUntilStart: true,
    candidates: Object.freeze([
      "video",
      "family-values",
      "guarantee",
      "credibility",
    ]),
    excludesExperimentKeys: Object.freeze([
      EXPERIMENT_KEYS.funnelLandingSectionSlot3V1,
      EXPERIMENT_KEYS.funnelLandingSectionSlot4V1,
    ]),
  }),
]);

const markPrimaryGoal = (goals = [], primaryGoalKey = "") =>
  goals.reduce((orderedGoals, { primary: _primary, ...goal }) => {
    const markedGoal =
      goal.key === primaryGoalKey ? { ...goal, primary: true } : goal;
    if (markedGoal.primary) orderedGoals.unshift(markedGoal);
    else orderedGoals.push(markedGoal);
    return orderedGoals;
  }, []);

const LANDING_HERO_GOAL_CANDIDATES = [
  {
    key: "qualified_active_engagement_per_landing_session",
    label: "Qualified active engagement per Landing session",
    type: "session_rate",
    horizon: "short",
    direction: "increase",
    denominatorEventName: "landing_viewed",
    numeratorEventName: LANDING_HERO_ENGAGEMENT_PRIMARY_METRIC_KEY,
    minimumDenominator: 250,
    practicalLift: 0.05,
  },
  {
    key: "hero_clicks_per_landing_session",
    label: "Hero CTA clicks per Landing session",
    type: "session_rate",
    horizon: "short",
    direction: "increase",
    denominatorEventName: "landing_viewed",
    numeratorEventName: "landing_cta_clicked",
    numeratorProperties: {
      ctaId: "hero_primary",
    },
    minimumDenominator: 250,
    practicalLift: 0.04,
  },
  {
    key: "hero_visible_to_click_session_rate",
    label: "Hero CTA visible-to-click session rate",
    type: "session_rate",
    horizon: "short",
    direction: "increase",
    denominatorEventName: "landing_cta_viewed",
    denominatorProperties: {
      ctaId: "hero_primary",
    },
    numeratorEventName: "landing_cta_clicked",
    numeratorProperties: {
      ctaId: "hero_primary",
    },
    minimumDenominator: 150,
    practicalLift: 0.05,
  },
  {
    key: "pre_onboarding_opens_per_landing_session",
    label: "Pre-onboarding opens per Landing session",
    type: "session_rate",
    horizon: "short",
    direction: "increase",
    denominatorEventName: "landing_viewed",
    numeratorEventName: "landing_pre_onboarding_opened",
    numeratorProperties: {
      ctaId: "hero_primary",
    },
    minimumDenominator: 250,
    practicalLift: 0.05,
  },
  {
    key: "account_created_per_landing_session",
    label: "Accounts created per Landing session",
    type: "session_rate",
    horizon: "long",
    direction: "increase",
    denominatorEventName: "landing_viewed",
    numeratorConversionEventName: "member_account_created",
    minimumDenominator: 350,
    practicalLift: 0.08,
  },
  {
    key: "membership_paid_per_landing_session",
    label: "Membership purchases per Landing session",
    type: "session_rate",
    horizon: "long",
    direction: "increase",
    denominatorEventName: "landing_viewed",
    numeratorConversionEventName: "member_membership_paid",
    minimumDenominator: 500,
    practicalLift: 0.08,
  },
  {
    key: "membership_refund_per_landing_session",
    label: "Membership refunds per Landing session",
    type: "session_rate",
    horizon: "long",
    direction: "decrease",
    denominatorEventName: "landing_viewed",
    numeratorConversionEventName: "member_membership_refunded",
    minimumDenominator: 500,
    practicalLift: 0.1,
  },
  {
    key: "visit_refund_per_landing_session",
    label: "Visit refunds per Landing session",
    type: "session_rate",
    horizon: "long",
    direction: "decrease",
    denominatorEventName: "landing_viewed",
    numeratorConversionEventName: "member_visit_refunded",
    minimumDenominator: 500,
    practicalLift: 0.1,
  },
];

const LANDING_SECTION_ORDER_GOALS = markPrimaryGoal(
  funnelExperimentGoals[EXPERIMENT_KEYS.funnelLandingSectionOrderV1]?.goals ||
    [],
  "binary_cta_click_per_visible_section_order_session",
);

const HELPER_SELECTIVITY_SECTION_KEY = "helper-selectivity";
const HELPER_SELECTIVITY_PRESENCE_ASSIGNED_METRIC_KEY =
  "landing_helper_selectivity_presence_assigned";
const HELPER_SELECTIVITY_PRESENCE_PRIMARY_METRIC_KEY =
  "landing_pre_onboarding_opened";
const HELPER_SELECTIVITY_PRESENCE_GOALS = markPrimaryGoal(
  funnelExperimentGoals[
    EXPERIMENT_KEYS.funnelLandingHelperSelectivityPresenceV1
  ]?.goals || [],
  "pre_onboarding_opens_per_assigned_helper_selectivity_presence_session",
);
const HELPER_SELECTIVITY_PRESENCE_GUARDRAIL_METRIC_KEYS = [
  "landing_cta_clicked",
  toOutcomeMetricKey("member_account_created"),
  toOutcomeMetricKey("member_membership_paid"),
  toOutcomeMetricKey("member_membership_refunded"),
  toOutcomeMetricKey("member_visit_refunded"),
];
const HELPER_SELECTIVITY_PRESENCE_METRICS = [
  {
    key: HELPER_SELECTIVITY_PRESENCE_ASSIGNED_METRIC_KEY,
    label: "Assigned helper-selectivity presence session",
    kind: "exposure",
  },
  {
    key: HELPER_SELECTIVITY_PRESENCE_PRIMARY_METRIC_KEY,
    label: "Pre-onboarding opened",
    kind: "conversion",
  },
  {
    key: "landing_section_visible",
    label: "Landing section visible",
    kind: "view",
  },
  {
    key: "landing_section_meaningfully_viewed",
    label: "Landing section meaningfully viewed",
    kind: "view",
  },
  {
    key: "landing_cta_clicked",
    label: "Landing CTA clicked",
    kind: "action",
  },
];

const HELPER_DISCOUNT_PRESENTATION_ELIGIBLE_METRIC_KEY =
  "helper_discount_presentation_eligible_viewed";
const HELPER_DISCOUNT_PRESENTATION_OFFER_VIEWED_METRIC_KEY =
  "helper_discount_presentation_offer_viewed";
const HELPER_DISCOUNT_STICKY_BAR_CLICKED_METRIC_KEY =
  "helper_discount_sticky_bar_clicked";
const HELPER_DISCOUNT_STICKY_BAR_DISMISSED_METRIC_KEY =
  "helper_discount_sticky_bar_dismissed";
const HELPER_DISCOUNT_PRESENTATION_PRIMARY_METRIC_KEY =
  "landing_pre_onboarding_opened";
const HELPER_DISCOUNT_PRESENTATION_GOALS = markPrimaryGoal(
  funnelExperimentGoals[EXPERIMENT_KEYS.funnelHelperDiscountPresentationV1]
    ?.goals || [],
  "pre_onboarding_opens_per_eligible_helper_discount_view",
);
const HELPER_DISCOUNT_PRESENTATION_GUARDRAIL_METRIC_KEYS = [
  "landing_cta_clicked",
  HELPER_DISCOUNT_STICKY_BAR_DISMISSED_METRIC_KEY,
  toOutcomeMetricKey("member_account_created"),
  toOutcomeMetricKey("member_membership_paid"),
  toOutcomeMetricKey("member_membership_refunded"),
  toOutcomeMetricKey("member_visit_refunded"),
];
const HELPER_DISCOUNT_PRESENTATION_METRICS = [
  {
    key: HELPER_DISCOUNT_PRESENTATION_ELIGIBLE_METRIC_KEY,
    label: "Eligible helper-discount view",
    kind: "exposure",
  },
  {
    key: HELPER_DISCOUNT_PRESENTATION_OFFER_VIEWED_METRIC_KEY,
    label: "Helper discount offer viewed",
    kind: "view",
  },
  {
    key: HELPER_DISCOUNT_STICKY_BAR_CLICKED_METRIC_KEY,
    label: "Helper discount sticky bar clicked",
    kind: "action",
  },
  {
    key: HELPER_DISCOUNT_STICKY_BAR_DISMISSED_METRIC_KEY,
    label: "Helper discount sticky bar dismissed",
    kind: "action",
  },
  {
    key: "landing_cta_clicked",
    label: "Landing CTA clicked",
    kind: "action",
  },
  {
    key: HELPER_DISCOUNT_PRESENTATION_PRIMARY_METRIC_KEY,
    label: "Pre-onboarding opened",
    kind: "conversion",
  },
];

const LANDING_HERO_ENGAGEMENT_GOALS = markPrimaryGoal(
  LANDING_HERO_GOAL_CANDIDATES,
  "qualified_active_engagement_per_landing_session",
);

const LANDING_HERO_CTA_COPY_GOALS = markPrimaryGoal(
  LANDING_HERO_GOAL_CANDIDATES,
  "hero_clicks_per_landing_session",
);

const LANDING_HERO_TRUST_SIGNAL_GOALS = markPrimaryGoal(
  funnelExperimentGoals[EXPERIMENT_KEYS.funnelLandingHeroTrustSignalV1]
    ?.goals || [],
  "qualified_active_engagement_per_landing_session",
);

const LANDING_HERO_EXPERIMENT_METRICS = [
  {
    key: "landing_hero_viewed",
    label: "Hero viewed",
    kind: "exposure",
  },
  {
    key: LANDING_HERO_ENGAGEMENT_PRIMARY_METRIC_KEY,
    label: "Qualified active engagement",
    kind: "action",
  },
  {
    key: "landing_cta_viewed",
    label: "Hero CTA viewed",
    kind: "view",
  },
  {
    key: "landing_cta_clicked",
    label: "Hero CTA clicked",
    kind: "action",
  },
  {
    key: "landing_pre_onboarding_opened",
    label: "Pre-onboarding opened",
    kind: "conversion",
  },
  {
    key: "landing_section_meaningfully_viewed",
    label: "Landing section meaningfully viewed",
    kind: "view",
  },
];

const LANDING_AUDIENCE_LANGUAGE_METRICS = [
  {
    key: "landing_audience_language_viewed",
    label: "Landing audience language viewed",
    kind: "exposure",
  },
  {
    key: "landing_pre_onboarding_opened",
    label: "Pre-onboarding opened",
    kind: "conversion",
  },
];

const LANDING_SECTION_SLOT_METRICS = [
  {
    key: "landing_section_slot_assigned",
    label: "Slot assigned",
    kind: "exposure",
  },
  {
    key: "landing_section_slot_visible",
    label: "Selected section visible",
    kind: "view",
  },
  {
    key: "landing_section_slot_meaningfully_viewed",
    label: "Selected section meaningfully viewed",
    kind: "view",
  },
  {
    key: "landing_section_slot_cta_clicked",
    label: "CTA clicked after selected section visibility",
    kind: "conversion",
  },
  {
    key: "landing_section_slot_pre_onboarding_opened",
    label: "Pre-onboarding opened after selected section visibility",
    kind: "conversion",
  },
];

const landingSectionSlotExperimentDefinition = ({
  experimentKey,
  revisionId,
  slotKey,
  effectiveStartAt = VISIT_BOOKING_REALLOCATION_AT,
  decisionGradeStartAt = effectiveStartAt,
  effectiveEndAt = null,
  status = "active",
  promotedVariantKey = "",
  continuePreviousRevisionUntilStart = false,
  candidates,
  excludesExperimentKeys,
}) => {
  const slotNumber = Number(slotKey.replace("slot", ""));
  const variantDefinitions = candidates.map((sectionKey) => ({
    key: sectionKey,
    label: LANDING_SECTION_SLOT_LABELS[sectionKey] || sectionKey,
    description: `Shows ${
      LANDING_SECTION_SLOT_LABELS[sectionKey] || sectionKey
    } in Landing ${slotKey}.`,
    payload: {
      landingSectionSlot: {
        enabled: true,
        slotKey,
        sectionKey,
      },
    },
  }));

  return {
    key: experimentKey,
    revisionId,
    effectiveStartAt,
    ...(effectiveEndAt ? { effectiveEndAt } : {}),
    fallbackVariantKey: candidates[0],
    surfaceSlot: `landing_section_${slotKey}`,
    searchToken: `${SEARCH_PREFIX}${experimentKey}`,
    title: `Landing Section Slot ${slotNumber}`,
    description: `Selects the actual section rendered in the ${slotKey} position after the fixed Landing hero.`,
    status,
    continuePreviousRevisionUntilStart,
    kind: "ab_test",
    owner: "growth",
    tags: ["funnel", "landing", "section_slot", slotKey, "experiments_v1"],
    surfaces: ["join_landing"],
    hypothesis:
      "The section shown in this post-hero position can improve meaningful attention and downstream member conversion.",
    allocationReason:
      "Equal allocation across the candidates remaining after earlier slot selections are removed.",
    assignment: {
      ...CANONICAL_RANDOMIZED_ASSIGNMENT_POLICY,
      unit: "session",
      cadence: "static",
      seedSalt: revisionId,
      revisionId,
      candidateExclusionPolicyKey: "landing_prior_slot_selections_v1",
      excludesExperimentKeys: [...excludesExperimentKeys],
      variants: candidates.map((key) => ({ key, weight: 1 })),
    },
    variants: variantDefinitions,
    exposureMetricKey: "landing_section_slot_assigned",
    primaryMetricKey: "landing_section_slot_pre_onboarding_opened",
    northStarMetricKey: "value_metrics_d30_contribution_profit_cents",
    guardrailMetricKeys: [
      toOutcomeMetricKey("member_account_created"),
      toOutcomeMetricKey("member_visit_booked"),
      toOutcomeMetricKey("member_membership_refunded"),
    ],
    minimumSample: {
      sessionsPerVariant: 300,
      visibleViewsPerVariant: 200,
      meaningfulViewsPerVariant: 100,
      preOnboardingOpensPerVariant: 35,
    },
    stopRule:
      promotedVariantKey
        ? `${status === "retired" ? "Retired" : "Ends"} at ${effectiveEndAt}; ${
            LANDING_SECTION_SLOT_LABELS[promotedVariantKey] ||
            promotedVariantKey
          } was promoted as the fixed product section.`
        : "Review after minimum sample or 14 complete rollup days; compare the section variants within this slot only and preserve downstream account, booking, paid-membership, profit, and refund guardrails.",
    goals:
      status === "retired"
        ? []
        : funnelExperimentGoals[experimentKey]?.goals || [],
    metrics: LANDING_SECTION_SLOT_METRICS,
    outcomeMetrics: FUNNEL_OUTCOME_METRICS.filter((metric) =>
      [
        "member_account_created",
        "member_visit_booked",
        "member_membership_refunded",
      ].includes(metric.sourceEvent),
    ),
  };
};

const buildSimpleMemberBookingExperiment = ({
  key,
  revisionId,
  title,
  description,
  surfaces,
  eligibilityPolicyKey,
  variants,
  fallbackVariantKey = variants[0].key,
  exposureMetricKey,
  actionMetrics = [],
  hypothesis,
  effectiveStartAt = MEMBER_FIRST_BOOKING_EXPERIMENTS_START_AT,
  surfaceSlot = null,
  supersedesExperimentKeys = [],
}) => ({
  key,
  revisionId,
  effectiveStartAt,
  referenceVariantKey: variants[0].key,
  fallbackVariantKey,
  allocationReason:
    "Equal member-level allocation provides a clean concurrent comparison for one narrow booking behavior.",
  searchToken: `${SEARCH_PREFIX}${key}`,
  title,
  description,
  status: "active",
  kind: "ab_test",
  owner: "growth",
  tags: ["member_dashboard", "booking", "experiments_v1"],
  surfaces,
  ...(surfaceSlot ? { surfaceSlot } : {}),
  ...(supersedesExperimentKeys.length
    ? { supersedesExperimentKeys }
    : {}),
  hypothesis,
  assignment: {
    ...CANONICAL_RANDOMIZED_ASSIGNMENT_POLICY,
    eligibilityPolicyKey,
    unit: "member",
    cadence: "static",
    seedSalt: revisionId,
    revisionId,
    variants: variants.map(({ key: variantKey }) => ({
      key: variantKey,
      weight: 1,
    })),
  },
  variants,
  exposureMetricKey,
  primaryMetricKey: toOutcomeMetricKey("member_visit_booked"),
  primaryMetricLabel: "Visit bookings per assigned member",
  northStarMetricKey: "value_metrics_d30_contribution_profit_cents",
  guardrailMetricKeys: [
    toOutcomeMetricKey("member_membership_refunded"),
    toOutcomeMetricKey("member_visit_refunded"),
  ],
  minimumSample: {
    assignedMembersPerVariant: 200,
    eligibleViewsPerVariant: 120,
    visitBookingsPerVariant: 20,
  },
  stopRule:
    "Choose a winner only after the minimum sample and D30 guardrails are available; stop early for a material refund or booking-regression signal.",
  goals: funnelExperimentGoals[key]?.goals || [],
  metrics: [
    {
      key: exposureMetricKey,
      label: "Eligible surface viewed",
      kind: "exposure",
    },
    ...actionMetrics,
  ],
  outcomeMetrics: FUNNEL_OUTCOME_METRICS,
});

const EXPERIMENT_DEFINITIONS = {
  [EXPERIMENT_KEYS.visitMarginV1]: {
    key: "visit_margin_v1", revisionId: "visit_margin_v1_r1", effectiveStartAt: "2026-11-04T02:23:20.000Z",
    referenceVariantKey: "margin_25", fallbackVariantKey: "margin_25", surfaceSlot: "new_signup_visit_margin",
    title: "New Signup Visit Margin", description: "Tests 25% versus 30% minimum visit margin for new accounts only; existing accounts keep their policy.",
    status: "active", kind: "ab_test", owner: "growth",
    surfaces: ["app_root", "landing_pricing", "account_creation"],
    hypothesis: "A 30% minimum margin increases gross profit from new accounts without materially reducing visit bookings.",
    assignment: { ...CANONICAL_RANDOMIZED_ASSIGNMENT_POLICY, eligibilityPolicyKey: "new_account_after_cutover", unit: "session", cadence: "static", seedSalt: "visit_margin_v1_r1", revisionId: "visit_margin_v1_r1", variants: [{ key: "margin_25", weight: 1 }, { key: "margin_30", weight: 1 }] },
    variants: [{ key: "margin_25", label: "25% margin", payload: { visitMargin: { minimumGrossMarginBps: 2500 } } }, { key: "margin_30", label: "30% margin", payload: { visitMargin: { minimumGrossMarginBps: 3000 } } }],
    exposureMetricKey: "visit_margin_pricing_viewed", primaryMetricKey: "outcome_member_visit_booked",
    primaryMetricLabel: "Visit bookings per assigned session", northStarMetricKey: "value_metrics_d30_contribution_profit_cents",
    tags: ["funnel", "pricing", "new_signup"],
    minimumSample: { assignedSessionsPerVariant: 400, visitBookingsPerVariant: 30 },
    stopRule: "Use completed canonical financial horizons and booking/refund guardrails before promotion; stop for a material billing or booking regression.",
    goals: funnelExperimentGoals[EXPERIMENT_KEYS.visitMarginV1]?.goals || [],
    metrics: [{ key: "visit_margin_pricing_viewed", label: "Visit pricing viewed", kind: "exposure" }],
    outcomeMetrics: FUNNEL_OUTCOME_METRICS,
  },

  [EXPERIMENT_KEYS.membershipInitialChoiceOptionsV1]: {
    key: "membership_initial_choice_options_v1", revisionId: "membership_initial_choice_options_v1_r1", effectiveStartAt: "2026-11-03T19:45:00.000Z",
    referenceVariantKey: "control", fallbackVariantKey: "control", surfaceSlot: "membership_initial_choice_options",
    title: "Initial Membership Choice Options", description: "Tests the initial membership card with and without zero and remind later.", status: "active", kind: "ab_test", owner: "growth",
    tags: ["membership"], surfaces: ["schedule_membership_choice"],
    assignment: { ...CANONICAL_RANDOMIZED_ASSIGNMENT_POLICY, unit: "session", cadence: "static", seedSalt: "membership_initial_choice_options_v1_r1", revisionId: "membership_initial_choice_options_v1_r1", variants: [{ key: "control", weight: 1 }, { key: "no_zero_or_skip", weight: 1 }] },
    variants: [{ key: "control", label: "Zero and remind later" }, { key: "no_zero_or_skip", label: "No zero or remind later" }],
    exposureMetricKey: "initial_membership_choice_shown", primaryMetricKey: toOutcomeMetricKey("member_visit_booked"), primaryMetricLabel: "Visit bookings per visible membership card session",
    goals: funnelExperimentGoals[EXPERIMENT_KEYS.membershipInitialChoiceOptionsV1]?.goals || [], metrics: [{ key: "initial_membership_choice_shown", label: "Initial membership choice shown", kind: "exposure" }], outcomeMetrics: FUNNEL_OUTCOME_METRICS,
  },

  [EXPERIMENT_KEYS.funnelPreOnboardingLandingEducationV1]: {
    key: "funnel_pre_onboarding_landing_education_v1", revisionId: "funnel_pre_onboarding_landing_education_v1_r1", effectiveStartAt: "2026-11-02T18:30:00.000Z",
    referenceVariantKey: "control", fallbackVariantKey: "control", title: "Landing Education Cards",
    description: "Shows unviewed landing sections during new member pre-onboarding.", status: "active", kind: "ab_test", owner: "growth",
    tags: ["funnel", "pre_onboarding"], surfaces: ["pre_onboarding_entry"],
    hypothesis: "Seeing missed landing sections before signup may improve downstream bookings.",
    assignment: { ...CANONICAL_RANDOMIZED_ASSIGNMENT_POLICY, unit: "session", cadence: "static", seedSalt: "funnel_pre_onboarding_landing_education_v1_r1", revisionId: "funnel_pre_onboarding_landing_education_v1_r1", variants: [{ key: "control", weight: 1 }, { key: "education", weight: 1 }] },
    variants: [{ key: "control", label: "Current flow" }, { key: "education", label: "Landing education cards" }],
    exposureMetricKey: "landing_education_entered", primaryMetricKey: toOutcomeMetricKey("member_visit_booked"), primaryMetricLabel: "First visits booked per assigned pre-onboarding open",
    northStarMetricKey: "value_metrics_d30_contribution_profit_cents", goals: funnelExperimentGoals[EXPERIMENT_KEYS.funnelPreOnboardingLandingEducationV1]?.goals || [],
    metrics: [{ key: "landing_education_entered", label: "Pre-onboarding entered", kind: "exposure" }], outcomeMetrics: FUNNEL_OUTCOME_METRICS,
  },

  [EXPERIMENT_KEYS.landingCredibilityCountersV1]: {
    key: EXPERIMENT_KEYS.landingCredibilityCountersV1, revisionId: "funnel_landing_credibility_counters_v1_r1", effectiveStartAt: "2026-11-02T16:15:00.000Z",
    referenceVariantKey: "control", fallbackVariantKey: "control", surfaceSlot: "landing_credibility_counters",
    title: "Landing Credibility Counters", description: "Tests credibility section with or without odometer cards.", status: "active", kind: "ab_test", owner: "growth",
    tags: ["funnel", "landing", "credibility"], surfaces: ["join_landing"],
    hypothesis: "Odometer credibility cards may improve account creation and downstream bookings.",
    assignment: { ...CANONICAL_RANDOMIZED_ASSIGNMENT_POLICY, unit: "session", cadence: "static", seedSalt: "funnel_landing_credibility_counters_v1_r1", revisionId: "funnel_landing_credibility_counters_v1_r1", variants: [{ key: "control", weight: 1 }, { key: "odometer", weight: 1 }] },
    variants: [{ key: "control", label: "Credibility section without cards", payload: {} }, { key: "odometer", label: "Credibility section with odometer cards", payload: {} }],
    exposureMetricKey: "landing_section_visible", primaryMetricKey: "outcome_member_account_created", primaryMetricLabel: "Accounts created per assigned session", northStarMetricKey: "membership_revenue_per_landing_session",
    minimumSample: { assignedSessionsPerVariant: 400 }, goals: funnelExperimentGoals[EXPERIMENT_KEYS.landingCredibilityCountersV1]?.goals || [],
    metrics: [{ key: "landing_section_visible", label: "Credibility section visible", kind: "exposure" }], outcomeMetrics: FUNNEL_OUTCOME_METRICS,
  },
  [EXPERIMENT_KEYS.landingHelpersTitleV1]: {
    key: EXPERIMENT_KEYS.landingHelpersTitleV1, revisionId: "funnel_landing_helpers_title_v1_r1", effectiveStartAt: "2026-11-01T19:25:00.000Z",
    referenceVariantKey: "control", fallbackVariantKey: "control", surfaceSlot: "landing_helpers_title",
    title: "Landing Helpers Title", description: "Tests the helpers section title.", status: "active", kind: "ab_test", owner: "growth",
    tags: ["funnel", "landing", "copy", "experiments_v1"], surfaces: ["join_landing"],
    hypothesis: "A different section title may improve member signups.",
    assignment: { ...CANONICAL_RANDOMIZED_ASSIGNMENT_POLICY, unit: "session", cadence: "static", seedSalt: "funnel_landing_helpers_title_v1_r1", revisionId: "funnel_landing_helpers_title_v1_r1", variants: [{ key: "control", weight: 1 }, { key: "helpers_title", weight: 1 }] },
    variants: [{ key: "control", label: "Top Student Helpers", payload: { title: "Top Student Helpers" } }, { key: "helpers_title", label: "Meet My Helpers", payload: { title: "Meet My Helpers" } }],
    exposureMetricKey: "landing_section_visible", primaryMetricKey: "outcome_member_account_created", primaryMetricLabel: "Accounts created per assigned session", northStarMetricKey: "membership_revenue_per_landing_session",
    minimumSample: { assignedSessionsPerVariant: 400 }, goals: funnelExperimentGoals[EXPERIMENT_KEYS.landingHelpersTitleV1]?.goals || [],
    metrics: [{ key: "landing_section_visible", label: "Section visible", kind: "exposure" }], outcomeMetrics: FUNNEL_OUTCOME_METRICS,
  },
  [EXPERIMENT_KEYS.landingReviewsTitleV1]: {
    key: EXPERIMENT_KEYS.landingReviewsTitleV1, revisionId: "funnel_landing_reviews_title_v1_r1", effectiveStartAt: "2026-11-01T19:25:00.000Z",
    referenceVariantKey: "control", fallbackVariantKey: "control", surfaceSlot: "landing_reviews_title",
    title: "Landing Reviews Title", description: "Tests the reviews section title.", status: "active", kind: "ab_test", owner: "growth",
    tags: ["funnel", "landing", "copy", "experiments_v1"], surfaces: ["join_landing"],
    hypothesis: "A different section title may improve member signups.",
    assignment: { ...CANONICAL_RANDOMIZED_ASSIGNMENT_POLICY, unit: "session", cadence: "static", seedSalt: "funnel_landing_reviews_title_v1_r1", revisionId: "funnel_landing_reviews_title_v1_r1", variants: [{ key: "control", weight: 1 }, { key: "reviews_title", weight: 1 }] },
    variants: [{ key: "control", label: "Recent Reviews", payload: { title: "Recent Reviews" } }, { key: "reviews_title", label: "Excellent Senior Reviews", payload: { title: "Excellent Senior Reviews" } }],
    exposureMetricKey: "landing_section_visible", primaryMetricKey: "outcome_member_account_created", primaryMetricLabel: "Accounts created per assigned session", northStarMetricKey: "membership_revenue_per_landing_session",
    minimumSample: { assignedSessionsPerVariant: 400 }, goals: funnelExperimentGoals[EXPERIMENT_KEYS.landingReviewsTitleV1]?.goals || [],
    metrics: [{ key: "landing_section_visible", label: "Section visible", kind: "exposure" }], outcomeMetrics: FUNNEL_OUTCOME_METRICS,
  },
  [EXPERIMENT_KEYS.landingHelperSelectivityTitleV1]: {
    key: EXPERIMENT_KEYS.landingHelperSelectivityTitleV1, revisionId: "funnel_landing_helper_selectivity_title_v1_r1", effectiveStartAt: "2026-11-01T19:25:00.000Z",
    referenceVariantKey: "control", fallbackVariantKey: "control", surfaceSlot: "landing_helper_selectivity_title",
    title: "Landing Helper Selectivity Title", description: "Tests the helper selectivity section title.", status: "active", kind: "ab_test", owner: "growth",
    tags: ["funnel", "landing", "copy", "experiments_v1"], surfaces: ["join_landing"],
    hypothesis: "A different section title may improve member signups.",
    assignment: { ...CANONICAL_RANDOMIZED_ASSIGNMENT_POLICY, unit: "session", cadence: "static", seedSalt: "funnel_landing_helper_selectivity_title_v1_r1", revisionId: "funnel_landing_helper_selectivity_title_v1_r1", variants: [{ key: "control", weight: 1 }, { key: "helper_selectivity_title", weight: 1 }] },
    variants: [{ key: "control", label: "Becoming a Helper", payload: { title: "Becoming a Helper" } }, { key: "helper_selectivity_title", label: "Our Selective Process", payload: { title: "Our Selective Process" } }],
    exposureMetricKey: "landing_section_visible", primaryMetricKey: "outcome_member_account_created", primaryMetricLabel: "Accounts created per assigned session", northStarMetricKey: "membership_revenue_per_landing_session",
    minimumSample: { assignedSessionsPerVariant: 400 }, goals: funnelExperimentGoals[EXPERIMENT_KEYS.landingHelperSelectivityTitleV1]?.goals || [],
    metrics: [{ key: "landing_section_visible", label: "Section visible", kind: "exposure" }], outcomeMetrics: FUNNEL_OUTCOME_METRICS,
  },
  [EXPERIMENT_KEYS.landingFounderStoryTitleV1]: {
    key: EXPERIMENT_KEYS.landingFounderStoryTitleV1, revisionId: "funnel_landing_founder_story_title_v1_r1", effectiveStartAt: "2026-11-01T19:25:00.000Z",
    referenceVariantKey: "control", fallbackVariantKey: "control", surfaceSlot: "landing_founder_story_title",
    title: "Landing Founder Story Title", description: "Tests the founder story section title.", status: "active", kind: "ab_test", owner: "growth",
    tags: ["funnel", "landing", "copy", "experiments_v1"], surfaces: ["join_landing"],
    hypothesis: "A different section title may improve member signups.",
    assignment: { ...CANONICAL_RANDOMIZED_ASSIGNMENT_POLICY, unit: "session", cadence: "static", seedSalt: "funnel_landing_founder_story_title_v1_r1", revisionId: "funnel_landing_founder_story_title_v1_r1", variants: [{ key: "control", weight: 1 }, { key: "founder_story_title", weight: 1 }] },
    variants: [{ key: "control", label: "Started by Three Brothers", payload: { title: "Started by Three Brothers" } }, { key: "founder_story_title", label: "Inspired by Our Grandparents", payload: { title: "Inspired by Our Grandparents" } }],
    exposureMetricKey: "landing_section_visible", primaryMetricKey: "outcome_member_account_created", primaryMetricLabel: "Accounts created per assigned session", northStarMetricKey: "membership_revenue_per_landing_session",
    minimumSample: { assignedSessionsPerVariant: 400 }, goals: funnelExperimentGoals[EXPERIMENT_KEYS.landingFounderStoryTitleV1]?.goals || [],
    metrics: [{ key: "landing_section_visible", label: "Section visible", kind: "exposure" }], outcomeMetrics: FUNNEL_OUTCOME_METRICS,
  },

  [EXPERIMENT_KEYS.funnelLandingPuzzlesV1]: {
  "key": "funnel_landing_puzzles_v1",
  "revisionId": "funnel_landing_puzzles_v1_r1",
  "effectiveStartAt": "2026-10-30T07:00:00.000Z",
  "referenceVariantKey": "hide_puzzles",
  "fallbackVariantKey": "hide_puzzles",
  "surfaceSlot": "landing_puzzles",
  "title": "Landing Daily Puzzles",
  "status": "active",
  "kind": "ab_test",
  "owner": "growth",
  "surfaces": [
    "join_landing"
  ],
  "assignmentMetricKey": "assignment",
  "exposureMetricKey": "landing_puzzles_opportunity",
  "primaryMetricKey": "outcome_member_account_created",
  "primaryMetricLabel": "Accounts created per eligible landing session",
  "northStarMetricKey": "value_metrics_d30_contribution_profit_cents",
  "hypothesis": "Offering daily puzzles to visitors who reach the lower landing section may increase account creation and long-term value.",
  "assignment": {
    "eligibilityPolicyKey": "universal",
    "assignmentHashVersion": "uint53_avalanche_v1",
    "assignmentBucketPolicyVersion": "cadence_weighted_v1",
    "unit": "session",
    "cadence": "static",
    "seedSalt": "funnel_landing_puzzles_v1_r1",
    "revisionId": "funnel_landing_puzzles_v1_r1",
    "variants": [
      {
        "key": "hide_puzzles",
        "weight": 1
      },
      {
        "key": "show_puzzles",
        "weight": 1
      }
    ]
  },
  "variants": [
    {
      "key": "hide_puzzles",
      "label": "Hide puzzle section"
    },
    {
      "key": "show_puzzles",
      "label": "Show puzzle section"
    }
  ],
  "searchToken": "exp_track__funnel_landing_puzzles_v1",
  "description": "Offering daily puzzles to visitors who reach the lower landing section may increase account creation and long-term value.",
  "tags": [
    "puzzles",
    "landing"
  ],
  "guardrailMetricKeys": [
    "outcome_member_membership_refunded",
    "outcome_member_visit_refunded"
  ],
  "minimumSample": {
    "assignedSessionsPerVariant": 400
  },
  "stopRule": "Use mature D30 contribution profit per assigned unit for the decision; inspect D7 early and D60/D90/D365/lifetime as they mature. Check bookings, paid membership and refunds before promotion.",
  "metrics": [
    {
      "key": "landing_puzzles_opportunity",
      "label": "Puzzle opportunity reached",
      "kind": "exposure"
    }
  ],
  goals: funnelExperimentGoals[EXPERIMENT_KEYS.funnelLandingPuzzlesV1]?.goals || [],
  outcomeMetrics: FUNNEL_OUTCOME_METRICS,
},
  [EXPERIMENT_KEYS.memberDashboardPuzzlesV1]: {
  "key": "member_dashboard_puzzles_v1",
  "revisionId": "member_dashboard_puzzles_v1_r1",
  "effectiveStartAt": "2026-10-30T07:00:00.000Z",
  "referenceVariantKey": "hide_puzzles",
  "fallbackVariantKey": "hide_puzzles",
  "surfaceSlot": "member_dashboard_puzzles",
  "title": "Member Dashboard Puzzles",
  "status": "active",
  "kind": "ab_test",
  "owner": "growth",
  "surfaces": [
    "member_dashboard_home"
  ],
  "assignmentMetricKey": "assignment",
  "exposureMetricKey": "dashboard_puzzles_opportunity",
  "primaryMetricKey": "outcome_member_visit_booked",
  "primaryMetricLabel": "Members booking visits per assigned member",
  "northStarMetricKey": "value_metrics_d30_contribution_profit_cents",
  "hypothesis": "Offering daily puzzles in the dashboard may increase ongoing member value and bookings.",
  "assignment": {
    "eligibilityPolicyKey": "authenticated_member",
    "assignmentHashVersion": "uint53_avalanche_v1",
    "assignmentBucketPolicyVersion": "cadence_weighted_v1",
    "unit": "member",
    "cadence": "static",
    "seedSalt": "member_dashboard_puzzles_v1_r1",
    "revisionId": "member_dashboard_puzzles_v1_r1",
    "variants": [
      {
        "key": "hide_puzzles",
        "weight": 1
      },
      {
        "key": "show_puzzles",
        "weight": 1
      }
    ]
  },
  "variants": [
    {
      "key": "hide_puzzles",
      "label": "Hide puzzle entries"
    },
    {
      "key": "show_puzzles",
      "label": "Show puzzle entries"
    }
  ],
  "searchToken": "exp_track__member_dashboard_puzzles_v1",
  "description": "Offering daily puzzles in the dashboard may increase ongoing member value and bookings.",
  "tags": [
    "puzzles",
    "member_dashboard"
  ],
  "guardrailMetricKeys": [
    "outcome_member_membership_refunded",
    "outcome_member_visit_refunded"
  ],
  "minimumSample": {
    "assignedMembersPerVariant": 200
  },
  "stopRule": "Use mature D30 contribution profit per assigned unit for the decision; inspect D7 early and D60/D90/D365/lifetime as they mature. Check bookings, paid membership and refunds before promotion.",
  "metrics": [
    {
      "key": "dashboard_puzzles_opportunity",
      "label": "Puzzle opportunity reached",
      "kind": "exposure"
    }
  ],
  goals: funnelExperimentGoals[EXPERIMENT_KEYS.memberDashboardPuzzlesV1]?.goals || [],
  outcomeMetrics: FUNNEL_OUTCOME_METRICS,
},
  [visitAcceptanceExperiment.key]: { ...visitAcceptanceExperiment, goals: funnelExperimentGoals[visitAcceptanceExperiment.key]?.goals || [], outcomeMetrics: FUNNEL_OUTCOME_METRICS },
  [EXPERIMENT_KEYS.funnelLandingPwycChartCenterV1]: {
    key: EXPERIMENT_KEYS.funnelLandingPwycChartCenterV1,
    revisionId: "funnel_landing_pwyc_chart_center_v1_r1",
    effectiveStartAt: "2026-10-12T07:00:00.000Z",
    referenceVariantKey: "current_center",
    fallbackVariantKey: "current_center",
    surfaceSlot: "landing_pwyc_pricing_chart_center",
    searchToken: `${SEARCH_PREFIX}${EXPERIMENT_KEYS.funnelLandingPwycChartCenterV1}`,
    title: "Landing PWYC Pricing Chart Center",
    description: "Tests a chart-only example rate raised toward the local range midpoint.",
    status: "active",
    kind: "ab_test",
    owner: "growth",
    tags: ["funnel", "landing", "pricing"],
    surfaces: ["join_landing"],
    hypothesis: "A less low-anchored example rate may improve pricing-section CTA use without reducing account creation or booking.",
    assignment: {
      ...CANONICAL_RANDOMIZED_ASSIGNMENT_POLICY,
      unit: "session",
      cadence: "static",
      seedSalt: "funnel_landing_pwyc_chart_center_v1_r1",
      revisionId: "funnel_landing_pwyc_chart_center_v1_r1",
      variants: [
        { key: "current_center", weight: 1 },
        { key: "raised_center", weight: 1 },
      ],
    },
    variants: [
      { key: "current_center", label: "Current calculated chart center", payload: {} },
      { key: "raised_center", label: "Raise example rate 60% toward midpoint", payload: {} },
    ],
    exposureMetricKey: "landing_pricing_section_viewed",
    primaryMetricKey: "landing_cta_clicked",
    primaryMetricLabel: "Pricing CTA clicks per assigned session",
    northStarMetricKey: "membership_revenue_per_landing_session",
    guardrailMetricKeys: [toOutcomeMetricKey("member_membership_refunded")],
    minimumSample: { assignedSessionsPerVariant: 400 },
    stopRule: "Compare pricing CTA clicks, account creation, paid membership, booking, and refunds per assigned session after sufficient sample and mature outcomes.",
    goals: funnelExperimentGoals[EXPERIMENT_KEYS.funnelLandingPwycChartCenterV1]?.goals || [],
    metrics: [
      { key: "landing_pricing_section_viewed", label: "Pricing section viewed", kind: "exposure" },
      { key: "landing_cta_clicked", label: "Pricing CTA clicked", kind: "action" },
    ],
    outcomeMetrics: FUNNEL_OUTCOME_METRICS,
  },
  [EXPERIMENT_KEYS.funnelLandingHeroSelectivityV1]: {
    key: EXPERIMENT_KEYS.funnelLandingHeroSelectivityV1,
    revisionId: "funnel_landing_hero_selectivity_v1_r1",
    effectiveStartAt: "2026-10-31T07:00:00.000Z",
    referenceVariantKey: "control",
    fallbackVariantKey: "control",
    surfaceSlot: "landing_hero_selectivity",
    searchToken: `${SEARCH_PREFIX}${EXPERIMENT_KEYS.funnelLandingHeroSelectivityV1}`,
    title: "Landing Hero Helper Selectivity",
    description: "Compares the current hero proof with the helper selectivity treatment.",
    status: "active",
    kind: "ab_test",
    owner: "growth",
    tags: ["funnel", "landing", "hero", "trust"],
    surfaces: ["join_landing"],
    hypothesis: "Showing how few applicants become helpers may increase member conversion and bookings.",
    assignment: {
      ...CANONICAL_RANDOMIZED_ASSIGNMENT_POLICY,
      unit: "session",
      cadence: "static",
      seedSalt: "funnel_landing_hero_selectivity_v1_r1",
      revisionId: "funnel_landing_hero_selectivity_v1_r1",
      variants: [{ key: "control", weight: 1 }, { key: "selectivity", weight: 1 }],
    },
    variants: [
      { key: "control", label: "Current hero", payload: {} },
      { key: "selectivity", label: "Helper selectivity hero", payload: {} },
    ],
    exposureMetricKey: "landing_hero_viewed",
    primaryMetricKey: toOutcomeMetricKey("member_account_created"),
    primaryMetricLabel: "Accounts created per assigned session",
    northStarMetricKey: "membership_revenue_per_landing_session",
    guardrailMetricKeys: [toOutcomeMetricKey("member_membership_refunded"), toOutcomeMetricKey("member_visit_refunded")],
    minimumSample: { assignedSessionsPerVariant: 400 },
    stopRule: "Compare accounts, first bookings, paid membership, revenue and contribution profit per assigned session; monitor refunds and allow financial outcomes to mature.",
    goals: funnelExperimentGoals[EXPERIMENT_KEYS.funnelLandingHeroSelectivityV1]?.goals || [],
    metrics: [
      { key: "landing_hero_viewed", label: "Hero viewed", kind: "exposure" },
      { key: "landing_cta_clicked", label: "Hero CTA clicked", kind: "action" },
    ],
    outcomeMetrics: FUNNEL_OUTCOME_METRICS,
  },
  [EXPERIMENT_KEYS.funnelLandingTopSectionOrderV1]: {
    key: EXPERIMENT_KEYS.funnelLandingTopSectionOrderV1,
    revisionId: "funnel_landing_top_section_order_v1_r1",
    effectiveStartAt: "2026-10-31T07:00:00.000Z",
    referenceVariantKey: "control",
    fallbackVariantKey: "control",
    surfaceSlot: "landing_top_section_order",
    searchToken: `${SEARCH_PREFIX}${EXPERIMENT_KEYS.funnelLandingTopSectionOrderV1}`,
    title: "Landing Top Section Order",
    description: "Compares Helpers then Reviews with Reviews then Helpers.",
    status: "active",
    kind: "ab_test",
    owner: "growth",
    tags: ["funnel", "landing", "hero", "trust"],
    surfaces: ["join_landing"],
    hypothesis: "The order of Helpers and Reviews may affect member conversion and bookings.",
    assignment: {
      ...CANONICAL_RANDOMIZED_ASSIGNMENT_POLICY,
      unit: "session",
      cadence: "static",
      seedSalt: "funnel_landing_top_section_order_v1_r1",
      revisionId: "funnel_landing_top_section_order_v1_r1",
      variants: [{ key: "control", weight: 1 }, { key: "reviews_first", weight: 1 }],
    },
    variants: [
      { key: "control", label: "Helpers then Reviews", payload: {} },
      { key: "reviews_first", label: "Reviews then Helpers", payload: {} },
    ],
    exposureMetricKey: "landing_hero_viewed",
    primaryMetricKey: toOutcomeMetricKey("member_account_created"),
    primaryMetricLabel: "Accounts created per assigned session",
    northStarMetricKey: "membership_revenue_per_landing_session",
    guardrailMetricKeys: [toOutcomeMetricKey("member_membership_refunded"), toOutcomeMetricKey("member_visit_refunded")],
    minimumSample: { assignedSessionsPerVariant: 400 },
    stopRule: "Compare accounts, first bookings, paid membership, revenue and contribution profit per assigned session; monitor refunds and allow financial outcomes to mature.",
    goals: funnelExperimentGoals[EXPERIMENT_KEYS.funnelLandingTopSectionOrderV1]?.goals || [],
    metrics: [
      { key: "landing_hero_viewed", label: "Hero viewed", kind: "exposure" },
      { key: "landing_cta_clicked", label: "Hero CTA clicked", kind: "action" },
    ],
    outcomeMetrics: FUNNEL_OUTCOME_METRICS,
  },
  [EXPERIMENT_KEYS.funnelLandingReviewLayoutV1]: {
    key: EXPERIMENT_KEYS.funnelLandingReviewLayoutV1,
    revisionId: "funnel_landing_review_layout_v1_r1",
    effectiveStartAt: "2026-11-01T14:00:00.000Z",
    referenceVariantKey: "control",
    fallbackVariantKey: "control",
    surfaceSlot: "landing_review_layout",
    searchToken: `${SEARCH_PREFIX}${EXPERIMENT_KEYS.funnelLandingReviewLayoutV1}`,
    title: "Landing Review Layout",
    description: "Compares the current review grid with a single-row review carousel.",
    status: "active",
    kind: "ab_test",
    owner: "growth",
    tags: ["funnel", "landing", "hero", "trust"],
    surfaces: ["join_landing"],
    hypothesis: "A bounded review carousel may improve landing-page engagement and conversion.",
    assignment: {
      ...CANONICAL_RANDOMIZED_ASSIGNMENT_POLICY,
      unit: "session",
      cadence: "static",
      seedSalt: "funnel_landing_review_layout_v1_r1",
      revisionId: "funnel_landing_review_layout_v1_r1",
      variants: [{ key: "control", weight: 1 }, { key: "carousel", weight: 1 }],
    },
    variants: [
      { key: "control", label: "Current expanding grid", payload: {} },
      { key: "carousel", label: "Single-row carousel", payload: {} },
    ],
    exposureMetricKey: "landing_hero_viewed",
    primaryMetricKey: toOutcomeMetricKey("member_account_created"),
    primaryMetricLabel: "Accounts created per assigned session",
    northStarMetricKey: "membership_revenue_per_landing_session",
    guardrailMetricKeys: [toOutcomeMetricKey("member_membership_refunded"), toOutcomeMetricKey("member_visit_refunded")],
    minimumSample: { assignedSessionsPerVariant: 400 },
    stopRule: "Compare accounts, first bookings, paid membership, revenue and contribution profit per assigned session; monitor refunds and allow financial outcomes to mature.",
    goals: funnelExperimentGoals[EXPERIMENT_KEYS.funnelLandingReviewLayoutV1]?.goals || [],
    metrics: [
      { key: "landing_hero_viewed", label: "Hero viewed", kind: "exposure" },
      { key: "landing_cta_clicked", label: "Hero CTA clicked", kind: "action" },
    ],
    outcomeMetrics: FUNNEL_OUTCOME_METRICS,
  },
  [EXPERIMENT_KEYS.funnelLandingHeroReviewQuoteV1]: {
    key: EXPERIMENT_KEYS.funnelLandingHeroReviewQuoteV1,
    revisionId: "funnel_landing_hero_review_quote_v1_r1",
    effectiveStartAt: "2026-10-08T20:00:00.000Z",
    referenceVariantKey: "hide_quote",
    fallbackVariantKey: "hide_quote",
    surfaceSlot: "landing_hero_review_quote",
    searchToken: `${SEARCH_PREFIX}${EXPERIMENT_KEYS.funnelLandingHeroReviewQuoteV1}`,
    title: "Landing Hero Local Review Quote",
    description: "Tests a local member quote beneath hero trust proof only when a qualifying helper review is available.",
    status: "active",
    kind: "ab_test",
    owner: "growth",
    tags: ["funnel", "landing", "hero", "trust"],
    surfaces: ["join_landing"],
    hypothesis: "A concise, attributable local review may increase account creation among visitors who can see local helper proof.",
    assignment: {
      ...CANONICAL_RANDOMIZED_ASSIGNMENT_POLICY,
      unit: "session",
      cadence: "static",
      seedSalt: "funnel_landing_hero_review_quote_v1_r1",
      revisionId: "funnel_landing_hero_review_quote_v1_r1",
      variants: [
        { key: "hide_quote", weight: 1 },
        { key: "show_quote", weight: 1 },
      ],
    },
    variants: [
      { key: "hide_quote", label: "No review quote", payload: {} },
      { key: "show_quote", label: "Show local review quote", payload: {} },
    ],
    exposureMetricKey: "landing_hero_viewed",
    primaryMetricKey: toOutcomeMetricKey("member_account_created"),
    primaryMetricLabel: "Accounts created per assigned session",
    northStarMetricKey: "membership_revenue_per_landing_session",
    guardrailMetricKeys: [toOutcomeMetricKey("member_membership_refunded")],
    minimumSample: { assignedSessionsPerVariant: 400 },
    stopRule: "Compare account creation, paid membership, and booking per assigned session after sufficient sample and mature outcomes; monitor refunds.",
    goals: funnelExperimentGoals[EXPERIMENT_KEYS.funnelLandingHeroReviewQuoteV1]?.goals || [],
    metrics: [
      { key: "landing_hero_viewed", label: "Eligible hero viewed", kind: "exposure" },
      { key: "landing_cta_clicked", label: "Hero CTA clicked", kind: "action" },
    ],
    outcomeMetrics: FUNNEL_OUTCOME_METRICS,
  },
  [EXPERIMENT_KEYS.funnelLandingHeroVideoV1]: {
    key: EXPERIMENT_KEYS.funnelLandingHeroVideoV1,
    revisionId: "funnel_landing_hero_video_v1_r1",
    effectiveStartAt: "2026-10-28T07:00:00.000Z",
    referenceVariantKey: "founder_video",
    fallbackVariantKey: "founder_video",
    surfaceSlot: "landing_hero_video",
    searchToken: `${SEARCH_PREFIX}${EXPERIMENT_KEYS.funnelLandingHeroVideoV1}`,
    title: "Landing Hero Video",
    description: "Compares the founder welcome video with the senior testimonial video in the same hero media slot on mobile and desktop.",
    status: "active",
    kind: "ab_test",
    owner: "growth",
    tags: ["funnel", "landing", "hero", "video"],
    surfaces: ["join_landing"],
    hypothesis: "Senior testimonials in the hero can increase account creation by showing member experiences at the first decision point.",
    assignment: {
      ...CANONICAL_RANDOMIZED_ASSIGNMENT_POLICY,
      unit: "session",
      cadence: "static",
      seedSalt: "funnel_landing_hero_video_v1_r1",
      revisionId: "funnel_landing_hero_video_v1_r1",
      variants: [
        { key: "founder_video", weight: 1 },
        { key: "senior_testimonials", weight: 1 },
      ],
    },
    variants: [
      { key: "founder_video", label: "Founder video", payload: { landingHeroMedia: { enabled: true, mediaVariantKey: "founder_video" } } },
      { key: "senior_testimonials", label: "Senior testimonials", payload: { landingHeroMedia: { enabled: true, mediaVariantKey: "senior_testimonials" } } },
    ],
    exposureMetricKey: "landing_hero_viewed",
    primaryMetricKey: toOutcomeMetricKey("member_account_created"),
    primaryMetricLabel: "Accounts created per assigned session",
    northStarMetricKey: "membership_revenue_per_landing_session",
    guardrailMetricKeys: [
      ...LANDING_HERO_GUARDRAIL_METRIC_KEYS,
      toOutcomeMetricKey("member_visit_refunded"),
    ],
    minimumSample: { assignedSessionsPerVariant: 400 },
    stopRule: "Compare accounts, paid memberships, and bookings per assigned session after sufficient sample and mature outcomes; monitor refunds.",
    goals: funnelExperimentGoals[EXPERIMENT_KEYS.funnelLandingHeroVideoV1]?.goals || [],
    metrics: [
      { key: "landing_hero_viewed", label: "Hero viewed", kind: "exposure" },
      { key: "landing_video_interaction", label: "Hero video interaction", kind: "action" },
      { key: "landing_cta_clicked", label: "Hero CTA clicked", kind: "action" },
    ],
    outcomeMetrics: FUNNEL_OUTCOME_METRICS,
  },
  [EXPERIMENT_KEYS.funnelLandingHeroTaglineV1]: {
    key: EXPERIMENT_KEYS.funnelLandingHeroTaglineV1,
    revisionId: "funnel_landing_hero_tagline_v1_r1",
    effectiveStartAt: "2026-10-31T07:00:00.000Z",
    referenceVariantKey: "control",
    fallbackVariantKey: "control",
    surfaceSlot: "landing_hero_tagline",
    searchToken: `${SEARCH_PREFIX}${EXPERIMENT_KEYS.funnelLandingHeroTaglineV1}`,
    title: "Landing Hero Tagline",
    description: "Compares the existing hero tagline with no tagline.",
    status: "active",
    kind: "ab_test",
    owner: "growth",
    tags: ["funnel", "landing", "hero", "copy"],
    surfaces: ["join_landing"],
    hypothesis: "Removing the hero tagline may improve account creation.",
    assignment: {
      ...CANONICAL_RANDOMIZED_ASSIGNMENT_POLICY,
      unit: "session",
      cadence: "static",
      seedSalt: "funnel_landing_hero_tagline_v1_r1",
      revisionId: "funnel_landing_hero_tagline_v1_r1",
      variants: [
        { key: "control", weight: 1 },
        { key: "no_tagline", weight: 1 },
      ],
    },
    variants: [
      { key: "control", label: "Existing tagline", payload: {} },
      { key: "no_tagline", label: "No tagline", payload: {} },
    ],
    exposureMetricKey: "landing_hero_viewed",
    primaryMetricKey: toOutcomeMetricKey("member_account_created"),
    primaryMetricLabel: "Accounts created per assigned session",
    northStarMetricKey: "membership_revenue_per_landing_session",
    guardrailMetricKeys: [
      ...LANDING_HERO_GUARDRAIL_METRIC_KEYS,
      toOutcomeMetricKey("member_visit_refunded"),
    ],
    minimumSample: { assignedSessionsPerVariant: 400 },
    stopRule: "Compare accounts, paid memberships, and bookings per assigned session after sufficient sample and mature outcomes; monitor refunds.",
    goals: funnelExperimentGoals[EXPERIMENT_KEYS.funnelLandingHeroTaglineV1]?.goals || [],
    metrics: [
      { key: "landing_hero_viewed", label: "Hero viewed", kind: "exposure" },
      { key: "landing_cta_clicked", label: "Hero CTA clicked", kind: "action" },
    ],
    outcomeMetrics: FUNNEL_OUTCOME_METRICS,
  },
  [EXPERIMENT_KEYS.funnelLandingHeroHeadlineV1]: {
    key: EXPERIMENT_KEYS.funnelLandingHeroHeadlineV1,
    revisionId: "funnel_landing_hero_headline_v1_r4",
    effectiveStartAt: EXPERIMENT_PORTFOLIO_REALLOCATION_AT,
    referenceVariantKey: "control",
    fallbackVariantKey: "control",
    surfaceSlot: "landing_hero_headline",
    priorIncidentGap: LANDING_EXPERIMENT_INCIDENT_GAP,
    searchToken: `${SEARCH_PREFIX}${EXPERIMENT_KEYS.funnelLandingHeroHeadlineV1}`,
    title: "Landing Hero Headline",
    description:
      "Tests which default Landing hero H1 improves engagement and member funnel starts.",
    status: "active",
    kind: "ab_test",
    owner: "growth",
    tags: ["funnel", "landing", "hero", "copy", "experiments_v1"],
    surfaces: ["join_landing"],
    hypothesis:
      "Changing the default Landing hero H1 may reduce no-click/no-engagement sessions and improve qualified CTA starts.",
    assignment: {
      ...CANONICAL_RANDOMIZED_ASSIGNMENT_POLICY,
      unit: "session",
      cadence: "static",
      seedSalt: "funnel_landing_hero_headline_v1_r4",
      revisionId: "funnel_landing_hero_headline_v1_r4",
      variants: [
        { key: "control", weight: 40 },
        { key: "age_in_place_with_confidence", weight: 15 },
        { key: "help_around_the_home_you_love", weight: 15 },
        { key: "affordable_help_for_seniors", weight: 15 },
        { key: "delightful_help_around_the_house", weight: 15 },
      ],
    },
    variants: [
      {
        key: "control",
        label: "Household Help for Seniors",
        description: "Current default Landing hero H1.",
        payload: {
          landingHeroHeadline: {
            enabled: true,
            contentVariantKey: "hero_h1_household_help_for_seniors",
            headline: "Household Help for Seniors",
          },
        },
      },
      {
        key: "age_in_place_with_confidence",
        label: "Age in Place with Confidence",
        description:
          "Landing hero H1 candidate focused on confident aging at home.",
        payload: {
          landingHeroHeadline: {
            enabled: true,
            contentVariantKey: "hero_h1_age_in_place_with_confidence",
            headline: "Age in Place with Confidence",
          },
        },
      },
      {
        key: "help_around_the_home_you_love",
        label: "Help Around the Home You Love",
        description:
          "Landing hero H1 candidate focused on practical help in a familiar home.",
        payload: {
          landingHeroHeadline: {
            enabled: true,
            contentVariantKey: "hero_h1_help_around_the_home_you_love",
            headline: "Help Around the Home You Love",
          },
        },
      },
      {
        key: "affordable_help_for_seniors",
        label: "Affordable Help for Seniors",
        description:
          "Default Landing hero H1 candidate focused on affordability.",
        payload: {
          landingHeroHeadline: {
            enabled: true,
            contentVariantKey: "hero_h1_affordable_help_for_seniors",
            headline: "Affordable Help for Seniors",
          },
        },
      },
      {
        key: "delightful_help_around_the_house",
        label: "Delightful Help Around the House",
        description:
          "New Landing hero H1 candidate pairing a warm service-experience promise with concrete household help.",
        payload: {
          landingHeroHeadline: {
            enabled: true,
            contentVariantKey: "hero_h1_delightful_help_around_the_house",
            headline: "Delightful Help Around the House",
          },
        },
      },
    ],
    exposureMetricKey: "landing_hero_viewed",
    primaryMetricKey: LANDING_HERO_ENGAGEMENT_PRIMARY_METRIC_KEY,
    northStarMetricKey: "membership_revenue_per_landing_session",
    guardrailMetricKeys: LANDING_HERO_GUARDRAIL_METRIC_KEYS,
    minimumSample: {
      sessionsPerVariant: 250,
      qualifiedEngagementsPerVariant: 50,
      ctaClicksPerVariant: 30,
    },
    stopRule:
      "Review after minimum sample or 14 complete rollup days; promote only if qualified active engagement, CTA/open movement, and downstream outcomes are positive without hurting account creation, paid conversion, revenue, or refunds.",
    goals: LANDING_HERO_ENGAGEMENT_GOALS,
    metrics: LANDING_HERO_EXPERIMENT_METRICS,
    outcomeMetrics: FUNNEL_OUTCOME_METRICS,
  },

  [EXPERIMENT_KEYS.funnelLandingAudienceLanguageV1]: {
    key: EXPERIMENT_KEYS.funnelLandingAudienceLanguageV1,
    revisionId: "funnel_landing_audience_language_v1_r1",
    effectiveStartAt: LANDING_AUDIENCE_LANGUAGE_START_AT,
    referenceVariantKey: "control",
    fallbackVariantKey: "control",
    surfaceSlot: "landing_audience_language",
    searchToken: `${SEARCH_PREFIX}${EXPERIMENT_KEYS.funnelLandingAudienceLanguageV1}`,
    title: "Landing Audience Language",
    description:
      "Tests whether referring to customers as seniors instead of members improves account creation.",
    status: "active",
    kind: "ab_test",
    owner: "growth",
    tags: ["funnel", "landing", "pre_onboarding", "copy", "experiments_v1"],
    surfaces: ["join_landing", "pre_onboarding_flow"],
    hypothesis:
      "Removing member language from customer-facing Landing and pre-onboarding copy may reduce perceived commitment and increase account creation.",
    allocationReason:
      "Equal session-level allocation provides a direct concurrent comparison between current member language and seniors language.",
    assignment: {
      ...CANONICAL_RANDOMIZED_ASSIGNMENT_POLICY,
      unit: "session",
      cadence: "static",
      seedSalt: "funnel_landing_audience_language_v1_r1",
      revisionId: "funnel_landing_audience_language_v1_r1",
      variants: [
        { key: "control", weight: 1 },
        { key: "seniors", weight: 1 },
      ],
    },
    variants: [
      {
        key: "control",
        label: "Current member language",
        description:
          "Preserves the current public-facing member and senior-member wording.",
        payload: {
          landingAudienceLanguage: {
            enabled: true,
            contentVariantKey: "control",
            variantKey: "control",
          },
        },
      },
      {
        key: "seniors",
        label: "Seniors language",
        description:
          "Uses senior or seniors for the customer role while preserving true membership-product terms.",
        payload: {
          landingAudienceLanguage: {
            enabled: true,
            contentVariantKey: "seniors",
            variantKey: "seniors",
          },
        },
      },
    ],
    exposureMetricKey: "landing_audience_language_viewed",
    primaryMetricKey: toOutcomeMetricKey("member_account_created"),
    primaryMetricLabel: "Accounts created per Landing session",
    northStarMetricKey: "membership_revenue_per_landing_session",
    guardrailMetricKeys: [
      "landing_pre_onboarding_opened",
      toOutcomeMetricKey("member_onboarding_completed"),
      toOutcomeMetricKey("member_visit_booked"),
      toOutcomeMetricKey("member_membership_paid"),
      toOutcomeMetricKey("member_membership_refunded"),
      toOutcomeMetricKey("member_visit_refunded"),
    ],
    minimumSample: {
      sessionsPerVariant: 350,
      accountCreationsPerVariant: 50,
    },
    stopRule:
      "Review after minimum sample or 14 complete rollup days; promote only if account creation improves without weakening onboarding, booking, membership, revenue, or refund guardrails.",
    goals:
      funnelExperimentGoals[EXPERIMENT_KEYS.funnelLandingAudienceLanguageV1]
        ?.goals || [],
    metrics: LANDING_AUDIENCE_LANGUAGE_METRICS,
    outcomeMetrics: FUNNEL_OUTCOME_METRICS,
  },

  [EXPERIMENT_KEYS.funnelSitewideBbbBrandingV1]: {
    key: EXPERIMENT_KEYS.funnelSitewideBbbBrandingV1,
    revisionId: "funnel_sitewide_bbb_branding_v1_r2",
    effectiveStartAt: EXPERIMENT_DENOMINATOR_RESTART_AT,
    referenceVariantKey: "control",
    fallbackVariantKey: "control",
    surfaceSlot: "sitewide_bbb_branding",
    searchToken: `${SEARCH_PREFIX}${EXPERIMENT_KEYS.funnelSitewideBbbBrandingV1}`,
    title: "Sitewide BBB Branding",
    description:
      "Tests whether removing BBB accreditation badges and copy from customer-facing product surfaces improves conversion.",
    status: "active",
    kind: "ab_test",
    owner: "growth",
    tags: ["funnel", "sitewide", "branding", "trust", "experiments_v1"],
    surfaces: [
      "sitewide_branding",
      "public_site",
      "pre_onboarding_flow",
      "member_onboarding",
      "membership_checkout",
      "schedule_visit_flow",
      "member_dashboard",
    ],
    hypothesis:
      "Removing BBB branding may make Linked Lives feel more modern and focused while preserving the stronger product-specific trust signals, improving account creation and first conversion.",
    allocationReason:
      "Equal session-level allocation provides a direct intent-to-treat comparison between the unchanged sitewide brand and the same experience without BBB branding.",
    assignment: {
      ...CANONICAL_RANDOMIZED_ASSIGNMENT_POLICY,
      unit: "session",
      cadence: "static",
      seedSalt: "funnel_sitewide_bbb_branding_v1_r2",
      revisionId: "funnel_sitewide_bbb_branding_v1_r2",
      variants: [
        { key: "control", weight: 1 },
        { key: "without_bbb", weight: 1 },
      ],
    },
    variants: [
      {
        key: "control",
        label: "Current BBB branding",
        description:
          "Preserves every existing customer-facing BBB badge and accreditation reference.",
      },
      {
        key: "without_bbb",
        label: "No BBB branding",
        description:
          "Removes customer-facing BBB badges and accreditation references while preserving adjacent proof and refund copy.",
      },
    ],
    exposureMetricKey: "bbb_branding_surface_viewed",
    primaryMetricKey: toOutcomeMetricKey("member_account_created"),
    primaryMetricLabel: "Accounts created per assigned session",
    northStarMetricKey: "value_metrics_d30_contribution_profit_cents",
    guardrailMetricKeys: [
      toOutcomeMetricKey("member_onboarding_completed"),
      toOutcomeMetricKey("member_visit_booked"),
      toOutcomeMetricKey("member_membership_paid"),
      toOutcomeMetricKey("member_membership_refunded"),
      toOutcomeMetricKey("member_visit_refunded"),
    ],
    minimumSample: {
      sessionsPerVariant: 500,
      accountCreationsPerVariant: 50,
      firstConversionsPerVariant: 25,
    },
    stopRule:
      "Review after each arm reaches the minimum sample and completed D30 evidence is available; promote only if account creation and first conversion improve without weakening paid conversion, refunds, revenue, or contribution profit.",
    goals:
      funnelExperimentGoals[EXPERIMENT_KEYS.funnelSitewideBbbBrandingV1]
        ?.goals || [],
    metrics: [
      {
        key: "bbb_branding_surface_viewed",
        label: "BBB branding surface viewed",
        kind: "exposure",
      },
    ],
    outcomeMetrics: FUNNEL_OUTCOME_METRICS,
  },

  [EXPERIMENT_KEYS.funnelLandingHeroCtaCopyV1]: {
    key: EXPERIMENT_KEYS.funnelLandingHeroCtaCopyV1,
    revisionId: "funnel_landing_hero_cta_copy_v1_r6",
    effectiveStartAt: LANDING_HERO_CTA_COPY_FOUR_ARM_AT,
    referenceVariantKey: "check_availability",
    fallbackVariantKey: "check_availability",
    surfaceSlot: "landing_hero_cta",
    priorIncidentGap: LANDING_EXPERIMENT_INCIDENT_GAP,
    searchToken: `${SEARCH_PREFIX}${EXPERIMENT_KEYS.funnelLandingHeroCtaCopyV1}`,
    title: "Landing Hero CTA Copy",
    description:
      "Tests four founder-selected Landing hero CTA labels while preserving prior arms for historical readouts.",
    status: "active",
    continuePreviousRevisionUntilStart: true,
    kind: "ab_test",
    owner: "growth",
    tags: ["funnel", "landing", "hero", "cta", "copy", "experiments_v1"],
    surfaces: ["join_landing"],
    hypothesis:
      "More concrete hero CTA language may increase hero CTA clicks and member funnel starts.",
    assignment: {
      ...CANONICAL_RANDOMIZED_ASSIGNMENT_POLICY,
      unit: "session",
      cadence: "static",
      seedSalt: "funnel_landing_hero_cta_copy_v1_r6",
      revisionId: "funnel_landing_hero_cta_copy_v1_r6",
      variants: [
        { key: "check_availability", weight: 1 },
        { key: "start_my_to_do_list", weight: 1 },
        { key: "meet_my_helper", weight: 1 },
        { key: "plan_my_first_visit", weight: 1 },
      ],
    },
    variants: [
      {
        key: "check_availability",
        label: "Check Availability",
        description:
          "Frames the next step as checking whether Linked Lives can help nearby.",
        payload: {
          landingHeroCtaCopy: {
            enabled: true,
            labelKey: "check_availability",
            label: "Check Availability",
            iconKey: "availability",
          },
        },
      },
      {
        key: "meet_my_helper",
        label: "Meet My Helper",
        description:
          "Frames the next step as a personal introduction to a potential helper.",
        payload: {
          landingHeroCtaCopy: {
            enabled: true,
            labelKey: "meet_my_helper",
            label: "Meet My Helper",
            iconKey: "waving_hand",
          },
        },
      },
      {
        key: "start_my_to_do_list",
        label: "Start My To-Do List",
        description:
          "Frames the next step around the visitor's own household to-do list.",
        payload: {
          landingHeroCtaCopy: {
            enabled: true,
            labelKey: "start_my_to_do_list",
            label: "Start My To-Do List",
            iconKey: "task_list",
          },
        },
      },
      {
        key: "plan_my_first_visit",
        label: "Plan My First Visit",
        description:
          "Frames the next step around planning a concrete first visit.",
        payload: {
          landingHeroCtaCopy: {
            enabled: true,
            labelKey: "plan_my_first_visit",
            label: "Plan My First Visit",
            iconKey: "availability",
          },
        },
      },
    ],
    exposureMetricKey: "landing_hero_viewed",
    primaryMetricKey: LANDING_HERO_CTA_COPY_PRIMARY_METRIC_KEY,
    northStarMetricKey: "membership_revenue_per_landing_session",
    guardrailMetricKeys: LANDING_HERO_GUARDRAIL_METRIC_KEYS,
    minimumSample: {
      sessionsPerVariant: 250,
      qualifiedEngagementsPerVariant: 50,
      ctaClicksPerVariant: 30,
    },
    stopRule:
      "Review after minimum sample or 14 complete rollup days; promote only if hero CTA clicks improve, with qualified active engagement, pre-onboarding opens, and downstream outcomes stable or better without hurting account creation, paid conversion, revenue, or refunds.",
    goals: LANDING_HERO_CTA_COPY_GOALS,
    metrics: LANDING_HERO_EXPERIMENT_METRICS,
    outcomeMetrics: FUNNEL_OUTCOME_METRICS,
  },

  [EXPERIMENT_KEYS.funnelLandingHeroTrustSignalV1]: {
    key: EXPERIMENT_KEYS.funnelLandingHeroTrustSignalV1,
    revisionId: "funnel_landing_hero_trust_signal_v1_r3",
    effectiveStartAt: VISIT_BOOKING_REALLOCATION_AT,
    referenceVariantKey: "control",
    fallbackVariantKey: "control",
    surfaceSlot: "landing_hero_trust",
    priorIncidentGap: LANDING_EXPERIMENT_INCIDENT_GAP,
    searchToken: `${SEARCH_PREFIX}${EXPERIMENT_KEYS.funnelLandingHeroTrustSignalV1}`,
    title: "Landing Hero Trust Signal",
    description:
      "Tests the compact trust line under the Landing hero review count.",
    status: "retired",
    kind: "ab_test",
    owner: "growth",
    tags: ["funnel", "landing", "hero", "copy", "trust", "experiments_v1"],
    surfaces: ["join_landing"],
    hypothesis:
      "A more human, work-ethic-oriented hero trust line may make helpers feel both capable and likable, increasing qualified engagement without weakening downstream conversion quality.",
    assignment: null,
    variants: [
      {
        key: "control",
        label: "Local • Dependable • Friendly",
        description: "Current hero social-proof trust line.",
        payload: {
          landingHeroTrustSignal: {
            enabled: true,
            trustSignalVariantKey: "vetted_dependable_friendly",
            label: "Local • Dependable • Friendly",
          },
        },
      },
      {
        key: "hard_working_personable",
        label: "Local • Hard-working • Personable",
        description:
          "Trust line focused on local help, work ethic, and interpersonal fit.",
        payload: {
          landingHeroTrustSignal: {
            enabled: true,
            trustSignalVariantKey: "vetted_hard_working_personable",
            label: "Local • Hard-working • Personable",
          },
        },
      },
    ],
    exposureMetricKey: "landing_hero_viewed",
    primaryMetricKey: LANDING_HERO_ENGAGEMENT_PRIMARY_METRIC_KEY,
    northStarMetricKey: "membership_revenue_per_landing_session",
    guardrailMetricKeys: LANDING_HERO_GUARDRAIL_METRIC_KEYS,
    minimumSample: {
      sessionsPerVariant: 250,
      qualifiedEngagementsPerVariant: 50,
      ctaClicksPerVariant: 30,
    },
    stopRule:
      "Review after minimum sample or 14 complete rollup days; promote only if qualified active engagement and hero CTA/open movement improve without hurting account creation, paid conversion, revenue, support burden, or refund guardrails.",
    goals: LANDING_HERO_TRUST_SIGNAL_GOALS,
    metrics: LANDING_HERO_EXPERIMENT_METRICS,
    outcomeMetrics: FUNNEL_OUTCOME_METRICS,
  },

  [EXPERIMENT_KEYS.funnelLandingDesktopHeroMediaV1]: {
    key: EXPERIMENT_KEYS.funnelLandingDesktopHeroMediaV1,
    revisionId: "funnel_landing_desktop_hero_media_v1_r1",
    searchToken: `${SEARCH_PREFIX}${EXPERIMENT_KEYS.funnelLandingDesktopHeroMediaV1}`,
    title: "Desktop Landing Hero Media",
    description:
      "Tests the desktop Landing hero founder welcome video against the Stack real-photo hero without changing mobile layout or founder-story placement.",
    status: "retired",
    kind: "ab_test",
    owner: "growth",
    tags: ["funnel", "landing", "hero", "media", "desktop", "experiments_v1"],
    surfaces: ["join_landing"],
    hypothesis:
      "On desktop, a real-visit photo stack may improve hero CTA starts compared with the founder welcome video while downstream account creation, paid conversion, revenue, and refunds remain stable.",
    assignment: {
      ...CANONICAL_RANDOMIZED_ASSIGNMENT_POLICY,
      unit: "session",
      cadence: "static",
      seedSalt: "funnel_landing_desktop_hero_media_v1",
      variants: [
        { key: "founder_video", weight: 50 },
        { key: "stack", weight: 50 },
      ],
    },
    variants: [
      {
        key: "founder_video",
        label: "Founder video hero",
        description:
          "Desktop Landing hero with the founder welcome video card in the media column.",
        payload: {
          landingHeroMedia: {
            enabled: true,
            mediaVariantKey: "founder_video",
            founderPlacementKey: "default",
            desktopHeroMediaVariantKey: "founder_video",
          },
        },
      },
      {
        key: "stack",
        label: "Stack photo hero",
        description:
          "Desktop Landing hero with the Stack real-photo media in the media column.",
        payload: {
          landingHeroMedia: {
            enabled: true,
            mediaVariantKey: "stack",
            founderPlacementKey: "above_pricing",
            desktopHeroMediaVariantKey: "stack",
          },
        },
      },
    ],
    exposureMetricKey: "landing_hero_viewed",
    primaryMetricKey: "landing_pre_onboarding_opened",
    northStarMetricKey: "membership_revenue_per_landing_session",
    guardrailMetricKeys: LANDING_HERO_GUARDRAIL_METRIC_KEYS,
    minimumSample: {
      sessionsPerVariant: 250,
      preOnboardingOpensPerVariant: 40,
      ctaClicksPerVariant: 30,
    },
    stopRule:
      "Retired on 2026-08-01 after founder decision; founder_video was promoted as the default desktop Landing hero media treatment.",
    goals: [],
    metrics: LANDING_HERO_EXPERIMENT_METRICS,
    outcomeMetrics: FUNNEL_OUTCOME_METRICS,
  },

  [EXPERIMENT_KEYS.funnelLandingMobileHeroMediaLayoutV1]: {
    key: EXPERIMENT_KEYS.funnelLandingMobileHeroMediaLayoutV1,
    revisionId: "funnel_landing_mobile_hero_media_layout_v1_r3",
    effectiveStartAt: VISIT_BOOKING_REALLOCATION_AT,
    referenceVariantKey: "founder_media_first",
    fallbackVariantKey: "founder_media_first",
    surfaceSlot: "landing_hero_mobile_media",
    priorIncidentGap: LANDING_EXPERIMENT_INCIDENT_GAP,
    searchToken: `${SEARCH_PREFIX}${EXPERIMENT_KEYS.funnelLandingMobileHeroMediaLayoutV1}`,
    title: "Mobile Landing Hero Media",
    effectiveEndAt: EXPERIMENT_PORTFOLIO_REALLOCATION_AT,
    description:
      "Retired after founder welcome media before copy was promoted as the mobile Landing default.",
    status: "retired",
    kind: "ab_test",
    owner: "growth",
    tags: ["funnel", "landing", "hero", "media", "mobile", "experiments_v1"],
    surfaces: ["join_landing"],
    hypothesis:
      "On mobile, a helper photo stack may outperform founder welcome media when both are shown before the copy.",
    assignment: {
      ...CANONICAL_RANDOMIZED_ASSIGNMENT_POLICY,
      unit: "session",
      cadence: "static",
      seedSalt: "funnel_landing_mobile_hero_media_layout_v1_r3",
      revisionId: "funnel_landing_mobile_hero_media_layout_v1_r3",
      variants: [
        { key: "founder_media_first", weight: 1 },
        { key: "stack_media_first", weight: 1 },
      ],
    },
    variants: [
      {
        key: "founder_media_first",
        label: "Founder media before copy",
        description:
          "The standard founder welcome media slot appears before mobile hero copy.",
        payload: {
          landingHeroMedia: {
            enabled: true,
            mediaVariantKey: "founder_video",
            founderPlacementKey: "default",
            heroMediaFrameKey: "standard",
            mobileHeroMediaLayoutVariantKey: "founder_media_first",
            mobileHeroMediaOrderKey: "media_first",
          },
        },
      },
      {
        key: "stack_media_first",
        label: "Stack media before copy",
        description:
          "The standard Stack real-photo media slot appears before mobile hero copy.",
        payload: {
          landingHeroMedia: {
            enabled: true,
            mediaVariantKey: "stack",
            founderPlacementKey: "above_pricing",
            heroMediaFrameKey: "standard",
            mobileHeroMediaLayoutVariantKey: "stack_media_first",
            mobileHeroMediaOrderKey: "media_first",
          },
        },
      },
    ],
    exposureMetricKey: "landing_hero_viewed",
    primaryMetricKey: "landing_pre_onboarding_opened",
    northStarMetricKey: "membership_revenue_per_landing_session",
    guardrailMetricKeys: LANDING_HERO_GUARDRAIL_METRIC_KEYS,
    minimumSample: {
      sessionsPerVariant: 250,
      preOnboardingOpensPerVariant: 35,
      ctaClicksPerVariant: 25,
    },
    stopRule:
      "Retired at the September 16 midnight PT cutover; founder_media_first was promoted as the product default.",
    goals: [],
    metrics: LANDING_HERO_EXPERIMENT_METRICS,
    outcomeMetrics: FUNNEL_OUTCOME_METRICS,
  },

  [EXPERIMENT_KEYS.funnelLandingSectionOrderV1]: {
    key: EXPERIMENT_KEYS.funnelLandingSectionOrderV1,
    revisionId: "funnel_landing_section_order_v1_r1",
    effectiveStartAt: "2026-06-28T07:00:00.000Z",
    effectiveEndAt: LANDING_EXPERIMENT_PAUSE_AT,
    searchToken: `${SEARCH_PREFIX}${EXPERIMENT_KEYS.funnelLandingSectionOrderV1}`,
    title: "Landing Section Order",
    description:
      "Tests deterministic first-three post-hero section orders while the hero stays fixed.",
    status: "retired",
    kind: "ab_test",
    owner: "growth",
    tags: ["funnel", "landing", "section_order", "experiments_v1"],
    surfaces: ["join_landing"],
    hypothesis:
      "A curated first-three post-hero order can improve CTA-click and pre-onboarding starts after visible tested-slot exposure, with meaningful views used as an attention-quality layer.",
    assignment: null,
    variants: [
      {
        key: "control",
        label: "Legacy section order",
        description:
          "Historical baseline row for section-order readouts before candidate-pool randomization became the only assigned variant.",
        payload: {
          landingSectionOrder: {
            enabled: false,
            variantKey: "control",
          },
        },
      },
      {
        key: "slot_candidate_randomization",
        label: "Candidate-pool randomization",
        description:
          "Randomizes slot one, slot two, and slot three from curated candidate pools without repeating a section.",
        payload: {
          landingSectionOrder: {
            enabled: true,
            rankerVersion: LANDING_SECTION_ORDER_RANKER_VERSION,
            variantKey: "slot_candidate_randomization",
            slotCandidatePools: LANDING_SECTION_ORDER_SLOT_CANDIDATE_POOLS,
            excludedTopSlotSectionKeys:
              LANDING_SECTION_ORDER_EXCLUDED_TOP_SLOT_KEYS,
            afterTopSlotSectionKeys: LANDING_SECTION_ORDER_AFTER_TOP_SLOT_KEYS,
            slotBackgrounds: LANDING_SECTION_ORDER_SLOT_BACKGROUNDS,
            adaptivePolicy: LANDING_SECTION_ORDER_ADAPTIVE_POLICY,
          },
        },
      },
    ],
    exposureMetricKey: "landing_section_order_assigned",
    primaryMetricKey: LANDING_SECTION_ORDER_PRIMARY_METRIC_KEY,
    northStarMetricKey: "landing_section_order_pre_onboarding_opened",
    guardrailMetricKeys: LANDING_HERO_GUARDRAIL_METRIC_KEYS,
    minimumSample: {
      sessionsPerVariant: 500,
      visibleTestedSlotViewsPerVariant: 300,
      meaningfulTestedSlotViewsPerVariant: 150,
      ctaClicksPerVariant: 50,
    },
    stopRule:
      "Retired on 2026-08-21 because its single assigned top-level arm was not a randomized experiment. Preserve historical ranker evidence for diagnostics only.",
    goals: LANDING_SECTION_ORDER_GOALS,
    metrics: [
      ...LANDING_HERO_EXPERIMENT_METRICS,
      {
        key: "landing_section_order_slot_visible",
        label: "Visible tested-slot exposure",
        kind: "view",
      },
      {
        key: LANDING_SECTION_ORDER_PRIMARY_METRIC_KEY,
        label: "CTA-click session after visible tested-slot exposure",
        kind: "conversion",
      },
      {
        key: "landing_section_order_pre_onboarding_opened",
        label: "Get Started opened after visible tested-slot exposure",
        kind: "conversion",
      },
      {
        key: "landing_section_order_slot_progressed",
        label: "Progressed to next meaningful section",
        kind: "view",
      },
    ],
    outcomeMetrics: FUNNEL_OUTCOME_METRICS,
  },

  ...Object.fromEntries(
    LANDING_SECTION_SLOT_EXPERIMENTS.map((slotConfig) => [
      slotConfig.experimentKey,
      landingSectionSlotExperimentDefinition(slotConfig),
    ]),
  ),

  [EXPERIMENT_KEYS.funnelLandingHelperSelectivityPresenceV1]: {
    key: EXPERIMENT_KEYS.funnelLandingHelperSelectivityPresenceV1,
    revisionId: "funnel_landing_helper_selectivity_presence_v1_r1",
    searchToken: `${SEARCH_PREFIX}${EXPERIMENT_KEYS.funnelLandingHelperSelectivityPresenceV1}`,
    title: "Rigorously Screened Presence",
    description:
      "Tests whether allowing the Rigorously Screened section into the Landing experience improves pre-onboarding starts and downstream signup outcomes.",
    status: "retired",
    kind: "ab_test",
    owner: "growth",
    tags: [
      "funnel",
      "landing",
      "helper_selectivity",
      "section_presence",
      "experiments_v1",
    ],
    surfaces: ["join_landing"],
    hypothesis:
      "Showing the Rigorously Screened section may increase signup intent by making helper screening more concrete, while downstream account, paid-conversion, revenue, and refund guardrails remain stable.",
    assignment: null,
    variants: [
      {
        key: "control_no_helper_selectivity",
        label: "Hide Rigorously Screened section",
        description:
          "Removes the helper-selectivity section from fallback Landing order and section-order slot selection.",
        payload: {
          helperSelectivityPresence: {
            enabled: true,
            showSection: false,
            contentVariantKey: "helper_selectivity_hidden_control_v1",
            hiddenSectionKeys: [HELPER_SELECTIVITY_SECTION_KEY],
          },
        },
      },
      {
        key: "show_helper_selectivity",
        label: "Show Rigorously Screened section",
        description:
          "Allows the helper-selectivity section to render in the normal Landing flow and participate in Slot 3 rotation.",
        payload: {
          helperSelectivityPresence: {
            enabled: true,
            showSection: true,
            contentVariantKey: "helper_selectivity_visible_v1",
            hiddenSectionKeys: [],
          },
        },
      },
    ],
    exposureMetricKey: HELPER_SELECTIVITY_PRESENCE_ASSIGNED_METRIC_KEY,
    primaryMetricKey: HELPER_SELECTIVITY_PRESENCE_PRIMARY_METRIC_KEY,
    northStarMetricKey: "membership_revenue_per_landing_session",
    guardrailMetricKeys: HELPER_SELECTIVITY_PRESENCE_GUARDRAIL_METRIC_KEYS,
    minimumSample: {
      assignedSessionsPerVariant: 500,
      preOnboardingOpensPerVariant: 50,
    },
    stopRule:
      "Promoted on 2026-08-12; Rigorously Screened now renders as the default Landing experience without an experiment assignment harness.",
    goals: HELPER_SELECTIVITY_PRESENCE_GOALS,
    metrics: HELPER_SELECTIVITY_PRESENCE_METRICS,
    outcomeMetrics: FUNNEL_OUTCOME_METRICS,
  },

  [EXPERIMENT_KEYS.funnelHelperDiscountPresentationV1]: {
    key: EXPERIMENT_KEYS.funnelHelperDiscountPresentationV1,
    revisionId: "funnel_helper_discount_presentation_v1_r3",
    effectiveStartAt: VISIT_BOOKING_REALLOCATION_AT,
    effectiveEndAt: "2026-09-25T07:00:00.000Z",
    referenceVariantKey: "control",
    fallbackVariantKey: "control",
    surfaceSlot: "landing_helper_discount_presentation",
    priorIncidentGap: LANDING_EXPERIMENT_INCIDENT_GAP,
    searchToken: `${SEARCH_PREFIX}${EXPERIMENT_KEYS.funnelHelperDiscountPresentationV1}`,
    title: "Helper Discount Presentation",
    description:
      "Tests the existing embedded helper-discount surfaces against a persistent landing sticky bar for helper-discount eligible sessions.",
    status: "retired",
    kind: "ab_test",
    owner: "growth",
    tags: [
      "funnel",
      "landing",
      "pre_onboarding",
      "helper_discount",
      "sticky_bar",
      "experiments_v1",
    ],
    surfaces: ["join_landing", "pre_onboarding_flow"],
    hypothesis:
      "A persistent helper-discount sticky bar may increase Check Availability starts among eligible helper-discount sessions by keeping the offer visible while removing duplicate embedded discount prompts.",
    assignment: {
      ...CANONICAL_RANDOMIZED_ASSIGNMENT_POLICY,
      unit: "session",
      cadence: "static",
      seedSalt: "funnel_helper_discount_presentation_v1_r3",
      revisionId: "funnel_helper_discount_presentation_v1_r3",
      variants: [
        { key: "control", weight: 1 },
        { key: "sticky_bar", weight: 1 },
      ],
    },
    variants: [
      {
        key: "control",
        label: "Embedded helper-discount surfaces",
        description:
          "Current helper-discount presentation: Landing hero discount offer and pre-onboarding helper-discount banner remain eligible to render.",
        payload: {
          helperDiscountPresentation: {
            enabled: true,
            presentationVariantKey: "control",
            contentVariantKey: "helper_discount_embedded_control_v1",
            suppressLandingHeroOffer: false,
            suppressPreOnboardingBanner: false,
            stickyBar: {
              enabled: false,
            },
          },
        },
      },
      {
        key: "sticky_bar",
        label: "Persistent helper-discount sticky bar",
        description:
          "Removes the Landing hero helper-discount offer and pre-onboarding banner, then registers a persistent top sticky bar with the discount and Check Availability CTA.",
        payload: {
          helperDiscountPresentation: {
            enabled: true,
            presentationVariantKey: "sticky_bar",
            contentVariantKey: "helper_discount_sticky_bar_v1",
            suppressLandingHeroOffer: true,
            suppressPreOnboardingBanner: true,
            stickyBar: {
              enabled: true,
              id: "landing-helper-discount",
              ctaLabel: "Check Availability",
              contentVariantKey: "helper_discount_sticky_bar_v1",
              delayMs: 0,
              overlayPolicy: "persistent-overlays",
              showActionOnDesktop: true,
              showActionOnMobile: false,
              allowWrapDesktop: false,
              allowWrapMobile: true,
            },
          },
        },
      },
    ],
    exposureMetricKey: HELPER_DISCOUNT_PRESENTATION_ELIGIBLE_METRIC_KEY,
    primaryMetricKey: HELPER_DISCOUNT_PRESENTATION_PRIMARY_METRIC_KEY,
    northStarMetricKey: "membership_revenue_per_landing_session",
    guardrailMetricKeys: HELPER_DISCOUNT_PRESENTATION_GUARDRAIL_METRIC_KEYS,
    minimumSample: {
      eligibleViewsPerVariant: 250,
      preOnboardingOpensPerVariant: 30,
    },
    stopRule:
      "Review after minimum sample or 14 complete rollup days; promote the sticky bar only if eligible-session pre-onboarding opens improve with account creation, paid conversion, revenue, refunds, and sticky dismissal guardrails stable or better.",
    goals: HELPER_DISCOUNT_PRESENTATION_GOALS,
    metrics: HELPER_DISCOUNT_PRESENTATION_METRICS,
    outcomeMetrics: FUNNEL_OUTCOME_METRICS,
  },

  [EXPERIMENT_KEYS.funnelLandingCredibilityMiddleBandsV1]: {
    key: EXPERIMENT_KEYS.funnelLandingCredibilityMiddleBandsV1,
    revisionId: "funnel_landing_credibility_middle_bands_v1_r1",
    effectiveStartAt: "2026-10-03T07:00:00.000Z",
    referenceVariantKey: "no_bar",
    fallbackVariantKey: "no_bar",
    surfaceSlot: "landing_sticky_bar",
    supersedesExperimentKeys: ["funnel_landing_sticky_bar_credibility_v1"],
    searchToken: `${SEARCH_PREFIX}${EXPERIMENT_KEYS.funnelLandingCredibilityMiddleBandsV1}`,
    title: "Landing Credibility — 10–99 Members",
    description: "Tests show versus hide only for 10–99 nearby members. Every conversion uses assigned sessions; bands are frozen at assignment.",
    status: "active",
    kind: "ab_test",
    owner: "growth",
    tags: ["funnel", "landing", "sticky_bar", "credibility"],
    surfaces: ["join_landing"],
    hypothesis: "For landing sessions first eligible with 10–99 nearby members, showing local proof increases accounts and paid membership without weakening bookings.",
    assignment: {
      ...CANONICAL_RANDOMIZED_ASSIGNMENT_POLICY,
      unit: "session",
      cadence: "static",
      seedSalt: "funnel_landing_credibility_middle_bands_v1_r1",
      revisionId: "funnel_landing_credibility_middle_bands_v1_r1",
      variants: [{ key: "no_bar", weight: 1 }, { key: "show_bar", weight: 1 }],
    },
    variants: [
      { key: "no_bar", label: "No sticky bar", payload: {} },
      { key: "show_bar", label: "Show local credibility bar", payload: {} },
    ],
    exposureMetricKey: "landing_credibility_sticky_bar_eligible_viewed",
    primaryMetricKey: toOutcomeMetricKey("member_account_created"),
    primaryMetricLabel: "Accounts created per assigned session",
    northStarMetricKey: "membership_revenue_per_landing_session",
    guardrailMetricKeys: ["landing_credibility_sticky_bar_dismissed", toOutcomeMetricKey("member_membership_refunded")],
    minimumSample: { assignedSessionsPerVariant: 400 },
    stopRule: "Compare accounts, paid members, and bookings per assigned session overall and in the three predeclared bands after sufficient sample and mature outcomes.",
    goals: markPrimaryGoal(
      funnelExperimentGoals[EXPERIMENT_KEYS.funnelLandingCredibilityMiddleBandsV1]?.goals || [],
      "accounts_per_assigned_session",
    ),
    metrics: [
      { key: "landing_credibility_sticky_bar_eligible_viewed", label: "Eligible sticky bar session", kind: "exposure" },
      { key: "landing_credibility_sticky_bar_viewed", label: "Credibility bar viewed", kind: "view" },
      { key: "landing_credibility_sticky_bar_clicked", label: "Credibility bar clicked", kind: "action" },
      { key: "landing_credibility_sticky_bar_dismissed", label: "Credibility bar dismissed", kind: "action" },
    ],
    outcomeMetrics: FUNNEL_OUTCOME_METRICS,
  },

  [EXPERIMENT_KEYS.funnelBookingCheckoutLocalCredibilityV1]: {
    key: EXPERIMENT_KEYS.funnelBookingCheckoutLocalCredibilityV1,
    revisionId: "funnel_booking_checkout_local_credibility_v1_r2",
    effectiveStartAt: "2026-10-14T21:15:00.000Z",
    continuePreviousRevisionUntilStart: true,
    referenceVariantKey: "no_alert",
    fallbackVariantKey: "no_alert",
    surfaceSlot: "booking_checkout_local_credibility",
    searchToken: `${SEARCH_PREFIX}${EXPERIMENT_KEYS.funnelBookingCheckoutLocalCredibilityV1}`,
    title: "First Booking Checkout Local Credibility",
    description:
      "Tests local member social proof beside payment entry when at least 25 nearby members are known in eligible first-visit booking sessions.",
    status: "active",
    kind: "ab_test",
    owner: "growth",
    tags: ["funnel", "checkout", "first_booking", "credibility"],
    surfaces: ["schedule_visit_flow"],
    hypothesis:
      "Local member social proof at payment entry increases first-visit bookings after the membership decision.",
    assignment: {
      ...CANONICAL_RANDOMIZED_ASSIGNMENT_POLICY,
      unit: "session",
      cadence: "static",
      seedSalt: "funnel_booking_checkout_local_credibility_v1_r2",
      revisionId: "funnel_booking_checkout_local_credibility_v1_r2",
      variants: [
        { key: "no_alert", weight: 1 },
        { key: "show_alert", weight: 1 },
      ],
    },
    variants: [
      { key: "no_alert", label: "Current payment entry", payload: {} },
      { key: "show_alert", label: "Local credibility alert", payload: {} },
    ],
    exposureMetricKey: "checkout_local_credibility_eligible_viewed",
    primaryMetricKey: toOutcomeMetricKey("member_visit_booked"),
    primaryMetricLabel: "First visits booked per assigned eligible checkout session",
    northStarMetricKey: "membership_revenue_per_assigned_session",
    guardrailMetricKeys: [
      toOutcomeMetricKey("member_membership_refunded"),
      toOutcomeMetricKey("member_visit_refunded"),
    ],
    minimumSample: { assignedSessionsPerVariant: 200, visitBookingsPerVariant: 30 },
    stopRule:
      "Compare first-visit booking conversion after enough assigned eligible checkout sessions; review payment errors, membership contributions, revenue, refunds, and concurrent experiments before promotion.",
    goals: (funnelExperimentGoals[EXPERIMENT_KEYS.funnelBookingCheckoutLocalCredibilityV1]?.goals || [])
      .filter(goal => !["1_to_9", "10_to_24"].includes(goal.denominatorProperties?.nearbyMemberCountBand))
      .map(goal => ({
        ...goal,
        label: goal.label.replace("eligible checkout session", "assigned eligible checkout session"),
        denominatorProperties: { ...goal.denominatorProperties, metricKey: "assignment" },
      })),
    metrics: [
      {
        key: "checkout_local_credibility_eligible_viewed",
        label: "Eligible checkout session",
        kind: "exposure",
      },
      {
        key: "checkout_local_credibility_alert_viewed",
        label: "Local credibility alert viewed",
        kind: "view",
      },
    ],
    outcomeMetrics: FUNNEL_OUTCOME_METRICS,
  },

  [EXPERIMENT_KEYS.funnelSameDayFirstVisitFreeHourV1]: {
    key: EXPERIMENT_KEYS.funnelSameDayFirstVisitFreeHourV1,
    revisionId: "funnel_same_day_first_visit_free_hour_v1_r1",
    searchToken: `${SEARCH_PREFIX}${EXPERIMENT_KEYS.funnelSameDayFirstVisitFreeHourV1}`,
    title: "Same-Day First Visit Free Hour",
    description:
      "Disabled guardrail record for the old Contact-step same-day first-visit free-hour offer.",
    futureOptimizationNotes: [
      "This offer runs alongside the new-member welcome discount for now; use applied/redeemed/purchase/refund outcomes to decide whether a later variant should remove the immediate discount, delay it by a week, or suppress overlapping welcome-discount engagement.",
    ],
    status: "retired",
    kind: "ab_test",
    owner: "growth",
    tags: ["funnel", "pre_onboarding", "same_day_booking", "experiments_v1"],
    surfaces: ["pre_onboarding_flow"],
    assignment: {
      ...CANONICAL_RANDOMIZED_ASSIGNMENT_POLICY,
      unit: "session",
      cadence: "static",
      seedSalt: "funnel_same_day_first_visit_free_hour_v1",
      variants: [
        { key: "control", weight: 100 },
        { key: "same_day_free_hour", weight: 0 },
      ],
    },
    variants: [
      {
        key: "control",
        label: "No same-day free-hour banner",
        description:
          "Current Contact step without the same-day first-visit free-hour offer.",
        payload: {},
      },
      {
        key: "same_day_free_hour",
        label: "Same-day first-visit offer disabled",
        description:
          "Kept disabled until any same-day booking incentive is converted to a price-aware dollar model.",
        payload: {
          sameDayFirstVisitOffer: {
            enabled: false,
            promoKey: "same_day_first_visit_free_hour_v1",
            discountHours: 1,
            offerCostCents: 2000,
            excludedTimingKeys: ["within_a_week"],
            bannerTitle:
              "Book your first visit today, and enjoy 1-hour for free!",
            bannerBody:
              "Create your account now to unlock this same-day booking offer.",
            contentVariantKey: "same_day_free_hour_contact_banner",
          },
        },
      },
    ],
    exposureMetricKey: "surface_viewed",
    primaryMetricKey: "pre_onboarding_submit_succeeded",
    goals:
      funnelExperimentGoals[EXPERIMENT_KEYS.funnelSameDayFirstVisitFreeHourV1]
        ?.goals || [],
    metrics: [
      { key: "surface_viewed", label: "Surface viewed", kind: "exposure" },
      {
        key: "pre_onboarding_step_viewed",
        label: "Step viewed",
        kind: "view",
      },
      {
        key: "pre_onboarding_submit_succeeded",
        label: "Account submitted",
        kind: "conversion",
      },
      {
        key: "member_same_day_offer_applied",
        label: "Same-day offer applied",
        kind: "conversion",
      },
      {
        key: "member_same_day_offer_redeemed",
        label: "Same-day offer redeemed",
        kind: "conversion",
      },
    ],
    outcomeMetrics: [
      ...FUNNEL_OUTCOME_METRICS,
      {
        key: toOutcomeMetricKey("member_same_day_offer_applied"),
        label: "Same-day offer applied",
        kind: "outcome",
        sourceEvent: "member_same_day_offer_applied",
      },
      {
        key: toOutcomeMetricKey("member_same_day_offer_redeemed"),
        label: "Same-day offer redeemed",
        kind: "outcome",
        sourceEvent: "member_same_day_offer_redeemed",
      },
    ],
  },

  [EXPERIMENT_KEYS.funnelPreOnboardingContactConsolidationV1]: {
    key: EXPERIMENT_KEYS.funnelPreOnboardingContactConsolidationV1,
    revisionId: "funnel_pre_onboarding_contact_consolidation_v1_r1",
    searchToken: `${SEARCH_PREFIX}${EXPERIMENT_KEYS.funnelPreOnboardingContactConsolidationV1}`,
    title: "Pre-Onboarding Contact Consolidation",
    description:
      "Tests combined contact capture against the historical split email/contact flow and a fully split name/email/phone flow.",
    status: "retired",
    kind: "ab_test",
    owner: "growth",
    tags: [
      "funnel",
      "pre_onboarding",
      "account_creation",
      "contact",
      "experiments_v1",
      "retired",
    ],
    surfaces: ["pre_onboarding_flow"],
    hypothesis:
      "Historical contact-ending test retired in favor of the modular pre-onboarding contact ending v2 experiment with fresh result attribution.",
    assignment: {
      ...CANONICAL_RANDOMIZED_ASSIGNMENT_POLICY,
      unit: "session",
      cadence: "static",
      seedSalt: "funnel_pre_onboarding_contact_consolidation_v1",
      variants: [
        { key: "combined_account_contact", weight: 34 },
        { key: "control", weight: 33 },
        { key: "fully_split_account_contact", weight: 33 },
      ],
    },
    variants: [
      {
        key: "combined_account_contact",
        label: "Combined email and contact card",
        description:
          "Replaces the standalone email and contact cards with one account/contact card.",
        payload: {
          hiddenStepIds: ["EMAIL", "CONTACT"],
          preOnboardingStepInsertions: [
            { stepId: "ACCOUNT_CONTACT", afterStepId: "QUESTIONS" },
          ],
          cardTreatments: {
            ACCOUNT_CONTACT: {
              cardVariantKey: "combined_email_contact_account_card",
              contentVariantKey: "account_contact_combined_v1",
              layoutVariantKey: "single_account_contact_card",
            },
          },
        },
      },
      {
        key: "control",
        label: "Separate email and contact cards",
        description:
          "Historical control flow with the standalone email check followed by the contact/account card.",
        payload: {},
      },
      {
        key: "fully_split_account_contact",
        label: "Fully split name, email, and phone cards",
        description:
          "Splits account contact capture into name, existing email, and phone-submit cards.",
        payload: {
          hiddenStepIds: ["CONTACT"],
          preOnboardingStepInsertions: [
            { stepId: "NAME", beforeStepId: "EMAIL" },
            { stepId: "PHONE", afterStepId: "EMAIL" },
          ],
          cardTreatments: {
            NAME: {
              cardVariantKey: "split_name_card",
              contentVariantKey: "name_card_v1",
              layoutVariantKey: "single_name_card",
            },
            PHONE: {
              cardVariantKey: "split_phone_submit_card",
              contentVariantKey: "phone_submit_card_v1",
              layoutVariantKey: "single_phone_submit_card",
            },
          },
        },
      },
    ],
    exposureMetricKey: "surface_viewed",
    primaryMetricKey: toOutcomeMetricKey("member_account_created"),
    northStarMetricKey: "membership_revenue_per_pre_onboarding_open",
    guardrailMetricKeys: [
      "pre_onboarding_email_checked",
      "pre_onboarding_existing_account_handoff",
      "pre_onboarding_validation_failed",
      "pre_onboarding_submit_failed",
      "pre_onboarding_submit_succeeded",
      toOutcomeMetricKey("member_membership_paid"),
      toOutcomeMetricKey("member_membership_refunded"),
      toOutcomeMetricKey("member_visit_refunded"),
    ],
    minimumSample: {
      accountContactEntriesPerVariant: 150,
      accountsCreatedPerVariant: 30,
    },
    stopRule:
      "Review after minimum sample or 14 complete rollup days; promote only if backend account creations per account/contact card entry improve, with existing-account handoff, phone duplicate, validation, paid conversion, refund, and support-demand guardrails stable or better.",
    goals:
      funnelExperimentGoals[
        EXPERIMENT_KEYS.funnelPreOnboardingContactConsolidationV1
      ]?.goals || [],
    metrics: [
      { key: "surface_viewed", label: "Surface viewed", kind: "exposure" },
      {
        key: "pre_onboarding_opened",
        label: "Pre-onboarding opened",
        kind: "view",
      },
      {
        key: "pre_onboarding_step_viewed",
        label: "Step viewed",
        kind: "view",
      },
      {
        key: "pre_onboarding_step_advanced",
        label: "Step advanced",
        kind: "action",
      },
      {
        key: "pre_onboarding_email_checked",
        label: "Email checked",
        kind: "action",
      },
      {
        key: "pre_onboarding_existing_account_handoff",
        label: "Existing account handoff",
        kind: "action",
      },
      {
        key: "pre_onboarding_validation_failed",
        label: "Validation failed",
        kind: "error",
      },
      {
        key: "pre_onboarding_submit_started",
        label: "Account submit started",
        kind: "action",
      },
      {
        key: "pre_onboarding_submit_failed",
        label: "Account submit failed",
        kind: "error",
      },
      {
        key: "pre_onboarding_submit_succeeded",
        label: "Account submitted",
        kind: "conversion",
      },
    ],
    outcomeMetrics: FUNNEL_OUTCOME_METRICS,
  },

  [EXPERIMENT_KEYS.funnelPreOnboardingFlowVersionV1]: {
    key: EXPERIMENT_KEYS.funnelPreOnboardingFlowVersionV1,
    continuePreviousRevisionUntilStart: true,
    revisionId: "funnel_pre_onboarding_flow_version_v1_r2",
    effectiveStartAt: PRE_ONBOARDING_FLOW_V3_SPLIT_START_AT,
    referenceVariantKey: "pre_onboarding_v1",
    fallbackVariantKey: "pre_onboarding_v1",
    allocationReason:
      "Equal session-level allocation compares the complete V1, V2, and V3 pre-onboarding experiences.",
    searchToken: `${SEARCH_PREFIX}${EXPERIMENT_KEYS.funnelPreOnboardingFlowVersionV1}`,
    title: "Pre-Onboarding Flow Version",
    description:
      "Compares the V1 account-creation flow, V2 booking-first flow, and V3 contact-first flow.",
    status: "active",
    kind: "ab_test",
    owner: "growth",
    tags: ["funnel", "pre_onboarding", "booking", "experiments_v2"],
    surfaces: [
      "pre_onboarding_entry",
      "pre_onboarding_flow",
      "pre_onboarding_v2",
      "pre_onboarding_v3",
    ],
    hypothesis:
      "A lower-friction entry can improve account creation and first-visit conversion without weakening contribution profit or guardrails.",
    assignment: {
      ...CANONICAL_RANDOMIZED_ASSIGNMENT_POLICY,
      unit: "session",
      cadence: "static",
      seedSalt: "funnel_pre_onboarding_flow_version_v1_r2",
      revisionId: "funnel_pre_onboarding_flow_version_v1_r2",
      variants: [
        { key: "pre_onboarding_v1", weight: 1 },
        { key: "pre_onboarding_v2", weight: 1 },
        { key: "pre_onboarding_v3", weight: 1 },
      ],
    },
    variants: [
      {
        key: "pre_onboarding_v1",
        label: "Pre-onboarding V1",
        description:
          "Keeps the current pre-onboarding account-creation experience.",
      },
      {
        key: "pre_onboarding_v2",
        label: "Pre-onboarding V2",
        description:
          "Uses the continuous address, membership, booking, payment, and account-creation experience.",
        payload: { preOnboardingFlow: { version: "v2" } },
      },
      {
        key: "pre_onboarding_v3",
        label: "Pre-onboarding V3",
        description: "Uses contact-first account lookup and a short member sign-up.",
        payload: { preOnboardingFlow: { version: "v3" } },
      },
    ],
    exposureMetricKey: "pre_onboarding_flow_version_entered",
    primaryMetricKey: toOutcomeMetricKey("member_visit_booked"),
    primaryMetricLabel: "First visits booked per assigned pre-onboarding open",
    northStarMetricKey: "value_metrics_d30_contribution_profit_cents",
    guardrailMetricKeys: [
      "pre_onboarding_submit_failed",
      toOutcomeMetricKey("member_membership_refunded"),
      toOutcomeMetricKey("member_visit_refunded"),
    ],
    minimumSample: {
      assignedSessionsPerVariant: 300,
      accountsCreatedPerVariant: 30,
      visitBookingsPerVariant: 20,
    },
    stopRule:
      "Review after minimum sample and D30 economics are available; stop early for a material account-creation, refund, or terminal-blocker regression.",
    goals:
      funnelExperimentGoals[EXPERIMENT_KEYS.funnelPreOnboardingFlowVersionV1]
        ?.goals || [],
    metrics: [
      {
        key: "pre_onboarding_flow_version_entered",
        label: "Assigned pre-onboarding flow entered",
        kind: "exposure",
      },
    ],
    outcomeMetrics: FUNNEL_OUTCOME_METRICS,
  },

  [EXPERIMENT_KEYS.funnelPreOnboardingV2ReservationEntryV1]: {
    key: EXPERIMENT_KEYS.funnelPreOnboardingV2ReservationEntryV1,
    revisionId: "funnel_pre_onboarding_v2_reservation_entry_v1_r1",
    effectiveStartAt: PRE_ONBOARDING_V2_RESERVATION_ENTRY_START_AT,
    referenceVariantKey: "existing_booking_experiments",
    fallbackVariantKey: "existing_booking_experiments",
    allocationReason:
      "Equal session-level allocation among serviceable V2 booking entrants compares the existing lobby and booking-mode sequence with a suggested-helper reservation card.",
    searchToken: `${SEARCH_PREFIX}${EXPERIMENT_KEYS.funnelPreOnboardingV2ReservationEntryV1}`,
    title: "Pre-Onboarding V2 Reservation Entry",
    description:
      "Tests the suggested-helper reservation card against the existing helper-lobby and Grace-versus-calendar booking sequence.",
    status: "active",
    kind: "ab_test",
    owner: "growth",
    tags: ["funnel", "pre_onboarding_v2", "booking", "reservation"],
    surfaces: ["pre_onboarding_v2"],
    hypothesis:
      "A single suggested helper and available times can improve first-visit booking completion while preserving checkout and refund guardrails.",
    assignment: {
      ...CANONICAL_RANDOMIZED_ASSIGNMENT_POLICY,
      unit: "session",
      cadence: "static",
      seedSalt: "funnel_pre_onboarding_v2_reservation_entry_v1_r1",
      revisionId: "funnel_pre_onboarding_v2_reservation_entry_v1_r1",
      variants: [
        { key: "existing_booking_experiments", weight: 1 },
        { key: "suggested_helper_reservation", weight: 1 },
      ],
    },
    variants: [
      {
        key: "existing_booking_experiments",
        label: "Existing booking entry",
        description: "Continues through the helper-lobby and Grace-versus-calendar experiments.",
      },
      {
        key: "suggested_helper_reservation",
        label: "Suggested helper reservation",
        description: "Shows a highlighted helper and available first-visit times.",
      },
    ],
    exposureMetricKey: "pre_onboarding_v2_reservation_entry_entered",
    primaryMetricKey: toOutcomeMetricKey("member_visit_booked"),
    primaryMetricLabel: "First visits booked per V2 reservation-entry entrant",
    northStarMetricKey: "value_metrics_d30_contribution_profit_cents",
    guardrailMetricKeys: [
      "pre_onboarding_submit_failed",
      toOutcomeMetricKey("member_membership_refunded"),
      toOutcomeMetricKey("member_visit_refunded"),
    ],
    minimumSample: { bookingEntrantsPerVariant: 150, visitBookingsPerVariant: 20 },
    stopRule:
      "Review after minimum sample and D30 economics are available; stop early if the reservation card materially increases terminal blockers, checkout failures, or refunds.",
    goals:
      funnelExperimentGoals[EXPERIMENT_KEYS.funnelPreOnboardingV2ReservationEntryV1]
        ?.goals || [],
    metrics: [
      {
        key: "pre_onboarding_v2_reservation_entry_entered",
        label: "V2 reservation entry entered",
        kind: "exposure",
      },
      { key: "reservation_choices_rendered", label: "Reservation choices rendered", kind: "view" },
      { key: "reservation_availability_wait_started", label: "Availability wait started", kind: "event" },
      { key: "reservation_availability_wait_finished", label: "Availability wait finished", kind: "event" },
      { key: "reservation_suggestions_unavailable", label: "Reservation suggestions unavailable", kind: "event" },
      { key: "reservation_booking_panel_reached", label: "Time choices reached", kind: "view" },
      { key: "reservation_scroll_more_clicked", label: "Scroll to choices tapped", kind: "event" },
      { key: "reservation_full_calendar_opened", label: "Full calendar opened", kind: "event" },
      { key: "reservation_time_selected", label: "Reservation time selected", kind: "event" },
      { key: "reservation_task_description_opened", label: "Task description opened", kind: "event" },
      { key: "reservation_duration_changed", label: "Reservation duration changed", kind: "event" },
      { key: "reservation_card_exited", label: "Reservation card exited", kind: "event" },
      { key: "reservation_returned_to_calendar", label: "Returned from tasks to calendar", kind: "event" },
      { key: "reservation_returned_from_booking", label: "Returned to reservation card", kind: "event" },
    ],
    outcomeMetrics: FUNNEL_OUTCOME_METRICS,
  },

  [EXPERIMENT_KEYS.funnelPreOnboardingV2HelperLobbyV1]: {
    key: EXPERIMENT_KEYS.funnelPreOnboardingV2HelperLobbyV1,
    revisionId: "funnel_pre_onboarding_v2_helper_lobby_v1_r1",
    effectiveStartAt: PRE_ONBOARDING_V2_EXPERIMENTS_START_AT,
    referenceVariantKey: "show_helper_lobby",
    fallbackVariantKey: "show_helper_lobby",
    allocationReason:
      "Equal session-level allocation among serviceable V2 booking entrants isolates helper browsing from the independently assigned Grace-versus-calendar mode.",
    searchToken: `${SEARCH_PREFIX}${EXPERIMENT_KEYS.funnelPreOnboardingV2HelperLobbyV1}`,
    title: "Pre-Onboarding V2 Helper Lobby",
    description:
      "Tests the helper lobby against going directly to the independently assigned Grace or calendar booking experience.",
    status: "active",
    kind: "ab_test",
    owner: "growth",
    tags: ["funnel", "pre_onboarding_v2", "booking", "helper_lobby"],
    surfaces: ["pre_onboarding_v2"],
    hypothesis:
      "Browsing local helpers before booking may build enough trust to improve first-visit conversion, while going directly to Grace or the calendar may win by reducing one step.",
    assignment: {
      ...CANONICAL_RANDOMIZED_ASSIGNMENT_POLICY,
      unit: "session",
      cadence: "static",
      seedSalt: "funnel_pre_onboarding_v2_helper_lobby_v1_r1",
      revisionId: "funnel_pre_onboarding_v2_helper_lobby_v1_r1",
      variants: [
        { key: "show_helper_lobby", weight: 1 },
        { key: "go_directly_to_booking", weight: 1 },
      ],
    },
    variants: [
      {
        key: "show_helper_lobby",
        label: "Show helper lobby",
        description: "Shows local helper profiles before booking.",
      },
      {
        key: "go_directly_to_booking",
        label: "Go directly to booking",
        description:
          "Opens the assigned Grace or calendar booking experience directly.",
        payload: { preOnboardingV2HelperLobby: { enabled: false } },
      },
    ],
    exposureMetricKey: "pre_onboarding_v2_helper_lobby_entered",
    primaryMetricKey: toOutcomeMetricKey("member_visit_booked"),
    primaryMetricLabel: "First visits booked per V2 helper-lobby entrant",
    northStarMetricKey: "value_metrics_d30_contribution_profit_cents",
    guardrailMetricKeys: [
      "pre_onboarding_submit_failed",
      toOutcomeMetricKey("member_membership_refunded"),
      toOutcomeMetricKey("member_visit_refunded"),
    ],
    minimumSample: {
      bookingEntrantsPerVariant: 150,
      visitBookingsPerVariant: 20,
    },
    stopRule:
      "Review after minimum sample and D30 economics are available; stop early if either lobby treatment materially increases terminal blockers, checkout failures, or refunds.",
    goals:
      funnelExperimentGoals[EXPERIMENT_KEYS.funnelPreOnboardingV2HelperLobbyV1]
        ?.goals || [],
    metrics: [
      {
        key: "pre_onboarding_v2_helper_lobby_entered",
        label: "V2 helper-lobby treatment entered",
        kind: "exposure",
      },
    ],
    outcomeMetrics: FUNNEL_OUTCOME_METRICS,
  },

  [EXPERIMENT_KEYS.funnelPreOnboardingStartCardV2]: {
    key: EXPERIMENT_KEYS.funnelPreOnboardingStartCardV2,
    revisionId: "funnel_pre_onboarding_start_card_v2_r2",
    effectiveStartAt: VISIT_BOOKING_REALLOCATION_AT,
    fallbackVariantKey: "help_type_first",
    searchToken: `${SEARCH_PREFIX}${EXPERIMENT_KEYS.funnelPreOnboardingStartCardV2}`,
    title: "Pre-Onboarding Start Card",
    description:
      "Tests which first card best matches pre-onboarding intent before the modular middle flow and contact ending compose the rest of the experience.",
    status: "retired",
    kind: "ab_test",
    owner: "growth",
    tags: [
      "funnel",
      "pre_onboarding",
      "account_creation",
      "step_order",
      "experiments_v2",
    ],
    surfaces: ["pre_onboarding_flow"],
    hypothesis:
      "A first card that immediately matches CTA intent and creates local trust can increase account creation and paid membership without increasing refund, validation, or support-demand guardrails.",
    assignment: null,
    variants: [
      {
        key: "help_type_first",
        label: "Help type first",
        description:
          "Starts with the task/help-type card, then lets the length experiment compose the remaining middle cards.",
        payload: {
          preOnboardingStart: {
            enabled: true,
            startVariantKey: "help_type_first_v2",
            stepIds: ["HELP_TYPE"],
          },
        },
      },
      {
        key: "location_first",
        label: "Location first",
        description:
          "Starts with location, then lets the length experiment decide whether helper preview belongs in the remaining middle cards.",
        payload: {
          preOnboardingStart: {
            enabled: true,
            startVariantKey: "location_first_v2",
            stepIds: ["LOCATION"],
          },
        },
      },
    ],
    exposureMetricKey: "surface_viewed",
    primaryMetricKey: toOutcomeMetricKey("member_account_created"),
    primaryMetricLabel: "Accounts created per pre-onboarding open",
    northStarMetricKey: toOutcomeMetricKey("member_membership_paid"),
    guardrailMetricKeys: [
      "pre_onboarding_account_contact_entry_evidenced",
      "pre_onboarding_existing_account_handoff",
      "pre_onboarding_validation_failed",
      "pre_onboarding_submit_failed",
      "pre_onboarding_submit_succeeded",
      toOutcomeMetricKey("member_onboarding_completed"),
      toOutcomeMetricKey("member_visit_booked"),
      toOutcomeMetricKey("member_membership_paid"),
      toOutcomeMetricKey("member_membership_refunded"),
      toOutcomeMetricKey("member_visit_refunded"),
    ],
    minimumSample: {
      preOnboardingOpensPerVariant: 300,
      accountsCreatedPerVariant: 30,
      paidMembershipsPerVariant: 10,
    },
    stopRule:
      "Review after minimum sample or 14 complete rollup days; promote a first-card unit only if account creation improves with paid membership, refund, validation, and support-demand guardrails stable or better.",
    goals:
      funnelExperimentGoals[EXPERIMENT_KEYS.funnelPreOnboardingStartCardV2]
        ?.goals || [],
    metrics: [
      { key: "surface_viewed", label: "Surface viewed", kind: "exposure" },
      {
        key: "pre_onboarding_opened",
        label: "Pre-onboarding opened",
        kind: "view",
      },
      {
        key: "pre_onboarding_step_viewed",
        label: "Step viewed",
        kind: "view",
      },
      {
        key: "pre_onboarding_step_advanced",
        label: "Step advanced",
        kind: "action",
      },
      {
        key: "pre_onboarding_account_contact_entry_evidenced",
        label: "Account/contact entry",
        kind: "view",
      },
      {
        key: "pre_onboarding_existing_account_handoff",
        label: "Existing account handoff",
        kind: "action",
      },
      {
        key: "pre_onboarding_validation_failed",
        label: "Validation failed",
        kind: "error",
      },
      {
        key: "pre_onboarding_submit_started",
        label: "Account submit started",
        kind: "action",
      },
      {
        key: "pre_onboarding_submit_failed",
        label: "Account submit failed",
        kind: "error",
      },
      {
        key: "pre_onboarding_submit_succeeded",
        label: "Account submitted",
        kind: "conversion",
      },
    ],
    outcomeMetrics: FUNNEL_OUTCOME_METRICS,
  },

  [EXPERIMENT_KEYS.funnelPreOnboardingLocationFirstShortV1]: {
    key: EXPERIMENT_KEYS.funnelPreOnboardingLocationFirstShortV1,
    revisionId: "funnel_pre_onboarding_location_first_short_v1_r1",
    searchToken: `${SEARCH_PREFIX}${EXPERIMENT_KEYS.funnelPreOnboardingLocationFirstShortV1}`,
    title: "Pre-Onboarding Location Order and Short Flow",
    description:
      "Tests the original long pre-onboarding flow against a long location-first flow and a shorter location-first flow that keeps membership, PWYC, and the existing contact-ending split.",
    status: "retired",
    kind: "ab_test",
    owner: "growth",
    tags: [
      "funnel",
      "pre_onboarding",
      "account_creation",
      "step_order",
      "flow_length",
      "experiments_v1",
      "retired",
    ],
    surfaces: ["pre_onboarding_flow"],
    hypothesis:
      "Historical combined order-and-length test retired in favor of modular start-card, flow-length, and contact-ending v2 experiments with fresh result attribution.",
    assignment: {
      ...CANONICAL_RANDOMIZED_ASSIGNMENT_POLICY,
      unit: "session",
      cadence: "static",
      seedSalt: "funnel_pre_onboarding_location_first_short_v1",
      variants: [
        { key: "control", weight: 25 },
        { key: "location_first_long", weight: 25 },
        { key: "location_first_short", weight: 50 },
      ],
    },
    variants: [
      {
        key: "control",
        label: "Original long flow",
        description:
          "Original long pre-onboarding order with the existing independent contact-ending split.",
        payload: {},
      },
      {
        key: "location_first_long",
        label: "Location-first long flow",
        description:
          "Full pre-onboarding path with the location card shown before the original long-flow questions.",
        payload: {
          preOnboardingFlow: {
            enabled: true,
            flowVariantKey: "location_first_long_v1",
          },
          stepOrder: [
            "LOCATION",
            "HELPER_PREVIEW",
            "HELP_TYPE",
            "PRIORITY",
            "TIMING",
            "SUMMARY",
            "HOW_IT_WORKS",
            "MEMBERSHIP",
            "PWYC_RATE",
            "QUESTIONS",
            "EMAIL",
            "CONTACT",
          ],
        },
      },
      {
        key: "location_first_short",
        label: "Location-first short flow",
        description:
          "Shorter path: location, help type, membership, PWYC rate, then the existing contact ending.",
        payload: {
          preOnboardingFlow: {
            enabled: true,
            flowVariantKey: "location_first_short_v1",
            removedStepIds: [
              "PRIORITY",
              "TIMING",
              "SUMMARY",
              "HOW_IT_WORKS",
              "QUESTIONS",
              "HELPER_PREVIEW",
            ],
            contactEndingAfterStepId: "PWYC_RATE",
          },
          hiddenStepIds: [
            "PRIORITY",
            "TIMING",
            "SUMMARY",
            "HOW_IT_WORKS",
            "QUESTIONS",
            "HELPER_PREVIEW",
          ],
          stepOrder: [
            "LOCATION",
            "HELP_TYPE",
            "MEMBERSHIP",
            "PWYC_RATE",
            "EMAIL",
            "CONTACT",
          ],
        },
      },
    ],
    exposureMetricKey: "surface_viewed",
    primaryMetricKey: toOutcomeMetricKey("member_account_created"),
    primaryMetricLabel: "Accounts created per pre-onboarding open",
    northStarMetricKey: toOutcomeMetricKey("member_membership_paid"),
    guardrailMetricKeys: [
      "pre_onboarding_account_contact_entry_evidenced",
      "pre_onboarding_existing_account_handoff",
      "pre_onboarding_validation_failed",
      "pre_onboarding_submit_failed",
      "pre_onboarding_submit_succeeded",
      toOutcomeMetricKey("member_onboarding_completed"),
      toOutcomeMetricKey("member_visit_booked"),
      toOutcomeMetricKey("member_membership_paid"),
      toOutcomeMetricKey("member_membership_refunded"),
      toOutcomeMetricKey("member_visit_refunded"),
    ],
    minimumSample: {
      preOnboardingOpensPerVariant: 250,
      accountsCreatedPerVariant: 30,
      paidMembershipsPerVariant: 10,
    },
    stopRule:
      "Review after minimum sample or 14 complete rollup days; promote only if accounts created per pre-onboarding open improve with paid membership, onboarding completion, visit booking, refund, validation, and support-demand guardrails stable or better.",
    goals:
      funnelExperimentGoals[
        EXPERIMENT_KEYS.funnelPreOnboardingLocationFirstShortV1
      ]?.goals || [],
    metrics: [
      { key: "surface_viewed", label: "Surface viewed", kind: "exposure" },
      {
        key: "pre_onboarding_opened",
        label: "Pre-onboarding opened",
        kind: "view",
      },
      {
        key: "pre_onboarding_step_viewed",
        label: "Step viewed",
        kind: "view",
      },
      {
        key: "pre_onboarding_step_advanced",
        label: "Step advanced",
        kind: "action",
      },
      {
        key: "pre_onboarding_account_contact_entry_evidenced",
        label: "Account/contact entry",
        kind: "view",
      },
      {
        key: "pre_onboarding_existing_account_handoff",
        label: "Existing account handoff",
        kind: "action",
      },
      {
        key: "pre_onboarding_validation_failed",
        label: "Validation failed",
        kind: "error",
      },
      {
        key: "pre_onboarding_submit_started",
        label: "Account submit started",
        kind: "action",
      },
      {
        key: "pre_onboarding_submit_failed",
        label: "Account submit failed",
        kind: "error",
      },
      {
        key: "pre_onboarding_submit_succeeded",
        label: "Account submitted",
        kind: "conversion",
      },
    ],
    outcomeMetrics: FUNNEL_OUTCOME_METRICS,
  },

  [EXPERIMENT_KEYS.funnelLocalHelperMomentumCardV1]: {
    key: EXPERIMENT_KEYS.funnelLocalHelperMomentumCardV1,
    revisionId: "funnel_local_helper_momentum_card_v1_r1",
    searchToken: `${SEARCH_PREFIX}${EXPERIMENT_KEYS.funnelLocalHelperMomentumCardV1}`,
    title: "Local Helper Momentum Card",
    description:
      "Tests whether showing truthful local helper activity builds enough confidence to improve paid membership conversion.",
    status: "retired",
    kind: "ab_test",
    owner: "growth",
    tags: [
      "funnel",
      "pre_onboarding",
      "member_onboarding",
      "member_dashboard",
      "helper_availability",
      "paid_membership",
      "experiments_v1",
    ],
    surfaces: [
      "pre_onboarding_flow",
      "pre_onboarding_helper_preview",
      "member_onboarding_service_status",
      "member_dashboard_helpers",
    ],
    hypothesis:
      "Showing active helpers, helpers starting soon, and recent local applications can make a growing market feel credible and increase paid memberships without increasing refunds.",
    assignment: {
      ...CANONICAL_RANDOMIZED_ASSIGNMENT_POLICY,
      unit: "session",
      cadence: "static",
      seedSalt: "funnel_local_helper_momentum_card_v1",
      variants: [
        { key: "control", weight: 50 },
        { key: "show_momentum_card", weight: 50 },
      ],
    },
    variants: [
      {
        key: "control",
        label: "No local momentum card",
        description:
          "Keeps the existing helper lists without the local momentum card.",
        payload: {},
      },
      {
        key: "show_momentum_card",
        label: "Show local momentum card",
        description:
          "Shows the approved local helper momentum card above the existing helper lists.",
        payload: {
          localHelperMomentumCard: {
            enabled: true,
          },
        },
      },
    ],
    exposureMetricKey: "local_helper_momentum_eligible_viewed",
    primaryMetricKey: toOutcomeMetricKey("member_membership_paid"),
    primaryMetricLabel: "Paid memberships per assigned session",
    northStarMetricKey: toOutcomeMetricKey("member_membership_paid"),
    guardrailMetricKeys: [
      toOutcomeMetricKey("member_account_created"),
      toOutcomeMetricKey("member_onboarding_completed"),
      toOutcomeMetricKey("member_visit_booked"),
      toOutcomeMetricKey("member_membership_refunded"),
      toOutcomeMetricKey("member_visit_refunded"),
    ],
    minimumSample: {
      assignedSessionsPerVariant: 500,
      eligibleViewsPerVariant: 500,
      paidMembershipsPerVariant: 20,
    },
    stopRule:
      "Retired on 2026-08-01 after founder decision; show_momentum_card was promoted as the default local helper momentum experience.",
    goals: [],
    metrics: [
      {
        key: "local_helper_momentum_eligible_viewed",
        label: "Eligible helper surface viewed",
        kind: "exposure",
      },
      {
        key: "local_helper_momentum_card_viewed",
        label: "Momentum card viewed",
        kind: "view",
      },
      {
        key: "local_helper_momentum_card_collapsed",
        label: "Momentum card collapsed",
        kind: "action",
      },
      {
        key: "local_helper_momentum_profile_opened",
        label: "Helper profile opened from momentum map",
        kind: "action",
      },
    ],
    outcomeMetrics: FUNNEL_OUTCOME_METRICS,
  },

  [EXPERIMENT_KEYS.memberStampCardV1]: {
    // Shared browser/server definition: member_stamp_card_v1_r1.
    ...buildStampCardExperiment(),
    searchToken: `${SEARCH_PREFIX}${EXPERIMENT_KEYS.memberStampCardV1}`,
    goals: funnelExperimentGoals[EXPERIMENT_KEYS.memberStampCardV1]?.goals || [],
    outcomeMetrics: FUNNEL_OUTCOME_METRICS,
  },

  [EXPERIMENT_KEYS.memberDashboardFounderNoteV2]: {
    key: EXPERIMENT_KEYS.memberDashboardFounderNoteV2,
    revisionId: "member_dashboard_founder_note_v2_r1",
    effectiveStartAt: "2026-10-12T07:00:00.000Z",
    referenceVariantKey: "click_to_open",
    fallbackVariantKey: "click_to_open",
    surfaceSlot: "member_dashboard_founder_note",
    searchToken: `${SEARCH_PREFIX}${EXPERIMENT_KEYS.memberDashboardFounderNoteV2}`,
    title: "Member Dashboard Founder Note",
    description:
      "Compares a note discovered through the welcome card with the same note automatically shown on the first eligible dashboard view.",
    status: "active",
    kind: "ab_test",
    owner: "growth",
    tags: ["member_dashboard", "founder_note", "experiments_v1"],
    surfaces: ["member_dashboard_home"],
    hypothesis:
      "Seeing the founder's note on the first eligible dashboard view may increase emotional connection and visit bookings.",
    allocationReason: "Equal member-level allocation between click-to-open and first-view auto-open.",
    assignment: {
      ...CANONICAL_RANDOMIZED_ASSIGNMENT_POLICY,
      unit: "member",
      cadence: "static",
      seedSalt: "member_dashboard_founder_note_v2_r1",
      revisionId: "member_dashboard_founder_note_v2_r1",
      variants: [
        { key: "click_to_open", weight: 1 },
        { key: "auto_open", weight: 1 },
      ],
    },
    variants: [
      {
        key: "click_to_open",
        label: "Click to open",
        description: "The welcome card and mail icon open the note when selected.",
        payload: { founderNote: { autoOpen: false } },
      },
      {
        key: "auto_open",
        label: "Open on first view",
        description: "The note opens once when the welcome card first appears; the card can reopen it.",
        payload: { founderNote: { autoOpen: true } },
      },
    ],
    exposureMetricKey: "founder_note_card_viewed",
    primaryMetricKey: toOutcomeMetricKey("member_visit_booked"),
    primaryMetricLabel: "Visit bookings per assigned member",
    northStarMetricKey: "value_metrics_d30_contribution_profit_cents",
    guardrailMetricKeys: [toOutcomeMetricKey("member_visit_refunded")],
    minimumSample: { assignedMembersPerVariant: 200, eligibleViewsPerVariant: 200 },
    stopRule:
      "Compare visit bookings after sufficient eligible exposure and a mature outcome window; inspect manual discovery, reopens, and visible dwell time by arm.",
    goals: funnelExperimentGoals[EXPERIMENT_KEYS.memberDashboardFounderNoteV2]?.goals || [],
    metrics: [
      { key: "founder_note_card_viewed", label: "Eligible welcome card viewed", kind: "exposure" },
      { key: "founder_note_opened", label: "Founder note opened", kind: "view" },
      { key: "founder_note_auto_opened", label: "Founder note automatically opened", kind: "view" },
      { key: "founder_note_manually_opened", label: "Founder note manually opened", kind: "action" },
      { key: "founder_note_first_manual_opened", label: "First manual note open", kind: "action" },
      { key: "founder_note_reopened", label: "Founder note reopened", kind: "action" },
      {
        key: "founder_note_dwell_ms",
        label: "Visible time with founder note open",
        kind: "timing",
        valueMode: "average",
        format: "duration_ms",
      },
      {
        key: "founder_note_auto_dwell_ms",
        label: "Visible time after automatic open",
        kind: "timing",
        valueMode: "average",
        format: "duration_ms",
      },
      {
        key: "founder_note_manual_dwell_ms",
        label: "Visible time after manual open",
        kind: "timing",
        valueMode: "average",
        format: "duration_ms",
      },
      { key: "founder_note_auto_quick_closed", label: "Automatic opens closed within 3 seconds", kind: "action" },
      { key: "founder_note_manual_quick_closed", label: "Manual opens closed within 3 seconds", kind: "action" },
    ],
    outcomeMetrics: FUNNEL_OUTCOME_METRICS,
  },

  [EXPERIMENT_KEYS.memberDashboardFounderWelcomeCardV1]: {
    key: EXPERIMENT_KEYS.memberDashboardFounderWelcomeCardV1,
    revisionId: "member_dashboard_founder_welcome_card_v1_r1",
    searchToken: `${SEARCH_PREFIX}${EXPERIMENT_KEYS.memberDashboardFounderWelcomeCardV1}`,
    title: "Member Dashboard Founder Welcome Card",
    description:
      "Tests whether adding an optional Founder Welcome video action to the member dashboard welcome card improves activation and backend-linked outcomes.",
    status: "retired",
    kind: "ab_test",
    owner: "growth",
    tags: [
      "member_dashboard",
      "home",
      "founder_welcome",
      "video",
      "experiments_v1",
    ],
    surfaces: ["member_dashboard_home"],
    assignment: null,
    variants: [
      {
        key: "control",
        label: "Standard welcome card",
        description:
          "Current member dashboard welcome card without a Founder Welcome video action.",
        payload: {},
      },
      {
        key: "show_founder_welcome",
        label: "Welcome card opens Founder Welcome video",
        description:
          "Adds an optional click action on the member dashboard welcome card that opens the Founder Welcome video.",
        payload: {
          founderWelcomeHomeCard: {
            enabled: true,
            actionVariantKey: "home_welcome_card_video",
            contentVariantKey: "founder_welcome_video_v1",
            videoId: MEMBER_DASHBOARD_FOUNDER_WELCOME_VIDEO_ID,
            videoUrl: MEMBER_DASHBOARD_FOUNDER_WELCOME_VIDEO_URL,
            posterUrl: MEMBER_DASHBOARD_FOUNDER_WELCOME_POSTER_URL,
            storagePath: MEMBER_DASHBOARD_FOUNDER_WELCOME_STORAGE_PATH,
            posterStoragePath:
              MEMBER_DASHBOARD_FOUNDER_WELCOME_POSTER_STORAGE_PATH,
          },
        },
      },
    ],
    exposureMetricKey: "welcome_card_viewed",
    primaryMetricKey: "founder_welcome_card_clicked",
    goals:
      funnelExperimentGoals[EXPERIMENT_KEYS.memberDashboardFounderWelcomeCardV1]
        ?.goals || [],
    metrics: [
      {
        key: "welcome_card_viewed",
        label: "Welcome card viewed",
        kind: "exposure",
      },
      {
        key: "founder_welcome_card_clicked",
        label: "Founder Welcome card clicked",
        kind: "action",
      },
      {
        key: "founder_welcome_video_started",
        label: "Founder Welcome video started",
        kind: "action",
      },
      {
        key: "founder_welcome_video_10s_reached",
        label: "Founder Welcome video reached 10 seconds",
        kind: "action",
      },
      {
        key: "founder_welcome_video_completed",
        label: "Founder Welcome video completed",
        kind: "conversion",
      },
      {
        key: "founder_welcome_video_error",
        label: "Founder Welcome video error",
        kind: "error",
      },
    ],
    outcomeMetrics: [
      {
        key: toOutcomeMetricKey("member_onboarding_completed"),
        label: "Onboarding completed",
        kind: "outcome",
        sourceEvent: "member_onboarding_completed",
      },
      {
        key: toOutcomeMetricKey("member_visit_booked"),
        label: "Visit booked",
        kind: "outcome",
        sourceEvent: "member_visit_booked",
      },
      {
        key: toOutcomeMetricKey("member_membership_paid"),
        label: "Membership paid",
        kind: "outcome",
        sourceEvent: "member_membership_paid",
      },
      {
        key: toOutcomeMetricKey("member_membership_refunded"),
        label: "Membership refunded",
        kind: "outcome",
        sourceEvent: "member_membership_refunded",
      },
      {
        key: toOutcomeMetricKey("member_visit_refunded"),
        label: "Visit refunded",
        kind: "outcome",
        sourceEvent: "member_visit_refunded",
      },
    ],
  },

  [EXPERIMENT_KEYS.memberPostSignupDashboardV3]: {
    key: EXPERIMENT_KEYS.memberPostSignupDashboardV3,
    revisionId: "member_post_signup_dashboard_v3_r2",
    effectiveStartAt: VISIT_BOOKING_REALLOCATION_AT,
    fallbackVariantKey: "legacy_onboarding",
    searchToken: `${SEARCH_PREFIX}${EXPERIMENT_KEYS.memberPostSignupDashboardV3}`,
    title: "Member Post-Signup Dashboard V3",
    description:
      "Tests the current forced post-account onboarding flow against a dashboard-first experience with a persistent new member checklist and pay-now membership entry points.",
    status: "retired",
    kind: "ab_test",
    owner: "growth",
    tags: [
      "member_dashboard",
      "member_onboarding",
      "billing",
      "first_visit",
      "experiments_v1",
    ],
    surfaces: [
      "member_post_signup",
      "member_dashboard_home",
      "member_dashboard_billing",
      "schedule_visit_flow",
    ],
    assignment: null,
    variants: [
      {
        key: "legacy_onboarding",
        label: "Legacy forced onboarding",
        description:
          "Keeps the current post-account MemberOnboardingFlow before dashboard access.",
        payload: {
          postSignupExperience: {
            mode: "legacy_onboarding",
          },
        },
      },
      {
        key: "dashboard_checklist_v2",
        label: "Dashboard checklist V2",
        description:
          "Lets new members enter the dashboard sooner and complete setup through a persistent checklist with pay-now membership access.",
        payload: {
          postSignupExperience: {
            mode: "dashboard_checklist_v2",
            dashboardPrioritySlot: true,
            shortenedOnboarding: true,
            membershipPayNow: true,
            membershipOffer: getMemberPostSignupMembershipOffer(),
          },
        },
      },
    ],
    exposureMetricKey: "post_signup_experience_viewed",
    primaryMetricKey: toOutcomeMetricKey("member_membership_paid"),
    goals:
      funnelExperimentGoals[EXPERIMENT_KEYS.memberPostSignupDashboardV3]
        ?.goals || [],
    metrics: [
      {
        key: "post_signup_experience_viewed",
        label: "Post-signup experience viewed",
        kind: "exposure",
      },
      {
        key: "new_member_checklist_viewed",
        label: "Checklist viewed",
        kind: "exposure",
      },
      {
        key: "new_member_checklist_item_opened",
        label: "Checklist item opened",
        kind: "action",
      },
      {
        key: "new_member_checklist_item_deferred",
        label: "Checklist item deferred",
        kind: "action",
      },
      {
        key: "new_member_checklist_item_completed",
        label: "Checklist item completed",
        kind: "action",
      },
      {
        key: "membership_start_opened",
        label: "Membership start opened",
        kind: "action",
      },
      {
        key: "billing_membership_start_opened",
        label: "Billing membership start opened",
        kind: "action",
      },
      {
        key: "first_visit_booking_opened",
        label: "First visit booking opened",
        kind: "action",
      },
    ],
    outcomeMetrics: FUNNEL_OUTCOME_METRICS,
  },

  [EXPERIMENT_KEYS.memberDashboardChecklistOrderV1]: {
    key: EXPERIMENT_KEYS.memberDashboardChecklistOrderV1,
    revisionId: "member_dashboard_checklist_order_v1_r2",
    effectiveStartAt: VISIT_BOOKING_REALLOCATION_AT,
    fallbackVariantKey: "current_order",
    searchToken: `${SEARCH_PREFIX}${EXPERIMENT_KEYS.memberDashboardChecklistOrderV1}`,
    title: "Member Dashboard Checklist Order V1",
    description:
      "Among Dashboard V2 members, tests the current seven-item checklist against a six-item checklist that starts with booking a visit.",
    status: "retired",
    kind: "ab_test",
    owner: "growth",
    tags: [
      "member_dashboard",
      "first_visit",
      "member_onboarding",
      "experiments_v1",
    ],
    surfaces: ["member_dashboard_home", "schedule_visit_flow"],
    assignment: null,
    variants: [
      {
        key: "current_order",
        label: "Current checklist",
        description: "Keeps the existing seven checklist cards and order.",
      },
      {
        key: "book_visit_first",
        label: "Book a visit first",
        description:
          "Shows Book a visit, Membership, Home address, Choose your rate, Your profile, and How you found us, in that order.",
      },
    ],
    exposureMetricKey: "new_member_checklist_viewed",
    primaryMetricKey: toOutcomeMetricKey("member_visit_booked"),
    primaryMetricLabel: "First visit bookings",
    northStarMetricKey: toOutcomeMetricKey("member_membership_paid"),
    hypothesis:
      "Putting the member's first concrete outcome first while crediting completed address and rate setup may increase visit bookings without reducing paid memberships or increasing refunds.",
    goals:
      funnelExperimentGoals[EXPERIMENT_KEYS.memberDashboardChecklistOrderV1]
        ?.goals || [],
    metrics: [
      {
        key: "new_member_checklist_viewed",
        label: "Checklist viewed",
        kind: "exposure",
      },
      {
        key: "new_member_checklist_item_opened",
        label: "Checklist item opened",
        kind: "action",
      },
      {
        key: "new_member_checklist_item_completed",
        label: "Checklist item completed",
        kind: "action",
      },
      {
        key: "first_visit_booking_opened",
        label: "First visit booking opened",
        kind: "action",
      },
      {
        key: "membership_start_opened",
        label: "Membership start opened",
        kind: "action",
      },
    ],
    outcomeMetrics: FUNNEL_OUTCOME_METRICS,
  },

  [EXPERIMENT_KEYS.memberPostSignupMembershipDiscountV1]: {
    key: EXPERIMENT_KEYS.memberPostSignupMembershipDiscountV1,
    revisionId: "member_post_signup_membership_discount_v1_r3",
    effectiveStartAt: VISIT_BOOKING_REALLOCATION_AT,
    fallbackVariantKey: "save_10_percent",
    searchToken: `${SEARCH_PREFIX}${EXPERIMENT_KEYS.memberPostSignupMembershipDiscountV1}`,
    title: "Post-Signup Membership Discount V1",
    description:
      "Among eligible fixed-membership Dashboard V2 members, tests 10% versus 20% same-day membership discounts. PWYC and zero-dollar treatments are excluded.",
    status: "retired",
    kind: "ab_test",
    owner: "growth",
    tags: [
      "member_dashboard",
      "billing",
      "membership",
      "discount",
      "experiments_v1",
    ],
    surfaces: [
      "member_dashboard_home",
      "member_dashboard_billing",
      "membership_checkout",
    ],
    assignment: null,
    variants: [10, 20].map((percent) => ({
      key: `save_${percent}_percent`,
      label: `Save ${percent}% today`,
      description: `Offers ${percent}% off a membership prepaid during the post-signup offer window.`,
      payload: { discountPercent: percent },
    })),
    exposureMetricKey: "membership_discount_offer_viewed",
    primaryMetricKey: toOutcomeMetricKey("member_membership_paid"),
    primaryMetricLabel: "Paid memberships",
    northStarMetricKey: "value_metrics_d30_contribution_profit_cents",
    hypothesis:
      "A 20% same-day discount may produce more total contribution profit than 10% by increasing immediate paid conversion enough to offset the additional discount and any refunds.",
    goals:
      funnelExperimentGoals[
        EXPERIMENT_KEYS.memberPostSignupMembershipDiscountV1
      ]?.goals || [],
    metrics: [
      {
        key: "membership_discount_offer_viewed",
        label: "Discount offer viewed",
        kind: "exposure",
      },
      {
        key: "membership_discount_offer_clicked",
        label: "Discount offer clicked",
        kind: "action",
      },
      {
        key: "membership_start_opened",
        label: "Membership start opened",
        kind: "action",
      },
      {
        key: "billing_membership_start_opened",
        label: "Billing membership start opened",
        kind: "action",
      },
    ],
    outcomeMetrics: FUNNEL_OUTCOME_METRICS,
  },

  [EXPERIMENT_KEYS.memberScheduleVisitPickerV2]: {
    key: EXPERIMENT_KEYS.memberScheduleVisitPickerV2,
    revisionId: "member_schedule_visit_picker_v2_r1",
    searchToken: `${SEARCH_PREFIX}${EXPERIMENT_KEYS.memberScheduleVisitPickerV2}`,
    title: "Member Schedule Visit Picker V2",
    description:
      "Historical scheduler picker experiment retired after the available-dates carousel, V2 visit slot picker, and requested visit path were promoted to the default booking experience.",
    status: "retired",
    kind: "ab_test",
    owner: "growth",
    tags: [
      "member_dashboard",
      "schedule_visit_flow",
      "first_visit",
      "booking",
      "experiments_v1",
    ],
    surfaces: ["schedule_visit_flow"],
    assignment: {
      ...CANONICAL_RANDOMIZED_ASSIGNMENT_POLICY,
      unit: "member",
      cadence: "static",
      seedSalt: "member_schedule_visit_picker_v2",
      variants: [
        { key: "legacy_calendar", weight: 0 },
        { key: "available_dates_v2", weight: 100 },
      ],
    },
    variants: [
      {
        key: "legacy_calendar",
        label: "Legacy calendar",
        description:
          "Keeps the original schedule visit calendar and legacy slot selection treatment.",
        payload: {
          scheduleVisitPicker: {
            calendarTreatment: "default",
          },
        },
      },
      {
        key: "available_dates_v2",
        label: "Available dates V2",
        description:
          "Uses the available-dates carousel, V2 filters, V2 visit slots, and V2 booking badges.",
        payload: {
          scheduleVisitPicker: {
            calendarTreatment: "available-dates",
          },
        },
      },
    ],
    exposureMetricKey: "schedule_visit_picker_viewed",
    primaryMetricKey: toOutcomeMetricKey("member_visit_request_claimed"),
    primaryMetricLabel: "Requested visit claimed",
    northStarMetricKey: toOutcomeMetricKey("member_visit_request_completed"),
    hypothesis:
      "The available-dates carousel, V2 visit slot picker, and requested visit path can increase visit booking by making sparse availability feel active and easier to choose without increasing refunds.",
    goals:
      funnelExperimentGoals[EXPERIMENT_KEYS.memberScheduleVisitPickerV2]
        ?.goals || [],
    metrics: [
      {
        key: "schedule_visit_picker_viewed",
        label: "Schedule visit picker viewed",
        kind: "exposure",
      },
      {
        key: "visit_time_selected",
        label: "Visit time selected",
        kind: "action",
      },
      {
        key: "calendar_continue",
        label: "Calendar step continued",
        kind: "action",
      },
      {
        key: "missing_visit_time",
        label: "Missing visit time validation",
        kind: "guardrail",
      },
      {
        key: "booking_submit",
        label: "Booking submitted",
        kind: "action",
      },
      {
        key: toOutcomeMetricKey("member_visit_request_created"),
        label: "Requested visit created",
        kind: "outcome",
        sourceEvent: "member_visit_request_created",
      },
      {
        key: toOutcomeMetricKey("member_visit_request_claimed"),
        label: "Requested visit claimed",
        kind: "outcome",
        sourceEvent: "member_visit_request_claimed",
      },
      {
        key: toOutcomeMetricKey("member_visit_request_completed"),
        label: "Requested visit completed",
        kind: "outcome",
        sourceEvent: "member_visit_request_completed",
      },
      {
        key: toOutcomeMetricKey("member_visit_request_expired"),
        label: "Requested visit expired",
        kind: "outcome",
        sourceEvent: "member_visit_request_expired",
      },
      {
        key: toOutcomeMetricKey("member_visit_request_cancelled"),
        label: "Requested visit cancelled",
        kind: "outcome",
        sourceEvent: "member_visit_request_cancelled",
      },
    ],
    outcomeMetrics: FUNNEL_OUTCOME_METRICS,
  },

  [EXPERIMENT_KEYS.memberFirstVisitReservationGateV2]:
    buildSimpleMemberBookingExperiment({
      key: EXPERIMENT_KEYS.memberFirstVisitReservationGateV2,
      revisionId: "member_first_visit_reservation_gate_v2_r2",
      title: "First Visit Reservation Dashboard Guard",
      description:
        "Tests a reservation guard after a new signup saves an address and has a bookable highlighted helper.",
      surfaces: ["member_dashboard_activation", "schedule_visit_flow"],
      eligibilityPolicyKey: "universal",
      effectiveStartAt: EXPERIMENT_DENOMINATOR_RESTART_AT,
      surfaceSlot: "member_first_visit_reservation_gate",
      supersedesExperimentKeys: ["member_first_visit_reservation_gate_v1"],
      variants: [
        {
          key: "standard_dashboard",
          label: "Standard dashboard entry",
          description:
            "Continues directly to the member dashboard after address setup.",
        },
        {
          key: "reservation_gate",
          label: "First visit reservation guard",
          description:
            "Introduces local pricing when needed, then a helper profile with four varied bookable times.",
          payload: { firstVisitReservationGate: { enabled: true } },
        },
      ],
      exposureMetricKey: "reservation_gate_eligible",
      actionMetrics: [
        {
          key: "reservation_highlighted_helper_bookable",
          label: "Bookable highlighted helper after address save",
          kind: "exposure",
        },
        {
          key: "reservation_choices_rendered",
          label: "Bookable reservation choices rendered",
          kind: "view",
        },
        {
          key: "reservation_pricing_market_timeout",
          label: "Reservation pricing market load timed out",
          kind: "diagnostic",
        },
        {
          key: "reservation_time_selected",
          label: "Suggested time selected",
          kind: "action",
        },
        {
          key: "reservation_booking_panel_reached",
          label: "Time choices reached",
          kind: "view",
        },
        {
          key: "reservation_scroll_more_clicked",
          label: "Scroll to choices tapped",
          kind: "action",
        },
        {
          key: "reservation_duration_changed",
          label: "Reservation duration changed",
          kind: "action",
        },
        {
          key: "reservation_task_description_opened",
          label: "Task description opened",
          kind: "action",
        },
        {
          key: "reservation_more_times_clicked",
          label: "Other times requested",
          kind: "action",
        },
        {
          key: "reservation_full_calendar_opened",
          label: "Full calendar opened",
          kind: "action",
        },
      ],
      hypothesis:
        "Turning the first dashboard transition into a concrete reservation choice may increase first visit bookings by carrying location intent directly into real availability.",
    }),

  [EXPERIMENT_KEYS.memberHelperGalleryVisibilityV1]:
    buildSimpleMemberBookingExperiment({
      key: EXPERIMENT_KEYS.memberHelperGalleryVisibilityV1,
      revisionId: "member_helper_gallery_visibility_v1_r1",
      title: "Member Helper Photo Galleries",
      description: "Shows or hides all helper photo galleries for a member after their first gallery opportunity.",
      surfaces: ["helper_profile", "member_dashboard_home", "schedule_visit_flow",
        "booking_review", "join_landing", "pre_onboarding_flow"],
      eligibilityPolicyKey: "authenticated_member",
      variants: [
        { key: "hide_gallery", label: "No helper gallery",
          description: "Keep the helper profile without the Photos section." },
        { key: "show_gallery", label: "Show helper gallery",
          description: "Keep the existing helper photo gallery." },
      ],
      fallbackVariantKey: "show_gallery",
      exposureMetricKey: "helper_gallery_opportunity",
      hypothesis: "Helper photos may increase first visit bookings and total booked visits.",
      effectiveStartAt: "2026-10-14T07:00:00.000Z",
      surfaceSlot: "member_helper_gallery",
    }),

  [EXPERIMENT_KEYS.memberFirstBookingHelperLobbyV1]:
    buildSimpleMemberBookingExperiment({
      key: EXPERIMENT_KEYS.memberFirstBookingHelperLobbyV1,
      revisionId: "member_first_booking_helper_lobby_v1_r1",
      title: "First Booking Helper Lobby",
      description:
        "Tests the helper lobby against opening booking directly only for members with no prior booking. Lapsed members always keep the lobby outside this experiment.",
      surfaces: ["schedule_visit_flow"],
      eligibilityPolicyKey: "universal",
      variants: [
        {
          key: "show_helper_lobby",
          label: "Show helper lobby",
          description: "Shows the existing helper lobby before booking.",
        },
        {
          key: "skip_helper_lobby",
          label: "Open booking directly",
          description:
            "Opens the independently assigned booking entry directly.",
          payload: { scheduleHelperLobby: { enabled: false } },
        },
      ],
      exposureMetricKey: "first_booking_helper_lobby_eligible",
      actionMetrics: [
        {
          key: "helper_lobby_viewed",
          label: "Helper lobby viewed",
          kind: "view",
        },
        {
          key: "helper_lobby_continued",
          label: "Helper lobby continued",
          kind: "action",
        },
      ],
      hypothesis:
        "Showing local helpers before a first booking may build enough trust to improve booking conversion relative to entering booking directly.",
      effectiveStartAt: MEMBER_FIRST_BOOKING_ENTRY_EXPERIMENTS_START_AT,
      surfaceSlot: "member_schedule_helper_lobby_gate",
      supersedesExperimentKeys: ["member_schedule_helper_lobby_v1"],
    }),

  [EXPERIMENT_KEYS.memberFirstBookingEntryModeV1]:
    buildSimpleMemberBookingExperiment({
      key: EXPERIMENT_KEYS.memberFirstBookingEntryModeV1,
      revisionId: "member_first_booking_entry_mode_v1_r1",
      title: "First Booking Entry Mode",
      description:
        "Tests fullscreen Grace against the standard calendar only for members with no prior booking.",
      surfaces: ["schedule_visit_flow"],
      eligibilityPolicyKey: "universal",
      variants: [
        {
          key: "standard_scheduler",
          label: "Calendar",
          description: "Opens the existing available-dates calendar.",
        },
        {
          key: "grace_assistant",
          label: "Grace",
          description: "Opens Grace fullscreen inside the booking flow.",
        },
      ],
      exposureMetricKey: "first_booking_entry_mode_entered",
      actionMetrics: [],
      hypothesis:
        "Grace may help first-time members turn an uncertain request into a bookable visit more often than starting with the calendar.",
      effectiveStartAt: MEMBER_FIRST_BOOKING_ENTRY_EXPERIMENTS_START_AT,
    }),

  [EXPERIMENT_KEYS.memberFirstVisitHelperCountDefaultV1]:
    buildSimpleMemberBookingExperiment({
      key: EXPERIMENT_KEYS.memberFirstVisitHelperCountDefaultV1,
      revisionId: "member_first_visit_helper_count_default_v1_r1",
      title: "First Visit Helper Count Default",
      description:
        "Tests a one-helper-only default against the existing one-or-two-helper default before a member's first booking, only when current availability includes a two-helper option.",
      surfaces: ["schedule_visit_flow"],
      eligibilityPolicyKey: "universal",
      variants: [
        {
          key: "one_or_two_helpers",
          label: "One or two helpers",
          description:
            "Keeps the existing one-or-two-helper scheduler default.",
        },
        {
          key: "one_helper_only",
          label: "One helper",
          description:
            "Defaults the first booking flow to visits with one helper.",
          payload: { firstVisitHelperCount: { helperCounts: [1] } },
        },
      ],
      exposureMetricKey: "first_visit_helper_count_default_applied",
      actionMetrics: [
        {
          key: "first_visit_helper_count_changed",
          label: "First visit helper count changed",
          kind: "action",
        },
      ],
      hypothesis:
        "Presenting the simplest staffing choice may reduce first-booking decision friction when both one- and two-helper visits are genuinely available.",
    }),

  [EXPERIMENT_KEYS.memberAddressCaptureModeV1]: {
    key: EXPERIMENT_KEYS.memberAddressCaptureModeV1,
    searchToken: `${SEARCH_PREFIX}${EXPERIMENT_KEYS.memberAddressCaptureModeV1}`,
    title: "Member Address Capture Mode",
    description:
      "Tracks whether onboarding members save a city-only address or a full street address, then attributes downstream member outcomes to that saved choice.",
    status: "retired",
    kind: "behavior_observation",
    owner: "growth",
    tags: ["member_onboarding", "address", "experiments_v1"],
    surfaces: ["member_onboarding_address_card"],
    assignment: null,
    variants: [
      {
        key: "city_only",
        label: "City Only",
        description: "Member saved a city-only address.",
      },
      {
        key: "street_address",
        label: "Street Address",
        description: "Member saved a full street address.",
      },
    ],
    exposureMetricKey: "form_viewed",
    primaryMetricKey: "address_saved",
    goals:
      funnelExperimentGoals[EXPERIMENT_KEYS.memberAddressCaptureModeV1]
        ?.goals || [],
    metrics: [
      {
        key: "form_viewed",
        label: "Form viewed",
        kind: "exposure",
      },
      {
        key: "option_selected",
        label: "Option selected",
        kind: "action",
      },
      {
        key: "address_saved",
        label: "Address saved",
        kind: "conversion",
      },
      {
        key: "completion_duration_ms",
        label: "Completion time",
        kind: "timing",
        valueMode: "average",
        format: "duration_ms",
      },
    ],
    outcomeMetrics: [
      {
        key: toOutcomeMetricKey("member_onboarding_completed"),
        label: "Onboarding completed",
        kind: "outcome",
        sourceEvent: "member_onboarding_completed",
      },
      {
        key: toOutcomeMetricKey("member_visit_booked"),
        label: "Visit booked",
        kind: "outcome",
        sourceEvent: "member_visit_booked",
      },
      {
        key: toOutcomeMetricKey("member_membership_paid"),
        label: "Membership paid",
        kind: "outcome",
        sourceEvent: "member_membership_paid",
      },
      {
        key: toOutcomeMetricKey("member_membership_refunded"),
        label: "Membership refunded",
        kind: "outcome",
        sourceEvent: "member_membership_refunded",
      },
    ],
  },
};

const buildMemberBookingLaunchContract = (
  comparisonVariantKey,
  {
    effectiveStartAt = MEMBER_FIRST_BOOKING_EXPERIMENTS_START_AT,
    decisionGradeStartAt = MEMBER_FIRST_BOOKING_EXPERIMENTS_DECISION_GRADE_START_AT,
    revisionHistory = [],
  } = {},
) => ({
  assignmentMetricKey: "assignment",
  valueMetrics: {
    enabled: true,
    activatedAt: effectiveStartAt,
    effectiveStartAt,
    decisionGradeStartAt,
    comparisonVariantKey,
    comparisonStatus: "valid_concurrent_control",
    financialPolicy: "assignment_cohort_d30",
    horizonDays: 30,
    revisionHistory,
  },
});

const EXPERIMENT_LAUNCH_CONTRACTS = {
  [EXPERIMENT_KEYS.visitMarginV1]: { assignmentMetricKey: "assignment", valueMetrics: {"enabled": true, "activatedAt": "2026-11-04T02:23:20.000Z", "effectiveStartAt": "2026-11-04T02:23:20.000Z", "decisionGradeStartAt": "2026-11-04T02:23:20.000Z", "comparisonVariantKey": "margin_25", "comparisonStatus": "valid_concurrent_control", "financialPolicy": "modular_clock_horizons", "horizonDays": 30, "financialClockKeys": ["trusted_assignment"], "financialHorizonKeys": ["d30", "d90", "d365", "lifetime"], "primaryFinancialHorizonKey": "d30", "primaryFinancialClockKey": "trusted_assignment"} },

  [EXPERIMENT_KEYS.membershipInitialChoiceOptionsV1]: { assignmentMetricKey: "assignment", valueMetrics: { enabled: true, activatedAt: "2026-11-03T19:45:00.000Z", effectiveStartAt: "2026-11-03T19:45:00.000Z", decisionGradeStartAt: "2026-11-03T19:45:00.000Z", comparisonVariantKey: "control", comparisonStatus: "valid_concurrent_control", financialPolicy: "modular_clock_horizons", horizonDays: 30, financialClockKeys: ["trusted_assignment"], financialHorizonKeys: ["d30", "d90", "d365", "lifetime"], primaryFinancialHorizonKey: "d30", primaryFinancialClockKey: "trusted_assignment" } },

  [EXPERIMENT_KEYS.funnelPreOnboardingLandingEducationV1]: { assignmentMetricKey: "assignment", valueMetrics: { enabled: true, activatedAt: "2026-11-02T18:30:00.000Z", effectiveStartAt: "2026-11-02T18:30:00.000Z", decisionGradeStartAt: "2026-11-02T18:30:00.000Z", comparisonVariantKey: "control", comparisonStatus: "valid_concurrent_control", financialPolicy: "modular_clock_horizons", horizonDays: 30, financialHorizonKeys: ["d30", "lifetime"], primaryFinancialHorizonKey: "d30" } },

  [EXPERIMENT_KEYS.landingCredibilityCountersV1]: { assignmentMetricKey: "assignment", valueMetrics: { enabled: true, activatedAt: "2026-11-02T16:15:00.000Z", effectiveStartAt: "2026-11-02T16:15:00.000Z", decisionGradeStartAt: "2026-11-02T16:15:00.000Z", comparisonVariantKey: "control", comparisonStatus: "valid_concurrent_control", financialPolicy: "modular_clock_horizons", horizonDays: 30, financialHorizonKeys: ["d30", "lifetime"], primaryFinancialHorizonKey: "d30" } },
  [EXPERIMENT_KEYS.landingHelpersTitleV1]: { assignmentMetricKey: "assignment", valueMetrics: { enabled: true, activatedAt: "2026-11-01T19:25:00.000Z", effectiveStartAt: "2026-11-01T19:25:00.000Z", decisionGradeStartAt: "2026-11-01T19:25:00.000Z", comparisonVariantKey: "control", comparisonStatus: "valid_concurrent_control", financialPolicy: "modular_clock_horizons", horizonDays: 30, financialHorizonKeys: ["d30", "lifetime"], primaryFinancialHorizonKey: "d30" } },
  [EXPERIMENT_KEYS.landingReviewsTitleV1]: { assignmentMetricKey: "assignment", valueMetrics: { enabled: true, activatedAt: "2026-11-01T19:25:00.000Z", effectiveStartAt: "2026-11-01T19:25:00.000Z", decisionGradeStartAt: "2026-11-01T19:25:00.000Z", comparisonVariantKey: "control", comparisonStatus: "valid_concurrent_control", financialPolicy: "modular_clock_horizons", horizonDays: 30, financialHorizonKeys: ["d30", "lifetime"], primaryFinancialHorizonKey: "d30" } },
  [EXPERIMENT_KEYS.landingHelperSelectivityTitleV1]: { assignmentMetricKey: "assignment", valueMetrics: { enabled: true, activatedAt: "2026-11-01T19:25:00.000Z", effectiveStartAt: "2026-11-01T19:25:00.000Z", decisionGradeStartAt: "2026-11-01T19:25:00.000Z", comparisonVariantKey: "control", comparisonStatus: "valid_concurrent_control", financialPolicy: "modular_clock_horizons", horizonDays: 30, financialHorizonKeys: ["d30", "lifetime"], primaryFinancialHorizonKey: "d30" } },
  [EXPERIMENT_KEYS.landingFounderStoryTitleV1]: { assignmentMetricKey: "assignment", valueMetrics: { enabled: true, activatedAt: "2026-11-01T19:25:00.000Z", effectiveStartAt: "2026-11-01T19:25:00.000Z", decisionGradeStartAt: "2026-11-01T19:25:00.000Z", comparisonVariantKey: "control", comparisonStatus: "valid_concurrent_control", financialPolicy: "modular_clock_horizons", horizonDays: 30, financialHorizonKeys: ["d30", "lifetime"], primaryFinancialHorizonKey: "d30" } },

  [EXPERIMENT_KEYS.funnelLandingPuzzlesV1]: {
  "assignmentMetricKey": "assignment",
  "valueMetrics": {
    "enabled": true,
    "activatedAt": "2026-10-30T07:00:00.000Z",
    "effectiveStartAt": "2026-10-30T07:00:00.000Z",
    "decisionGradeStartAt": "2026-10-30T07:00:00.000Z",
    "comparisonVariantKey": "hide_puzzles",
    "comparisonStatus": "valid_concurrent_control",
    "financialPolicy": "modular_clock_horizons",
    "horizonDays": 30,
    "financialClockKeys": [
      "trusted_assignment"
    ],
    "financialHorizonKeys": [
      "d7",
      "d30",
      "d60",
      "d90",
      "d365",
      "lifetime"
    ],
    "primaryFinancialHorizonKey": "d30",
    "primaryFinancialClockKey": "trusted_assignment"
  }
},
  [EXPERIMENT_KEYS.memberDashboardPuzzlesV1]: {
  "assignmentMetricKey": "assignment",
  "valueMetrics": {
    "enabled": true,
    "activatedAt": "2026-10-30T07:00:00.000Z",
    "effectiveStartAt": "2026-10-30T07:00:00.000Z",
    "decisionGradeStartAt": "2026-10-30T07:00:00.000Z",
    "comparisonVariantKey": "hide_puzzles",
    "comparisonStatus": "valid_concurrent_control",
    "financialPolicy": "modular_clock_horizons",
    "horizonDays": 30,
    "financialClockKeys": [
      "trusted_assignment"
    ],
    "financialHorizonKeys": [
      "d7",
      "d30",
      "d60",
      "d90",
      "d365",
      "lifetime"
    ],
    "primaryFinancialHorizonKey": "d30",
    "primaryFinancialClockKey": "trusted_assignment"
  }
},
  [visitAcceptanceExperiment.key]: { assignmentMetricKey: "assignment", valueMetrics: visitAcceptanceExperiment.valueMetrics },
  [EXPERIMENT_KEYS.funnelLandingPwycChartCenterV1]: {
    assignmentMetricKey: "assignment",
    valueMetrics: {
      enabled: true,
      activatedAt: "2026-10-12T07:00:00.000Z",
      effectiveStartAt: "2026-10-12T07:00:00.000Z",
      decisionGradeStartAt: "2026-10-12T07:00:00.000Z",
      comparisonVariantKey: "current_center",
      comparisonStatus: "valid_concurrent_control",
      financialPolicy: "assignment_cohort_d30",
      horizonDays: 30,
    },
  },
  [EXPERIMENT_KEYS.funnelLandingHeroReviewQuoteV1]: {
    assignmentMetricKey: "assignment",
    valueMetrics: {
      enabled: true,
      activatedAt: "2026-10-08T20:00:00.000Z",
      effectiveStartAt: "2026-10-08T20:00:00.000Z",
      decisionGradeStartAt: "2026-10-08T20:00:00.000Z",
      comparisonVariantKey: "hide_quote",
      comparisonStatus: "valid_concurrent_control",
      financialPolicy: "assignment_cohort_d30",
      horizonDays: 30,
    },
  },
  [EXPERIMENT_KEYS.funnelLandingHeroSelectivityV1]: {
    assignmentMetricKey: "assignment",
    valueMetrics: {
      enabled: true,
      activatedAt: "2026-10-31T07:00:00.000Z",
      effectiveStartAt: "2026-10-31T07:00:00.000Z",
      decisionGradeStartAt: "2026-10-31T07:00:00.000Z",
      comparisonVariantKey: "control",
      comparisonStatus: "valid_concurrent_control",
      financialPolicy: "modular_clock_horizons",
      horizonDays: 30,
      financialHorizonKeys: ["d30", "lifetime"],
      primaryFinancialHorizonKey: "d30",
    },
  },
  [EXPERIMENT_KEYS.funnelLandingTopSectionOrderV1]: {
    assignmentMetricKey: "assignment",
    valueMetrics: {
      enabled: true,
      activatedAt: "2026-10-31T07:00:00.000Z",
      effectiveStartAt: "2026-10-31T07:00:00.000Z",
      decisionGradeStartAt: "2026-10-31T07:00:00.000Z",
      comparisonVariantKey: "control",
      comparisonStatus: "valid_concurrent_control",
      financialPolicy: "modular_clock_horizons",
      horizonDays: 30,
      financialHorizonKeys: ["d30", "lifetime"],
      primaryFinancialHorizonKey: "d30",
    },
  },
  [EXPERIMENT_KEYS.funnelLandingReviewLayoutV1]: {
    assignmentMetricKey: "assignment",
    valueMetrics: {
      enabled: true,
      activatedAt: "2026-11-01T14:00:00.000Z",
      effectiveStartAt: "2026-11-01T14:00:00.000Z",
      decisionGradeStartAt: "2026-11-01T14:00:00.000Z",
      comparisonVariantKey: "control",
      comparisonStatus: "valid_concurrent_control",
      financialPolicy: "modular_clock_horizons",
      horizonDays: 30,
      financialHorizonKeys: ["d30", "lifetime"],
      primaryFinancialHorizonKey: "d30",
    },
  },
  [EXPERIMENT_KEYS.funnelLandingHeroTaglineV1]: {
    assignmentMetricKey: "assignment",
    valueMetrics: {
      enabled: true,
      activatedAt: "2026-10-31T07:00:00.000Z",
      effectiveStartAt: "2026-10-31T07:00:00.000Z",
      decisionGradeStartAt: "2026-10-31T07:00:00.000Z",
      comparisonVariantKey: "control",
      comparisonStatus: "valid_concurrent_control",
      financialPolicy: "modular_clock_horizons",
      horizonDays: 30,
      financialHorizonKeys: ["d30", "lifetime"],
      primaryFinancialHorizonKey: "d30",
    },
  },
  [EXPERIMENT_KEYS.funnelLandingHeroVideoV1]: {
    assignmentMetricKey: "assignment",
    valueMetrics: {
      enabled: true,
      activatedAt: "2026-10-28T07:00:00.000Z",
      effectiveStartAt: "2026-10-28T07:00:00.000Z",
      decisionGradeStartAt: "2026-10-28T07:00:00.000Z",
      comparisonVariantKey: "founder_video",
      comparisonStatus: "valid_concurrent_control",
      financialPolicy: "assignment_cohort_d30",
      horizonDays: 30,
    },
  },
  [EXPERIMENT_KEYS.funnelLandingHeroHeadlineV1]: {
    assignmentMetricKey: "assignment",
    valueMetrics: {
      enabled: true,
      activatedAt: EXPERIMENT_PORTFOLIO_REALLOCATION_AT,
      effectiveStartAt: EXPERIMENT_PORTFOLIO_REALLOCATION_AT,
      decisionGradeStartAt: EXPERIMENT_PORTFOLIO_REALLOCATION_AT,
      comparisonVariantKey: "control",
      comparisonStatus: "valid_concurrent_control",
      financialPolicy: "assignment_cohort_d30",
      horizonDays: 30,
      revisionHistory:
        CLEAN_SLATE_REVISION_HISTORY.funnel_landing_hero_headline_v1,
    },
  },
  [EXPERIMENT_KEYS.funnelLandingAudienceLanguageV1]: {
    assignmentMetricKey: "assignment",
    valueMetrics: {
      enabled: true,
      activatedAt: LANDING_AUDIENCE_LANGUAGE_START_AT,
      effectiveStartAt: LANDING_AUDIENCE_LANGUAGE_START_AT,
      decisionGradeStartAt: LANDING_AUDIENCE_LANGUAGE_DECISION_GRADE_START_AT,
      comparisonVariantKey: "control",
      comparisonStatus: "valid_concurrent_control",
      financialPolicy: "assignment_cohort_d30",
      horizonDays: 30,
      revisionHistory: [],
    },
  },
  [EXPERIMENT_KEYS.funnelSitewideBbbBrandingV1]: {
    assignmentMetricKey: "assignment",
    valueMetrics: {
      enabled: true,
      activatedAt: EXPERIMENT_DENOMINATOR_RESTART_AT,
      effectiveStartAt: EXPERIMENT_DENOMINATOR_RESTART_AT,
      decisionGradeStartAt: EXPERIMENT_DENOMINATOR_RESTART_AT,
      comparisonVariantKey: "control",
      comparisonStatus: "valid_concurrent_control",
      financialPolicy: "assignment_cohort_d30",
      horizonDays: 30,
      revisionHistory: [
        historicalExperimentRevision({
          revisionId: "funnel_sitewide_bbb_branding_v1_r1",
          seedSalt: "funnel_sitewide_bbb_branding_v1_r1",
          arms: [
            { key: "control", weight: 1 },
            { key: "without_bbb", weight: 1 },
          ],
          surfaces: [
            "public_site",
            "pre_onboarding_flow",
            "member_onboarding",
            "membership_checkout",
            "schedule_visit_flow",
            "member_dashboard",
          ],
          exposureMetricKey: "bbb_branding_surface_viewed",
          primaryMetricKey: "outcome_member_account_created",
          effectiveStartAt: SITEWIDE_BBB_BRANDING_START_AT,
          decisionGradeStartAt: SITEWIDE_BBB_BRANDING_DECISION_GRADE_START_AT,
          effectiveEndAt: EXPERIMENT_DENOMINATOR_RESTART_AT,
          comparisonVariantKey: "control",
          sourceContractFingerprint:
            "af1205e1a03c042ed00f1b0a3ef6d5f46839e23046fefe00e9dad6462a1c1773",
          contractFingerprint:
            "fb1ab9161897285c7cac72aafdc2e6fe7a93508d1518630344d3aab109099b4f",
        }),
      ],
    },
  },
  [EXPERIMENT_KEYS.funnelLandingHeroCtaCopyV1]: {
    assignmentMetricKey: "assignment",
    valueMetrics: {
      enabled: true,
      activatedAt: LANDING_HERO_CTA_COPY_FOUR_ARM_AT,
      effectiveStartAt: LANDING_HERO_CTA_COPY_FOUR_ARM_AT,
      decisionGradeStartAt: LANDING_HERO_CTA_COPY_FOUR_ARM_AT,
      comparisonVariantKey: "check_availability",
      comparisonStatus: "valid_concurrent_control",
      financialPolicy: "assignment_cohort_d30",
      horizonDays: 30,
      revisionHistory:
        CLEAN_SLATE_REVISION_HISTORY.funnel_landing_hero_cta_copy_v1,
    },
  },
  [EXPERIMENT_KEYS.funnelLandingHeroTrustSignalV1]: {
    assignmentMetricKey: "assignment",
    valueMetrics: {
      enabled: false,
      disabledReason: "retired_promoted_control_2026_09_01",
      effectiveEndAt: VISIT_BOOKING_REALLOCATION_AT,
      comparisonVariantKey: "control",
      comparisonStatus: "retired",
      financialPolicy: "retired",
      horizonDays: null,
      revisionHistory:
        CLEAN_SLATE_REVISION_HISTORY.funnel_landing_hero_trust_signal_v1,
    },
  },
  [EXPERIMENT_KEYS.funnelLandingDesktopHeroMediaV1]: {
    assignmentMetricKey: "assignment",
    valueMetrics: {
      enabled: false,
      disabledReason: "retired_promoted_founder_video_2026_08_01",
      activatedAt: "2026-09-01T07:00:00.000Z",
      effectiveStartAt: "2026-09-01T07:00:00.000Z",
      comparisonVariantKey: "founder_video",
      comparisonStatus: "retired",
      financialPolicy: "retired",
      horizonDays: null,
    },
  },
  [EXPERIMENT_KEYS.funnelLandingMobileHeroMediaLayoutV1]: {
    assignmentMetricKey: "assignment",
    valueMetrics: {
      enabled: false,
      disabledReason: "retired_promoted_founder_media_first_2026_09_16",
      activatedAt: VISIT_BOOKING_REALLOCATION_AT,
      effectiveStartAt: VISIT_BOOKING_REALLOCATION_AT,
      decisionGradeStartAt: VISIT_BOOKING_REALLOCATION_AT,
      effectiveEndAt: EXPERIMENT_PORTFOLIO_REALLOCATION_AT,
      comparisonVariantKey: "founder_media_first",
      comparisonStatus: "retired",
      financialPolicy: "retired",
      horizonDays: null,
      revisionHistory:
        CLEAN_SLATE_REVISION_HISTORY.funnel_landing_mobile_hero_media_layout_v1,
    },
  },
  [EXPERIMENT_KEYS.funnelLandingSectionOrderV1]: {
    assignmentMetricKey: "assignment",
    valueMetrics: {
      enabled: false,
      disabledReason: "retired_single_arm_pseudo_test_2026_08_21",
      activatedAt: "2026-08-14T07:00:00.000Z",
      effectiveStartAt: "2026-08-14T07:00:00.000Z",
      effectiveEndAt: LANDING_EXPERIMENT_PAUSE_AT,
      comparisonStatus: "retired",
      noValidComparisonReason: "single_registered_top_level_arm",
      financialPolicy: "retired",
      horizonDays: null,
    },
  },
  ...Object.fromEntries(
    LANDING_SECTION_SLOT_EXPERIMENTS.map(
      ({
        experimentKey,
        effectiveStartAt = VISIT_BOOKING_REALLOCATION_AT,
        decisionGradeStartAt = effectiveStartAt,
        effectiveEndAt = null,
        status = "active",
        sourceContractFingerprint = null,
      }) => [
        experimentKey,
        {
          assignmentMetricKey: "assignment",
          valueMetrics: {
            enabled: status === "active",
            ...(status === "retired"
              ? {
                  disabledReason: `retired_promoted_product_default_2026_09_16`,
                }
              : {}),
            activatedAt: effectiveStartAt,
            effectiveStartAt,
            decisionGradeStartAt,
            ...(effectiveEndAt ? { effectiveEndAt } : {}),
            ...(sourceContractFingerprint
              ? {
                  sourceContractFingerprint,
                  acceptedLegacyAssignmentFingerprints: [sourceContractFingerprint],
                }
              : {}),
            comparisonStatus:
              status === "active" ? "no_valid_concurrent_control" : "retired",
            noValidComparisonReason: "multi_arm_slot_has_no_control_variant",
            financialPolicy:
              status === "active" ? "assignment_cohort_d30" : "retired",
            horizonDays: status === "active" ? 30 : null,
            revisionHistory: CLEAN_SLATE_REVISION_HISTORY[experimentKey] || [],
          },
        },
      ],
    ),
  ),
  [EXPERIMENT_KEYS.funnelLandingHelperSelectivityPresenceV1]: {
    assignmentMetricKey: "assignment",
    valueMetrics: {
      enabled: false,
      disabledReason: "retired_promoted_show_helper_selectivity_2026_08_12",
      activatedAt: "2026-09-01T07:00:00.000Z",
      effectiveStartAt: "2026-09-01T07:00:00.000Z",
      comparisonVariantKey: "show_helper_selectivity",
      comparisonStatus: "retired",
      financialPolicy: "retired",
      horizonDays: null,
    },
  },
  [EXPERIMENT_KEYS.funnelHelperDiscountPresentationV1]: {
    assignmentMetricKey: "assignment",
    valueMetrics: {
      enabled: false,
      disabledReason: "retired_replaced_by_sticky_bar_credibility_2026_09_25",
      activatedAt: VISIT_BOOKING_REALLOCATION_AT,
      effectiveStartAt: VISIT_BOOKING_REALLOCATION_AT,
      decisionGradeStartAt: VISIT_BOOKING_REALLOCATION_AT,
      effectiveEndAt: "2026-09-25T07:00:00.000Z",
      comparisonVariantKey: "control",
      comparisonStatus: "retired",
      financialPolicy: "retired",
      horizonDays: null,
      revisionHistory:
        CLEAN_SLATE_REVISION_HISTORY.funnel_helper_discount_presentation_v1,
    },
  },
  [EXPERIMENT_KEYS.funnelLandingCredibilityMiddleBandsV1]: {
    assignmentMetricKey: "assignment",
    valueMetrics: {
      enabled: true,
      activatedAt: "2026-10-03T07:00:00.000Z",
      effectiveStartAt: "2026-10-03T07:00:00.000Z",
      decisionGradeStartAt: "2026-10-03T07:00:00.000Z",
      comparisonVariantKey: "no_bar",
      comparisonStatus: "valid_concurrent_control",
      financialPolicy: "assignment_cohort_d30",
      horizonDays: 30,
    },
  },
  [EXPERIMENT_KEYS.funnelBookingCheckoutLocalCredibilityV1]: {
    assignmentMetricKey: "assignment",
    valueMetrics: {
      enabled: true,
      activatedAt: "2026-10-14T21:15:00.000Z",
      effectiveStartAt: "2026-10-14T21:15:00.000Z",
      decisionGradeStartAt: "2026-10-14T21:15:00.000Z",
      comparisonVariantKey: "no_alert",
      comparisonStatus: "valid_concurrent_control",
      financialPolicy: "assignment_cohort_d30",
      horizonDays: 30,
      revisionHistory: [
        historicalExperimentRevision({
          revisionId: "funnel_booking_checkout_local_credibility_v1_r1",
          seedSalt: "funnel_booking_checkout_local_credibility_v1_r1",
          arms: [
            { key: "no_alert", weight: 1 },
            { key: "show_alert", weight: 1 },
          ],
          surfaces: ["schedule_visit_flow"],
          exposureMetricKey: "checkout_local_credibility_eligible_viewed",
          primaryMetricKey: "outcome_member_visit_booked",
          effectiveStartAt: "2026-09-28T07:00:00.000Z",
          effectiveEndAt: "2026-10-14T21:15:00.000Z",
          comparisonVariantKey: "no_alert",
          sourceContractFingerprint: "326bbb3cab0e5203bdcca050ad71e26ef5771faabfdb9209fa8d7598de2c7915",
          contractFingerprint: "51c80841e6d190776a15e06dc9f44b917c827a1f270394f4689e577601c96eef",
        }),
      ],
    },
  },
  [EXPERIMENT_KEYS.funnelSameDayFirstVisitFreeHourV1]: {
    assignmentMetricKey: "assignment",
    valueMetrics: {
      enabled: false,
      disabledReason: "retired_dormant_control_only_experiment",
      activatedAt: "2026-09-01T07:00:00.000Z",
      effectiveStartAt: "2026-09-01T07:00:00.000Z",
      comparisonStatus: "retired",
      financialPolicy: "retired",
      horizonDays: null,
    },
  },
  [EXPERIMENT_KEYS.funnelPreOnboardingContactConsolidationV1]: {
    assignmentMetricKey: "assignment",
    valueMetrics: {
      enabled: false,
      disabledReason: "retired_superseded_by_modular_pre_onboarding_v2",
      activatedAt: "2026-09-01T07:00:00.000Z",
      effectiveStartAt: "2026-09-01T07:00:00.000Z",
      comparisonVariantKey: "control",
      comparisonStatus: "retired",
      financialPolicy: "retired",
      horizonDays: null,
    },
  },
  [EXPERIMENT_KEYS.funnelPreOnboardingLocationFirstShortV1]: {
    assignmentMetricKey: "assignment",
    valueMetrics: {
      enabled: false,
      disabledReason: "retired_superseded_by_modular_pre_onboarding_v2",
      activatedAt: "2026-09-01T07:00:00.000Z",
      effectiveStartAt: "2026-09-01T07:00:00.000Z",
      comparisonVariantKey: "control",
      comparisonStatus: "retired",
      financialPolicy: "retired",
      horizonDays: null,
    },
  },
  [EXPERIMENT_KEYS.funnelPreOnboardingFlowVersionV1]: {
    assignmentMetricKey: "assignment",
    valueMetrics: {
      enabled: true,
      activatedAt: PRE_ONBOARDING_FLOW_V3_SPLIT_START_AT,
      effectiveStartAt: PRE_ONBOARDING_FLOW_V3_SPLIT_START_AT,
      decisionGradeStartAt: PRE_ONBOARDING_FLOW_V3_SPLIT_START_AT,
      comparisonVariantKey: "pre_onboarding_v1",
      comparisonStatus: "valid_concurrent_control",
      financialPolicy: "assignment_cohort_d30",
      horizonDays: 30,
      revisionHistory: [
        historicalExperimentRevision({
          revisionId: "funnel_pre_onboarding_flow_version_v1_r1",
          seedSalt: "funnel_pre_onboarding_flow_version_v1_r1",
          arms: [
            { key: "pre_onboarding_v1", weight: 1 },
            { key: "pre_onboarding_v2", weight: 1 },
          ],
          surfaces: [
            "pre_onboarding_entry",
            "pre_onboarding_flow",
            "pre_onboarding_v2",
          ],
          exposureMetricKey: "pre_onboarding_flow_version_entered",
          primaryMetricKey: toOutcomeMetricKey("member_visit_booked"),
          effectiveStartAt: PRE_ONBOARDING_V2_EXPERIMENTS_START_AT,
          decisionGradeStartAt:
            PRE_ONBOARDING_V2_EXPERIMENTS_DECISION_GRADE_START_AT,
          effectiveEndAt: PRE_ONBOARDING_FLOW_V3_SPLIT_START_AT,
          comparisonVariantKey: "pre_onboarding_v1",
          sourceContractFingerprint:
            "a6750b75fb0fce7465f0e2a57c6d52195e0d802047129fb196bbd05df7e47518",
          contractFingerprint:
            "b337716435553fa2d179a1bf1ff8d6dd3e57b84bfb432f1ab39e2f37c9df648f",
          acceptedLegacyAssignmentFingerprints: [
            "a6750b75fb0fce7465f0e2a57c6d52195e0d802047129fb196bbd05df7e47518",
            "a1e6be426b4eea1a35b6a0741561ee310b22f2b71d89c6de2d86223e3fc2a771",
          ],
        }),
      ],
    },
  },
  [EXPERIMENT_KEYS.funnelPreOnboardingV2ReservationEntryV1]: {
    assignmentMetricKey: "assignment",
    valueMetrics: {
      enabled: true,
      activatedAt: PRE_ONBOARDING_V2_RESERVATION_ENTRY_START_AT,
      effectiveStartAt: PRE_ONBOARDING_V2_RESERVATION_ENTRY_START_AT,
      decisionGradeStartAt:
        PRE_ONBOARDING_V2_RESERVATION_ENTRY_DECISION_GRADE_START_AT,
      comparisonVariantKey: "existing_booking_experiments",
      comparisonStatus: "valid_concurrent_control",
      financialPolicy: "assignment_cohort_d30",
      horizonDays: 30,
      revisionHistory: [],
    },
  },
  [EXPERIMENT_KEYS.funnelPreOnboardingV2HelperLobbyV1]: {
    assignmentMetricKey: "assignment",
    valueMetrics: {
      enabled: true,
      activatedAt: PRE_ONBOARDING_V2_EXPERIMENTS_START_AT,
      effectiveStartAt: PRE_ONBOARDING_V2_EXPERIMENTS_START_AT,
      decisionGradeStartAt:
        PRE_ONBOARDING_V2_EXPERIMENTS_DECISION_GRADE_START_AT,
      comparisonVariantKey: "show_helper_lobby",
      comparisonStatus: "valid_concurrent_control",
      financialPolicy: "assignment_cohort_d30",
      horizonDays: 30,
      revisionHistory: [],
    },
  },
  [EXPERIMENT_KEYS.funnelPreOnboardingStartCardV2]: {
    assignmentMetricKey: "assignment",
    valueMetrics: {
      enabled: false,
      disabledReason: "retired_universal_location_role_entry_2026_09_13",
      effectiveEndAt: "2026-09-13T07:00:00.000Z",
      comparisonVariantKey: "help_type_first",
      comparisonStatus: "retired",
      financialPolicy: "retired",
      horizonDays: null,
      revisionHistory:
        CLEAN_SLATE_REVISION_HISTORY.funnel_pre_onboarding_start_card_v2,
    },
  },
  [EXPERIMENT_KEYS.funnelLocalHelperMomentumCardV1]: {
    assignmentMetricKey: "assignment",
    valueMetrics: {
      enabled: false,
      disabledReason: "retired_promoted_show_momentum_card_2026_08_01",
      activatedAt: "2026-09-01T07:00:00.000Z",
      effectiveStartAt: "2026-09-01T07:00:00.000Z",
      comparisonVariantKey: "show_momentum_card",
      comparisonStatus: "retired",
      financialPolicy: "retired",
      horizonDays: null,
    },
  },
  [EXPERIMENT_KEYS.memberDashboardFounderWelcomeCardV1]: {
    assignmentMetricKey: "assignment",
    valueMetrics: {
      enabled: false,
      disabledReason: "retired_paused_show_founder_welcome_2026_08_12",
      activatedAt: "2026-09-01T07:00:00.000Z",
      effectiveStartAt: "2026-09-01T07:00:00.000Z",
      comparisonVariantKey: "control",
      comparisonStatus: "retired",
      financialPolicy: "retired",
      horizonDays: null,
    },
  },
  [EXPERIMENT_KEYS.memberStampCardV1]: {
    assignmentMetricKey: "assignment",
    valueMetrics: buildStampCardExperiment().valueMetrics,
  },
  [EXPERIMENT_KEYS.memberDashboardFounderNoteV2]:
    buildMemberBookingLaunchContract("click_to_open", {
      effectiveStartAt: "2026-10-12T07:00:00.000Z",
      decisionGradeStartAt: "2026-10-12T07:00:00.000Z",
    }),
  [EXPERIMENT_KEYS.memberPostSignupDashboardV3]: {
    assignmentMetricKey: "assignment",
    valueMetrics: {
      enabled: false,
      disabledReason: "retired_promoted_dashboard_checklist_v2_2026_09_01",
      effectiveEndAt: VISIT_BOOKING_REALLOCATION_AT,
      comparisonVariantKey: "dashboard_checklist_v2",
      comparisonStatus: "retired",
      financialPolicy: "retired",
      horizonDays: null,
      revisionHistory:
        CLEAN_SLATE_REVISION_HISTORY.member_post_signup_dashboard_v3,
    },
  },
  [EXPERIMENT_KEYS.memberDashboardChecklistOrderV1]: {
    assignmentMetricKey: "assignment",
    valueMetrics: {
      enabled: false,
      disabledReason: "retired_promoted_book_visit_first_2026_09_01",
      effectiveEndAt: VISIT_BOOKING_REALLOCATION_AT,
      comparisonVariantKey: "book_visit_first",
      comparisonStatus: "retired",
      financialPolicy: "retired",
      horizonDays: null,
      revisionHistory:
        CLEAN_SLATE_REVISION_HISTORY.member_dashboard_checklist_order_v1,
    },
  },
  [EXPERIMENT_KEYS.memberPostSignupMembershipDiscountV1]: {
    revisionId: "member_post_signup_membership_discount_v1_r3",
    assignmentMetricKey: "assignment",
    valueMetrics: {
      enabled: false,
      disabledReason: "retired_promoted_save_10_percent_2026_09_01",
      effectiveEndAt: VISIT_BOOKING_REALLOCATION_AT,
      comparisonVariantKey: "save_10_percent",
      comparisonStatus: "retired",
      financialPolicy: "retired",
      horizonDays: null,
      revisionHistory:
        CLEAN_SLATE_REVISION_HISTORY.member_post_signup_membership_discount_v1,
    },
  },
  [EXPERIMENT_KEYS.memberScheduleVisitPickerV2]: {
    assignmentMetricKey: "assignment",
    valueMetrics: {
      enabled: false,
      disabledReason: "retired_promoted_available_dates_v2_2026_08_04",
      activatedAt: "2026-08-03T07:00:00.000Z",
      effectiveStartAt: "2026-08-03T07:00:00.000Z",
      comparisonStatus: "retired",
      financialPolicy: "retired",
      horizonDays: null,
    },
  },
  [EXPERIMENT_KEYS.memberFirstVisitReservationGateV2]:
    buildMemberBookingLaunchContract("standard_dashboard", {
      effectiveStartAt: EXPERIMENT_DENOMINATOR_RESTART_AT,
      decisionGradeStartAt: EXPERIMENT_DENOMINATOR_RESTART_AT,
      revisionHistory: [
        historicalExperimentRevision({
          revisionId: "member_first_visit_reservation_gate_v2_r1",
          assignmentUnit: "member",
          seedSalt: "member_first_visit_reservation_gate_v2_r1",
          arms: [
            { key: "standard_dashboard", weight: 1 },
            { key: "reservation_gate", weight: 1 },
          ],
          surfaces: ["member_dashboard_activation", "schedule_visit_flow"],
          exposureMetricKey: "reservation_gate_eligible",
          primaryMetricKey: "outcome_member_visit_booked",
          effectiveStartAt: "2026-09-22T07:00:00.000Z",
          decisionGradeStartAt: "2026-09-23T07:00:00.000Z",
          effectiveEndAt: EXPERIMENT_DENOMINATOR_RESTART_AT,
          comparisonVariantKey: "standard_dashboard",
          sourceContractFingerprint:
            "5fa96005f520fd3d797ea3a86d8323d9749c1028406ce1516e2a35a3562dd459",
          contractFingerprint:
            "2c09d5df9d079a2d29a65725fa75a534bec1a02b84e33b857cfc116cbe847201",
        }),
      ],
    }),
  [EXPERIMENT_KEYS.memberHelperGalleryVisibilityV1]:
    buildMemberBookingLaunchContract("hide_gallery", {
      effectiveStartAt: "2026-10-14T07:00:00.000Z",
      decisionGradeStartAt: "2026-10-14T07:00:00.000Z",
    }),
  [EXPERIMENT_KEYS.memberFirstBookingHelperLobbyV1]:
    buildMemberBookingLaunchContract("show_helper_lobby", {
      effectiveStartAt: MEMBER_FIRST_BOOKING_ENTRY_EXPERIMENTS_START_AT,
      decisionGradeStartAt:
        MEMBER_FIRST_BOOKING_ENTRY_EXPERIMENTS_DECISION_GRADE_START_AT,
    }),
  [EXPERIMENT_KEYS.memberFirstBookingEntryModeV1]:
    buildMemberBookingLaunchContract("standard_scheduler", {
      effectiveStartAt: MEMBER_FIRST_BOOKING_ENTRY_EXPERIMENTS_START_AT,
      decisionGradeStartAt:
        MEMBER_FIRST_BOOKING_ENTRY_EXPERIMENTS_DECISION_GRADE_START_AT,
    }),
  [EXPERIMENT_KEYS.memberFirstVisitHelperCountDefaultV1]:
    buildMemberBookingLaunchContract("one_or_two_helpers"),
  [EXPERIMENT_KEYS.memberAddressCaptureModeV1]: {
    revisionId: "member_address_capture_mode_v1_r1",
    valueMetrics: {
      enabled: false,
      disabledReason: "retired_observational_harness_removed_2026_08_12",
      activatedAt: "2026-09-01T07:00:00.000Z",
      effectiveStartAt: "2026-09-01T07:00:00.000Z",
      comparisonStatus: "retired",
      financialPolicy: "retired",
      horizonDays: null,
    },
  },
};

export const EXPERIMENTS = Object.freeze(
  Object.fromEntries(
    Object.entries(EXPERIMENT_DEFINITIONS).map(
      ([experimentKey, experiment]) => {
        const launchContract = EXPERIMENT_LAUNCH_CONTRACTS[experimentKey];
        if (!launchContract) {
          throw new Error(
            `Missing canonical experiment launch contract for ${experimentKey}`,
          );
        }
        return [
          experimentKey,
          Object.freeze({
            ...experiment,
            ...launchContract,
          }),
        ];
      },
    ),
  ),
);

export const listExperiments = ({ includeInactive = true } = {}) =>
  Object.values(EXPERIMENTS).filter((experiment) =>
    includeInactive ? true : experiment?.status === "active",
  );

export const getExperimentByKey = (experimentKey = "") =>
  EXPERIMENTS[String(experimentKey || "").trim()] || null;

export const getExperimentAssignmentRevisionAt = (experiment, at = new Date()) => {
  if (!experiment?.continuePreviousRevisionUntilStart || !experiment?.effectiveStartAt) {
    return experiment;
  }
  const atMs = new Date(at).getTime();
  if (!Number.isFinite(atMs) || atMs >= new Date(experiment.effectiveStartAt).getTime()) {
    return experiment;
  }

  const historicalRevision = experiment.valueMetrics?.revisionHistory?.find(
    (revision) =>
      atMs >= new Date(revision.effectiveStartAt).getTime() &&
      (!revision.effectiveEndAt || atMs < new Date(revision.effectiveEndAt).getTime()),
  );
  if (!historicalRevision) return experiment;

  return {
    ...experiment,
    revisionId: historicalRevision.revisionId,
    effectiveStartAt: historicalRevision.effectiveStartAt,
    effectiveEndAt: historicalRevision.effectiveEndAt,
    assignment: {
      ...experiment.assignment,
      revisionId: historicalRevision.revisionId,
      seedSalt: historicalRevision.assignmentSeedSalt,
      variants: historicalRevision.arms.map(({ key, weight }) => ({ key, weight })),
    },
    variants: [
      ...experiment.variants,
      ...(experiment.surfaceSlot?.startsWith("landing_section_slot")
        ? historicalRevision.arms
            .filter(({ key }) => !experiment.variants.some((variant) => variant.key === key))
            .map(({ key }) => ({
              key,
              label: LANDING_SECTION_SLOT_LABELS[key] || key,
              payload: {
                landingSectionSlot: {
                  enabled: true,
                  slotKey: experiment.surfaceSlot.replace("landing_section_", ""),
                  sectionKey: key,
                },
              },
            }))
        : []),
      ...(experiment.key === EXPERIMENT_KEYS.funnelLandingHeroCtaCopyV1 &&
      historicalRevision.revisionId === "funnel_landing_hero_cta_copy_v1_r5"
        ? LANDING_HERO_CTA_RETIRED_VARIANTS
        : []
      ).map(({ key, label, iconKey }) => ({
          key,
          label,
          payload: {
            landingHeroCtaCopy: { enabled: true, labelKey: key, label, iconKey },
          },
        })),
    ],
  };
};

export const getExperimentMetricDefinitions = (experimentKey = "") => {
  const experiment = getExperimentByKey(experimentKey);
  if (!experiment) return [];

  return [
    ...(Array.isArray(experiment.metrics) ? experiment.metrics : []),
    ...(Array.isArray(experiment.outcomeMetrics)
      ? experiment.outcomeMetrics
      : []),
  ];
};

export const getExperimentMetricByKey = (experimentKey = "", metricKey = "") =>
  getExperimentMetricDefinitions(experimentKey).find(
    (metric) => metric?.key === metricKey,
  ) || null;

export const getExperimentVariantByKey = (
  experimentKey = "",
  variantKey = "",
  assignmentRevision = null,
) => {
  const experiment = assignmentRevision || getExperimentByKey(experimentKey);
  if (!experiment) return null;

  return (
    (Array.isArray(experiment.variants) ? experiment.variants : []).find(
      (variant) => variant?.key === variantKey,
    ) || null
  );
};

export const ADDRESS_CAPTURE_MODE_VARIANTS = {
  city: "city_only",
  home: "street_address",
};
